import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import Overlay from '../../components/Overlay';
import ActionButton from '../../components/ActionButton';
import InlineMessage from '../../components/InlineMessage';
import useMessage from '../../lib/useMessage';
import { MOBILE_BREAKPOINT, WORKBENCH_BREAKPOINT } from '../../lib/layout';
import { canApproveTransfer, canCloseTransferShort, canRouteRefusedLines, canSayNothingMoreComing } from '../../lib/canApprove';
import { canStateHowFilled, canSignOffProduction, isStaffWithPurchasing, canAnswerTruckExtra } from '../../lib/permissions';
import { namedItems, itemLabel } from '../../lib/itemLabel';
// #602 / gate 553 D28-D33 -- which actions belong to which tab.
import { allowsAction, deriveStage, signpostFor, signpostSentence, stageChipFor, stageIsTrue, emptyStageSentence, isKnownStage, TAB_LABELS, openingTab } from '../../lib/transferStageActions';
import { canSeeLoadValue } from '../../lib/transferLoads';
import { qtyBox, QTY_BOX_CLASS } from '../../lib/qtyBox';
import {
  transferLabel, inTransitQty, shortfall,
  productionQueue, makeProgress, isApprovable, isShippable, isReceivable,
  costToTravel, leadTimeDays,
  approveTransfer, denyTransfer, closeTransferShort,
  answerTransferLines, answeredLinesSentence, routeDeniedTransfer, routedSentence,
  routePack, suggestedRouteQty, packPhrase, branchesWithSpare, isOwnShelf, promisedFromShelf,
  transferWithdrawOffer, withdrawTransfer, plainTransferRefusal,
  // #665 D7 -- every item on a To make card, one press, all or nothing.
  signOffProductionLines,
  // #694 -- and that press taken back, from its answer on the card.
  undoSignOff,
  // #608 / gate 675 D15-D18 -- the work in hand, Finished's pages, one transfer.
  transfersInHand, fetchFinishedPage, fetchTransferById,
  // #752 -- refusals still waiting for the asking branch's decision.
  fetchWaitingRefusals, waitingOrder, refusedAtOf,
} from '../../lib/branchTransfers';
import { cutOff } from '../../lib/readLimit';
// #675 / gate 675 D1-D4 and D6, Session 79.22b.5.4 -- finding a transfer by
// typing. The matching itself is the database's (D5); see transferSearch.js.
import { IconSearch, IconX } from '@tabler/icons-react';
import {
  searchableTerm, searchTransfers, keepMatching, keepMatchingTrucks, matchLine, latestOnly,
  foundElsewhereSentence, notFoundSentence, lookAtBranchFor, SEARCH_PAUSE_MS,
  SEARCH_PLACEHOLDER_PHONE, SEARCH_PLACEHOLDER_DESK, SEARCH_ARIA_LABEL, CLEAR_SEARCH, SEARCHING,
  SEARCH_SCOPE, SEARCH_FAILED, TRY_AGAIN, AT_OTHER_BRANCHES,
} from '../../lib/transferSearch';
// #675 / gate 675 D7-D12 and D14, Session 79.22b.5.4b -- each tab's orders,
// remembered on the account, and the Filter & sort sheet. The rules are pure,
// in transferSortFilter.js; #752's group reads fetchWaitingRefusals().
import {
  TAB_SORTS, SORT_LABELS, SORT_PREFERENCE_KEY, sortFor, finishedOrder, finishedReadKey, waitingReadKey, sortRows,
  filtersOffered, filtersApplied, anyApplied, keepFiltered, finishedArgs, appliedChips,
  NO_FILTERS, DUE_CHOICES, WHEN_CHOICES, ENDED_CHOICES, ordered,
  FILTER_BUTTON, SHEET_TITLE, CLEAR_ALL, CLEAR_FILTERS, PICK_DATES, ANY_BRANCH,
  liveCount, showButton, filtersEmptySentence,
} from '../../lib/transferSortFilter';
import { getUiPreference, setUiPreference } from '../../lib/userPreferences';
// #675 / gate 675 D13, D19-D21 and #754, Session 79.22b.5.5 -- the desk: the list
// beside the chosen transfer, and which branches a narrow window shows.
import {
  DESK_PLACEHOLDER, DESK_LIST_WIDTH, DESK_GAP, CHOSEN_CARD, isChosenCard, branchViewFor, tabHoldsTransfer, deskSplits,
  sortButtonLabel, deskFilterButtons, DESK_FILTER_NAMES,
  // #675 / gate 675 D22, Session 79.22b.5.5b -- after a press on the right side;
  // and the one "which tab, for a person at this branch" rule a link lands by.
  stageAt, whereItNowIs, nowOnSentence, nextCardAfter, nextButtonLabel, cardsOnTab,
} from '../../lib/transferDesk';
// #665 -- the two job cards' words, the gate's copy table in one place.
import {
  makeCardHeading, makeCardLines, signOffButtonLabel, signOffFooter, SAY_HOW_MANY_MADE,
  NEEDS_THE_SWITCH, overMakeCeiling, signOffDoneLine, signOffAfterNote,
  loadCardHeading, loadCardLines, routeWords, sendButtonLabel, driverFact, loadFactsLine,
  loadCardFooter, CHECKING_TRUCKS, TYPE_HOW_MANY_LOAD, overLoadCeiling, sendDoneLine,
  signOffUndo, sendUndo,
  // #694, 79.22b.4.6.1b -- Collect's answer on its card, and its Undo; and
  // the answer after ANY Undo: what was undone, when, and what came back.
  collectDoneLine, collectAfterNote, collectUndo, undoneLine,
  // #704, 79.22b.4.6.1c -- the To collect card's footer names the button;
  // #740, 79.22b.6e -- and promises a count only where the sheet offers one.
  collectCardDoor,
  // #694, 79.22b.4.6.2 -- Count in's answer on its card, its Undo, and what
  // the Undo says came back.
  countInDoneLine, countInAfterNote, countInUndo, countInUndoneAfter,
  // #697, 79.22b.5.2 -- a hub's own goods, collected at the hub.
  hubCollectHeading, hubCollectNote, hubPickupSubline, hubPickupLabel,
  // #800, 79.22b.6.5.2 -- each item's own promise, and the card's line true on its own.
  promisedLine, itemPromise,
} from '../../lib/transferCardJobs';
import LoadCardAction, { CardNoteOnly, CardButtonRow, REASON_OWN_LINE } from '../../components/inventory/LoadCardAction';
// #689 D19 / #699 D28, Session 79.22b.5.1 -- each tab's list and whose job it
// is, one rule shared with Stock Home's shortcut row.
import {
  toLoadList, openOwnShelfFor, owedList, transferJobs,
  // #697, 79.22b.5.2 -- what is ready at a hub is the receiving branch's pickup.
  hubIdsOf, hubPickupsFor, awaitingCollectionAt, asHubPickup,
} from '../../lib/transferWork';
import WithdrawSheet from './WithdrawSheet';
import { useTier, controlHeight, LayoutTierContext } from '../../lib/tiers';
import { TAB_BAR, tabHeights, tabCountPill } from '../../lib/transferTabs';
import { HeaderLine, TabSheet, PaperTabStrip, PhoneStageRow, CountPill } from '../../components/inventory/TransferTabs';
import SignOffRunSheet from './SignOffRunSheet';
import { Actions, actionWidth } from '../../components/inventory/TransferActions';
import PerItemAnswer from '../../components/inventory/PerItemAnswer';
import ShelfLine from '../../components/inventory/ShelfLine';
import {
  reserveCandidates, productionRows, fetchProductionData, makeableAtBranch,
  unpricedMakeables, shipmentValueWarning,
  // #761 and #758, Session 79.22b.6c.3 -- the shelf run's answer and Undo (T4),
  // and the low-shelf row's "at its reorder point".
  undoRecordProduction, recordedLine, reorderWords,
} from '../../lib/production';
import ShelfRunAnswer from '../../components/inventory/ShelfRunAnswer';
import RecordProductionSheet, { PickWhatYouMadeSheet, SetProductionCostPanel } from './RecordProductionSheet';
// #517, Session 79.10 -- a transfer is filled by LOADS. See transferLoads.js.
import {
  fetchLoads, fetchOpenLoads, transferIdsWithLoadsToWatch, loadTransfer, countInLoad, markArrived,
  // #694, 79.22b.4.6.1 -- a send taken back, from its answer on the To load card.
  undoSend,
  // #694, 79.22b.4.6.1b -- and a collection, from its answer on the To collect card.
  undoCollect, stopKey,
  fetchProductionRuns, peopleInProduction,
  // #712 -- the item table's Made column, and what went from the shelf instead.
  madeByLine, shelfSentByItem,
  // #713, 682 D11 -- To make's "made" counts the runs, read for the whole tab.
  fetchMadeByLine, withMadeCounts,
  // #694, 79.22b.4.6.1b -- the collections behind the history's undone lines.
  fetchHandovers, peopleInHandovers,
  // #694, 79.22b.4.6.2 -- a count taken back, from its answer on the To count
  // in card, and the counts behind the history's undone lines.
  undoCountIn, fetchCountIns, peopleInCountIns,
  closeTransferUnfilled,
  isLoadable, hasReadyToLoad, standingForOnwardLoad, standingAt, arrivingFor, sortByPromised, promisedDateFor, transferProgress,
  shelfFillable, shelfNoteFor,
  arrivingAt, toCollectFor, movingElsewhere, handOverLoad, waitingAtHub, stopOf,
  hubsWaitingFor, isAwaitingCount, awaitingCountUnits, endingLabel, stillOnItsWaySentence,
  fetchBranchDrivers, fetchUserNames, peopleInLoads, peopleInTransfer,
  refusingBranchId, progressText, lineProgressText, approvalShelfLine,
  // #698 half (b), 79.22b.4.7 -- who refused EACH item, and which of them the
  // asking manager may route: read from the item, never inferred from the
  // transfer, once one transfer can carry both branches' refusals.
  lineRefusedByBranchId, routableRefusedLines,
  // #753 / #752, 79.22b.5.4b -- the one rule for a refusal still waiting.
  refusalAwaitsDecision,
  // #698 half (b), 79.22b.4.7.1 -- an item the asking branch turned down
  // itself reads "not needed", and the supplier is asked only about the rest.
  refusedByAsker, linesPutToSupplier,
  // #630, Session 79.22b.1 -- the card's own action needs the same taker
  // default the collect panel uses, and the same over-count refusal the
  // arrival panel uses. Both are imported rather than re-derived, because a
  // second copy of a guard is a guard that goes stale on one side (#358).
  driverPayload, overCountRefusal, discrepancy,
  // #682, 79.22b.6c.2b -- a count above the truck: what it will do, what it did,
  // and what was booked (P4, P5).
  overCountAsk, countAskedSentence, bookedTotal, senderCanPutRight,
  // #699, gate 665 D28 -- who drives: the presser's default at the yard.
  presserDriver,
  // #665 D9/D12 and #690 D11 -- the To load card's rules, shared with the sheet.
  defaultRoute, driverDefault, loaderNamesNobody, nextLoadSeq, loadRef,
  // #726 -- where a leg began, a branch or a spot on the road.
  legEnds,
  // #741 -- where an item's unbooked units are, before any is called lost.
  itemInFlight,
} from '../../lib/transferLoads';
import { LoadPanel, ArrivalPanel, CollectPanel, LoadHistory, NothingMoreComingOnMake } from '../../components/inventory/TransferLoadPanels';
// #682 / #702, Session 79.22b.6c.2 -- the corrections: read beside the loads for
// the history, and the one sheet that makes them.
import {
  fetchCorrections, fetchLoadExtras,
  // #682, 79.22b.6c.2b -- Houston's question about a truck's extra (P6).
  fetchTruckQuestions, truckQuestionEntry, answerLoadExtra, truckAnswerText, extraConfirmedAlert,
} from '../../lib/corrections';
import CorrectionSheet from '../../components/inventory/CorrectionSheet';
import TruckExtraCard from '../../components/inventory/TruckExtraCard';

// Inter-branch transfers. Issues #371 and #425, Session 79.6.
// Gate: docs/gates/Stede_BranchTransfer_Gate_371.html, REVISION 2, approved at
// Session 79.5. Twenty-seven decisions — read it before changing anything here.
//
// ── What this screen is for, in plain words ──
//
// Two things that used to have no honest home in Stede. A big job lands at
// Houston and the vinyl is sitting at Dallas, so somebody needs to move it. And
// every branch needs stakes and frames, which only Houston makes, out of raw
// steel, to order.
//
// Before this, the first was two unrelated adjustments with nothing linking
// them — if one got entered and the other did not, the company's total was
// simply wrong and no document would tell you which half was missing. The second
// went down the PURCHASING path and was stopped at the very end, because D&R
// does not send itself a bill.
//
// They are the same shape — ask, agree, send, receive, no money — so they share
// this one screen, and Stede works out which is which from the category tree.
//
// ── The one thing on this page that must never be "tidied" ──
//
// TRANSFERS AND MATERIAL REQUESTS ARE NEVER HELD IN ONE ARRAY. Gate D14's
// silent failure: a transfer handed to canApprove() returns false for every
// manager and true for admin and executive, with NO ERROR — it reads
// `request.branch_id` and a transfer has `from_branch_id`. The sending
// manager would see the card with no Approve button and nothing in the logs.
//
// They may be DISPLAYED together; they are HELD separately, so no object can
// reach the wrong rule. A merged list with a type tag works right up until
// somebody adds a third card type.

const MODE_LABEL = { stock: 'From the shelf', make: 'Made to order' };

// Seven statuses, and every one is placed in a group deliberately rather than
// defaulted. MyRequestsPage.js records why: a status that falls through no group
// renders nowhere and the document silently vanishes from the screen of the
// person waiting on it.
const OPEN_STATUSES = ['requested', 'awaiting_confirmation', 'approved', 'in_transit'];
// Exported for #579's test, which walks every ending rather than the two that
// were found -- so a new ending cannot quietly reintroduce the live wording.
export const FINISHED_STATUSES = ['received', 'denied', 'cancelled', 'closed_short', 'closed_unfilled'];

const STATUS_TEXT = {
  // #528, Session 79.10 -- TWO STAGES, TWO SENTENCES, and neither says
  // "waiting for approval" on its own. That phrase was true of one step and is
  // now ambiguous between two, which is exactly the confusion this session was
  // convened to remove.
  requested: 'Waiting on the asking branch',
  awaiting_confirmation: 'Waiting on the supplying branch',
  approved: 'Approved',
  in_transit: 'On its way',
  received: 'Received',
  denied: 'Denied',
  // #590, gate 371 F2 -- the only way a transfer ends `cancelled` is the asking
  // side withdrawing it, and the status word is "Withdrawn".
  cancelled: 'Withdrawn',
  closed_short: 'Closed short',
  // #517 / D56 -- the second ending, in the words Colby approved.
  closed_unfilled: 'Nothing more coming',
};

// #694, 79.22b.4.6.1b -- one To collect card per PILE: the transfer and the
// place, the key toCollectFor() already groups by. Module level, so the lists
// that use it never carry it as a dependency.
function pileKeyOf(load) {
  return `${load?.transfer_id ?? '?'}:${stopKey(stopOf(load))}`;
}

// #517 / D67 -- an approval whose per-item dates did not save, said as the
// half-success it is. One sentence for the page's answer and the desk's
// (#675 D22), so the two cannot drift.
function datesDidNotSave(text, warning) {
  return `${text} The per-item dates did not save, so every line shows the date you set for the whole request. ${warning?.message || ''}`.trim();
}

// HOW MANY A COUNT BOOKED IN, read the way the screen shows it: the number
// typed against an item, or what was loaded where nothing was typed. One rule
// for the sheet's answer and the To count in card's (#694, 79.22b.4.6.2).
// #682, 79.22b.6c.2b -- and never more than the truck carried: a count above it
// books the truck's number and asks the sender about the rest (682 D7), so the
// answer names what went on the shelf. bookedTotal() is count_in_load()'s rule.
function countedTotal(theLoad, counts) {
  return bookedTotal(theLoad, counts);
}

function ageInDays(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// ── #608 / gate 675, Session 79.22b.5.3 -- WHAT EVERY TRANSFER THE PAGE HOLDS
// NEEDS, WHEREVER IT CAME FROM ─────────────────────────────────────────────────
//
// The page now holds transfers from three reads, never one: the work in hand
// (the six working tabs, D15), a page of Finished (D16), and any single
// transfer a link opened (D18). Each needs the same two things added, so they
// are done here once rather than three times (locked rule 14).

// The item ids on a set of transfers' lines.
function itemIdsOf(rows) {
  return [...new Set((rows || []).flatMap(t => (t.branch_transfer_items || []).map(l => l.item_id)))];
}

// #567 — HAS THIS REFUSAL ALREADY BEEN DECIDED? The routing panel must not
// stay on screen after somebody routes it, and the answer is not on the
// transfer: it is whether anything points BACK at it. Two cheap reads rather
// than a nested relation, because PostgREST returns empty silently on a
// filter two hops deep and a missing successor would then look like an
// undecided one.
//
// The DATABASE still refuses a second decision — this only stops the screen
// offering one. A picker that filters is advisory; that is B4's rule.
//
// #608 -- moved out of load() so a refused transfer on Finished, or one a
// link opened, carries the same answer as one in the work in hand.
async function withSuccessors(list) {
  if (!list.length) return list;
  const ids = list.map(t => t.id);
  // #621 -- and WHICH document, so the Decided line can name it (gate 565
  // F2's "See {number}") rather than say only that something was decided.
  const [{ data: tSucc }, { data: mSucc }] = await Promise.all([
    supabase.from('branch_transfers').select('id, transfer_number, from_transfer_id').in('from_transfer_id', ids),
    supabase.from('material_requests').select('id, request_number, from_transfer_id').in('from_transfer_id', ids),
  ]);
  const successors = new Map();
  for (const r of (tSucc || [])) {
    successors.set(String(r.from_transfer_id), { kind: 'transfer', id: r.id, number: r.transfer_number || null });
  }
  for (const r of (mSucc || [])) {
    if (!successors.has(String(r.from_transfer_id))) {
      successors.set(String(r.from_transfer_id), { kind: 'request', id: r.id, number: r.request_number || null });
    }
  }
  return list.map(t => (successors.has(String(t.id))
    ? { ...t, __hasSuccessor: true, __successor: successors.get(String(t.id)) }
    : t));
}

// What the page needs to know about the items on those lines: their names,
// the packs their suppliers deal in, each branch's shelf, and the cost to make.
// One round trip each, side by side.
async function readItemFacts(itemIds) {
  const [{ data: itemRows }, { data: packRows }, { data: stockRows, error: stockErr }, { data: costRows }] = await Promise.all([
    supabase.from('inventory_items').select('id, name, sku, uom').in('id', itemIds),
    // #567 D9/D10 — HOW MANY THE SUPPLIER DEALS IN, which is the default the
    // routing screen offers. Colby placed it: "it would live in the vendor
    // catalog page for houston under associated items and item uom pack
    // quantity details like all other vendor info."
    //
    // Houston is an INTERNAL VENDOR — `inventory_vendors.is_internal` — so a
    // pallet of 250 stakes is already `vendor_items.pack_qty` on Houston's row
    // and this gate added no schema at all. The preferred row wins where a
    // vendor has several, matching how the rest of the app picks one.
    //
    // #578, Session 79.21b -- EVERY SUPPLIER'S PACK, WITH WHO THE SUPPLIER
    // IS. This kept ONE number per item from any row, internal or outside,
    // so the make route suggested an outside vendor's case of 10 as
    // "Houston makes these in pallets of 10" - for a need of 250. Colby's
    // rule (#567 rule 3) is per ROUTE: the maker's own pack on make, the
    // preferred outside vendor's on Purchasing, none on another branch. The
    // route is chosen on the screen, so all of them are kept here.
    supabase.from('vendor_items')
      .select('item_id, pack_qty, pack_unit, is_preferred, inventory_vendors(id, name, is_internal, branch_id)')
      .in('item_id', itemIds)
      .not('pack_qty', 'is', null),
    // Keyed on item AND branch, because avg_unit_cost is stored per branch —
    // which is the entire reason gate D16 overturned revision 1's "costing is
    // out of scope". Moving your own stock does not change what it cost to the
    // COMPANY; it changes what it cost to the BRANCH.
    // `reorder_point` rides along for #578's "Try another branch": what a
    // branch can SPARE is its pile above its own floor (#558), the rule
    // Stock Home and the request sheet already share. Fetched at every
    // width - the refusal panel is opened from a phone, and #551 was this
    // exact lookup hidden from phones.
    supabase.from('inventory_stock')
      .select('item_id, branch_id, qty_on_hand, avg_unit_cost, reorder_point')
      .in('item_id', itemIds),
    // The COST TO MAKE for anything a branch manufactures — gate D17, and the
    // reason it has to be fetched at all is that a made item has no average
    // to carry. Houston never BOUGHT a stake, so `avg_unit_cost` at Houston
    // is null forever and the goods would arrive at Dallas valued at nothing.
    //
    // #515 D32 moved this onto the item itself. The old source was the
    // internal vendor's price, keyed item:branch, and it was wrong twice
    // over: a vendor price is what we CHARGE rather than what it COSTS, and
    // it prices a PACK rather than one of the thing. Dibond Black is 50
    // sheets at $600 and this screen was reading $600 for a $12 sheet.
    //
    // Keyed on the item alone now, which is what D32 chose. Two branches
    // making the same thing at different costs is a real future case and the
    // gate's answer is that they are different items — 60in stakes are not
    // 24in stakes, and Austin's painted aluminium is not Houston's steel.
    supabase.from('inventory_items').select('id, standard_cost').in('id', itemIds),
  ]);
  const packs = {};
  for (const r of packRows || []) {
    const k = String(r.item_id);
    (packs[k] = packs[k] || []).push({
      qty: Number(r.pack_qty),
      unit: r.pack_unit || null,
      preferred: !!r.is_preferred,
      internal: !!r.inventory_vendors?.is_internal,
      branchId: r.inventory_vendors?.branch_id ?? null,
      vendorName: r.inventory_vendors?.name || null,
    });
  }
  return {
    // #714 -- marked as fetched, so a name still missing reads "…" until now.
    items: namedItems(itemRows),
    packs,
    stock: Object.fromEntries((stockRows || []).map(r => [`${r.item_id}:${r.branch_id}`, r])),
    stockErr,
    costs: Object.fromEntries((costRows || []).map(r => [String(r.id), r.standard_cost])),
  };
}

// D16 -- the day a finished transfer finished, on its Finished card.
function finishedDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// `initialTab` exists for the To make queue's own destination — #515 gate D33,
// "a destination like any other". `production_queue` is a real page key with a
// real landing-page entry, and it renders THIS page opened on that tab rather
// than a second screen showing the same rows. A fabrication manager can be
// given it as her landing page today, by name, with nothing else to build.
//
// Not a separate component, deliberately: the To make queue is a FILTERED VIEW
// of transfers plus this branch's own reserve work (gate D24, and screen 1's
// callout A). A second page would be a second reader of the same query, which
// is how the in-transit figure went wrong and why #512 exists.
//
// #689 D19, Session 79.22b.5.1 -- `initialTab` may now name any tab, because a
// Stock Home chip opens Transfers on ITS tab ("4 to make" on To make, "1 to
// load" on To load). `initialBranchView` opens it looking at the person's own
// branch, which is the branch the chip counted: an admin or executive would
// otherwise land on every branch, and the pill would disagree with the chip.
// `onEntryUsed` lets App forget the request, so the next ordinary visit opens
// on the person's job as D18 chooses.
export default function TransfersPage({ openTransferId, setOpenTransferId, initialTab, initialBranchView = null, onEntryUsed = null, setActive = null, setOpenRequestId = null }) {
  const auth = useAuth();
  // #651 -- `isPurchasing` is read here for the branch control below.
  const { profile, isAdmin, isExecutive, isManager, isPurchasing } = auth;
  const { message, type, showError, showSuccess, showInfo, clearMessage } = useMessage();

  // #608 / gate 675 D15 -- THE WORK IN HAND, and nothing that has finished:
  // every open transfer, and a "nothing more coming" one while a truck of it is
  // still out. The six working tabs read only this.
  const [transfers, setTransfers] = useState([]);
  // D17 -- when the work in hand arrived cut at the database's row limit, how
  // many arrived; null when it is whole. And the same for the trucks.
  const [inHandCutAt, setInHandCutAt] = useState(null);
  const [trucksCutAt, setTrucksCutAt] = useState(null);
  // D16 -- one page of Finished, read from the database, most recently
  // finished first. `at` is the page: an offset and a size on a desk; on a
  // phone the offset stays 0 and Show 25 more grows the size.
  // `term`: the search this page was read for (null for none), so a page read
  // for another search is never drawn (#675).
  const [finished, setFinished] = useState({ rows: [], total: 0, term: null, state: 'idle' });
  const [finishedAt, setFinishedAt] = useState({ offset: 0, size: 25 });
  // #675 / gate 675 D7, D8, Session 79.22b.5.4b -- each tab's order, read from
  // the person's account once and saved there on every choice (#607: the
  // account, never the device - D&R shares tablets). Held here because the
  // profile in memory is not re-read after a save.
  const [sorts, setSorts] = useState(() => {
    const saved = getUiPreference(profile, SORT_PREFERENCE_KEY, {});
    return saved && typeof saved === 'object' ? saved : {};
  });
  // D9-D11 -- the filters. NOT remembered between visits (D8): a list that
  // opens already filtered from last week reads as missing work. Within a visit
  // they carry across tabs, as the search does.
  const [filters, setFilters] = useState(NO_FILTERS);
  // D12 -- the Filter & sort sheet.
  const [sheetOpen, setSheetOpen] = useState(false);
  // #752 -- refusals waiting for the asking branch's decision, pinned on Finished.
  const [waiting, setWaiting] = useState({ rows: [], cut: false, state: 'idle', key: null });
  // D18 -- transfers a link opened that are neither in hand nor on the page of
  // Finished in view. Held so the sheet stays in step after a reload.
  const [extraTransfers, setExtraTransfers] = useState([]);
  // Bumped at the end of every load(), so Finished re-reads after an action.
  const [loadTick, setLoadTick] = useState(0);
  const finishedRef = useRef([]);
  const extraRef = useRef([]);
  const [branches, setBranches] = useState([]);
  // #553 D12. The people the load sheet's driver picker can offer, read from
  // the branch doing the loading. Its own effect rather than part of load():
  // it depends only on which branch you are standing at, so refetching it on
  // every transfer refresh would be a query per poll for a list that does not
  // change.
  const [drivers, setDrivers] = useState([]);
  // #665 D11 -- whether that list has arrived, so the To load card says "…"
  // and waits rather than naming nobody as the driver for a moment.
  const [driversLoaded, setDriversLoaded] = useState(false);
  // #690 -- "Who loads your trucks" (company_settings), read with the page so
  // the load sheet and the To load card name the driver by the same rule.
  const [loaderModel, setLoaderModel] = useState('driver_loads');
  // #665 D21 -- the answer to the last press on a card: which tab, which
  // transfer, where the card sat, and what to say. See cardRun().
  // #675 / gate 675 D22, Session 79.22b.5.5b -- and the answer to a press on the
  // desk's right side, marked `desk: true` and drawn there (see run()). `seq`
  // tells one answer from the next, so the right side returns to its top for
  // each.
  const [cardNote, setCardNote] = useState(null);
  const deskSeq = useRef(0);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  // Starts on the first stage, and once the page has loaded it moves ONCE to
  // the person's own job -- #689, gate 665 D18; see openingTab() and the
  // effect below `visible`. SUPERSEDED reasoning, kept: this opened on the
  // first stage "rather than on a personalised list", because "What needs me"
  // was answered by the count pills. D18 keeps every tab and every pill
  // visible and only chooses where the page starts, which is what cost a
  // fabricator a tap on every visit (#689: five taps to his card).
  const [tab, setTab] = useState(isKnownStage(initialTab) ? initialTab : 'confirm');
  // #689 D19 -- the request has been read; App may forget it.
  useEffect(() => { if (initialTab && onEntryUsed) onEntryUsed(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Chosen once. A page opened on a named tab (initialTab) or on a transfer
  // from a notification (openTransferId) has already been told where to go,
  // and that wins (553 D32).
  const tabChosen = useRef(!!initialTab || !!openTransferId);
  const [selected, setSelected] = useState(null);
  // #730, Session 79.22b.6e -- THE TRANSFER "See" OPENED. The sheet swapped to
  // the successor and kept the tab underneath, so SAT-TRF-00009 - approved,
  // nobody had loaded it - was headed "Finished", the refused transfer's stage.
  // While the successor is the one open, the sheet derives its own stage (D32)
  // instead; choosing another tab or another transfer ends that.
  const [seeOpenedId, setSeeOpenedId] = useState(null);
  useEffect(() => { setSeeOpenedId(null); }, [tab]);
  // #517 -- the loads on whichever transfer is open. Fetched per selection
  // rather than with the list: a transfer usually has one or two, and pulling
  // every load for every row makes the list slow to answer a question nobody
  // asked yet.
  const [loads, setLoads] = useState([]);
  const [loadsFor, setLoadsFor] = useState(null);   // #671 -- which transfer `loads` belongs to
  // #517 / D66 -- THE DRIVER'S QUEUE. Everything on the road heading here, and
  // everything standing here waiting to be taken on. Fetched for the BRANCH
  // rather than per transfer, because the driver's question is "what has to
  // move today" and that spans every request at once.
  const [openLoads, setOpenLoads] = useState([]);
  // #682, Session 79.22b.6c.2b -- the questions this branch has been asked about
  // a truck's extra (682 D7, D13), each with what its card needs; and the one a
  // press just answered, kept in its place with what happened (gate 665's pinned
  // card) until the next press or a change of tab.
  const [truckQuestions, setTruckQuestions] = useState([]);
  const [questionNote, setQuestionNote] = useState(null);
  // A refusal answered under a card's buttons (F3), and which answer is saving.
  const [questionRefusal, setQuestionRefusal] = useState(null);
  const [questionPressing, setQuestionPressing] = useState(null);
  // S6's box: the transfer a bell opened, and why (see the open effect below).
  const [bellAlert, setBellAlert] = useState(null);
  // #682, 79.22b.6c.3 -- the question's bell (transfer_extra_counted) lands on
  // Awaiting confirmation with that transfer's question card in view (F3).
  const [questionInView, setQuestionInView] = useState(null);
  const [busy, setBusy] = useState(false);
  // The RECEIVING branch's current on-hand and average cost, per item. Without
  // this the blend below computes against a quantity of zero, which SETS the
  // receiver's average to the transfer cost instead of blending it — and that is
  // acceptance line T14 failing while every screen still looks right.
  const [receiverStock, setReceiverStock] = useState({});
  // #665 D15 -- whether the shelf figures have arrived, so an approval card says
  // "checking…" or "couldn't check" in words rather than showing an empty shelf.
  const [stockState, setStockState] = useState('loading');
  // What each item costs to MAKE — `inventory_items.standard_cost`, new at
  // #515. Keyed on the item alone, because gate D32 put it on the item: there
  // is one figure for a thing rather than one per branch that makes it.
  //
  // IT USED TO BE READ FROM THE INTERNAL VENDOR'S PRICE, keyed item:branch, and
  // that was a stop-gap with two faults. A vendor price is what we CHARGE, and
  // it is the price of a PACK — Dibond Black is 50 sheets at $600, so this
  // screen was reading $600 for one sheet that costs $12. Both were found by
  // reading the rows the migration's backfill returned.
  const [standardCosts, setStandardCosts] = useState({});
  // #567 D10 -- item id -> the supplying vendor's pack quantity. Empty when
  // nobody has entered one, and the screen then says only what was asked for.
  const [packQtyByItem, setPackQtyByItem] = useState({});
  // The To make queue's reserve half — gate screen 1, callout B. Items this
  // branch MAKES that have fallen to their reorder point, with no order behind
  // them. Loaded separately from transfers because nothing links the two.
  const [categories, setCategories] = useState([]);
  const [makeableItems, setMakeableItems] = useState([]);
  const [branchStock, setBranchStock] = useState([]);
  // #608's guard, Session 79.22b.6.2d (R3) -- the shelf read failed or came
  // back cut, so the shelf rows and the picker of everything else made here
  // are held back and To make says so in a line, rather than passing part of
  // the catalog off as all of it.
  const [shelfReadFailed, setShelfReadFailed] = useState(false);
  const [recording, setRecording] = useState(null);   // { item, qtyOnHand, branchName }
  // The SECOND doorway. Colby, looking at the built screen: "houston will need
  // a way to record production that's not tied to any specific transfer
  // request." The first build had one way in — a reserve row — and a reserve
  // row needs the item to be below its reorder point, which no Houston item is.
  const [picking, setPicking] = useState(false);
  // D47 — the item whose missing cost is being set from the unpriced banner.
  const [costFor, setCostFor] = useState(null);
  // #553 D5 -- the transfer whose run is being signed off, or null.
  const [signingOff, setSigningOff] = useState(null);
  // #675 D22 -- a sign-off opened from the desk's right side: where its card
  // sat, so the right side can say where the transfer went once it is saved.
  const [signOffPin, setSignOffPin] = useState(null);
  const [showAllUnpriced, setShowAllUnpriced] = useState(false);
  // Colby, Session 79.6, on seeing the first build: "the sheet is not a centered
  // modal like the app is supposed to be." He is right and this was my error —
  // the detail was hardcoded to the bottom-anchored sheet.
  //
  // The house rule is a SHEET ON A PHONE and a CENTRED MODAL EVERYWHERE ELSE,
  // which is what InventoryActionCatalog, InvoicingPage, VendorCatalogPage and
  // UnitSelect all do. Overlay handles the flip at runtime; #224's longhand
  // borders exist precisely so switching variant on a resize is warning-free.
  //
  // And the listener is not optional: #356 records that a bare window.innerWidth
  // read is correct once, on mount, and silently wrong for the rest of the
  // session — which was #64, and #164 found it again.
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const branchNames = useMemo(
    () => Object.fromEntries(branches.map(b => [String(b.id), b.name])),
    [branches],
  );

  // The item facts on screen, merged rather than replaced when a page of
  // Finished or a linked transfer brings items the work in hand does not hold.
  const applyItemFacts = useCallback((f, { merge = false } = {}) => {
    setItems(prev => (merge ? { ...prev, ...f.items } : f.items));
    setPackQtyByItem(prev => (merge ? { ...prev, ...f.packs } : f.packs));
    setReceiverStock(prev => (merge ? { ...prev, ...f.stock } : f.stock));
    setStandardCosts(prev => (merge ? { ...prev, ...f.costs } : f.costs));
  }, []);

  // D18 -- ONE TRANSFER, HOWEVER OLD, read on its own when a link, a "See" in a
  // history, or a reload needs one the page does not hold. Held in
  // extraTransfers, with its successors and its items' facts, exactly as a
  // transfer in hand would be.
  const openOneTransfer = useCallback(async (id) => {
    const { data, error } = await fetchTransferById(id);
    if (error || !data) return null;
    const [plain] = await withSuccessors([data]);
    const madeRead = await fetchMadeByLine(plain?.branch_transfer_items || []);
    const [row] = withMadeCounts([plain], madeRead.data);
    extraRef.current = [...extraRef.current.filter(t => String(t.id) !== String(row.id)), row];
    setExtraTransfers(extraRef.current);
    const ids = itemIdsOf([row]);
    if (ids.length) applyItemFacts(await readItemFacts(ids), { merge: true });
    return row;
  }, [applyItemFacts]);

  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const load = useCallback(async () => {
    setLoading(true);
    const [inHand, { data: branchRows }, { data: settingsRows }] = await Promise.all([
      // #608 / gate 675 D15 -- THE WORK IN HAND, NEVER EVERY TRANSFER EVER
      // RAISED. This read every transfer the company had, with every line, on
      // every visit; past the database's 1,000-row limit the oldest would have
      // vanished from Finished with no error. Finished now reads its own pages
      // (D16) and a link reads its own transfer (D18).
      transfersInHand('*, branch_transfer_items(*)').order('requested_at', { ascending: false }),
      // #529 -- `is_transfer_hub` rides along with the branches rather than
      // being a second round trip. The handover is the 99.5% path, so the load
      // sheet needs to know the hub on first paint or the default arrives late
      // and the control flickers.
      supabase.from('branches').select('id, name, is_transfer_hub').order('id'),
      // #690 -- one column, one row. On both databases since 79.15f.
      supabase.from('company_settings').select('transfer_loader_model').limit(1),
    ]);
    const { data: rows, error } = inHand;
    if (error) showError(`Could not load transfers: ${error.message}`);
    setLoaderModel(settingsRows?.[0]?.transfer_loader_model || 'driver_loads');
    // D17 -- even the work in hand can pass the limit one day. When it does the
    // page says so, rather than showing part of it as the whole.
    const listCut = !error && cutOff(inHand);
    setInHandCutAt(listCut ? (rows || []).length : null);
    // #713, 682 D11 -- how many of each line were MADE, from its runs, so To
    // make never calls shelf goods made. A failed or cut read leaves the words
    // as they were.
    const inHandRows = await withSuccessors(rows || []);
    const madeRead = await fetchMadeByLine(inHandRows.flatMap(t => t.branch_transfer_items || []));
    const list = withMadeCounts(inHandRows, madeRead.data);

    setTransfers(list);
    setBranches(branchRows || []);

    // D18 -- the transfer open in the sheet may have just left the work in hand
    // (refused, withdrawn, closed). Read it on its own so the sheet shows where
    // it now stands rather than holding a stale copy with stale buttons.
    const sel = selectedRef.current;
    if (sel && !list.some(t => String(t.id) === String(sel.id))
      && !finishedRef.current.some(t => String(t.id) === String(sel.id))) {
      await openOneTransfer(sel.id);
    }

    const askers = [...new Set(list
      .filter(t => isOwnShelf(t) && !FINISHED_STATUSES.includes(t.status))
      .map(t => t.requested_by).filter(Boolean))];
    setAskerNames(askers.length ? await fetchUserNames(askers) : {});

    // D66's queue. It carries its own transfer so a card can name the place and
    // the promised date without a round trip per row, which is what makes a
    // list feel slow on a phone.
    // profile?.branch_id rather than `myBranch`, which is declared further down
    // the component body: the closure works either way, but this is the value
    // the dependency array below actually tracks.
    // SCOPED BY OPEN TRANSFER, NOT BY LOAD STATUS -- #529. Material dropped at
    // a hub sits on a leg that is already ARRIVED, so a queue asking only for
    // loads on the road loses whatever a driver could not fit on his truck.
    // #600: including a transfer that ended "nothing more coming" while a
    // truck was still out -- see transferIdsWithLoadsToWatch().
    const trucks = await fetchOpenLoads(
      profile?.branch_id,
      transferIdsWithLoadsToWatch(list));
    setOpenLoads(trucks.data || []);
    setTrucksCutAt(cutOff(trucks) ? (trucks.data || []).length : null);

    // #682, 79.22b.6c.2b -- THE QUESTIONS ASKED OF THIS BRANCH about a truck's
    // extra, for Awaiting confirmation (682 D13): only the branch that sent the
    // truck may answer, so they are read for the person's own branch. Read on
    // every tier (F4). A failed read draws no cards; the transfers are unaffected.
    const asks = await fetchTruckQuestions(profile?.branch_id);
    setTruckQuestions(asks.data || []);

    // One fetch for every item named on every line the page holds, rather than
    // one per line - the work in hand, the page of Finished in view and any
    // transfer a link opened.
    const itemIds = itemIdsOf([...list, ...finishedRef.current, ...extraRef.current]);
    if (itemIds.length) {
      const facts = await readItemFacts(itemIds);
      applyItemFacts(facts);
      // #721's reader of #608's cut-off -- what each branch has already
      // promised is counted from the work in hand (promisedFromShelf()). If
      // that list was cut, some promises are missing and every "has 45 -> 40
      // after" would overstate, so the approval card says "couldn't check"
      // instead (D17: the figures say so too, not only the list).
      setStockState(facts.stockErr || listCut ? 'failed' : 'ok');
    } else {
      setItems(namedItems([]));
    }

    // The reserve half of the To make queue. Only for a person standing at a
    // branch: an admin or executive sees every branch's ORDER-BACKED work
    // below, but "what should we build for our own rack" is a question only
    // the branch itself can be asked.
    //
    // `profile?.branch_id` rather than the `myBranch` const further down: that
    // one is declared AFTER this callback, so naming it here is a
    // use-before-define that React's dependency lint does not catch.
    const atBranch = profile?.branch_id;
    if (atBranch != null) {
      const prod = await fetchProductionData(atBranch);
      // On a failed or cut read the three lists come back empty (#608), so no
      // shelf row and no partial picker can show; the flag says why.
      setShelfReadFailed(!prod.ok);
      setCategories(prod.categories);
      setMakeableItems(prod.items);
      setBranchStock(prod.stock);
    }
    setLoading(false);
    setLoadTick(n => n + 1);
  }, [showError, profile?.branch_id, applyItemFacts, openOneTransfer]);

  useEffect(() => { load(); }, [load]);

  // OPEN A TRANSFER ON THE TAB THAT HOLDS IT, for this person. One rule for the
  // two ways in: a link (the effect below) and a search match at another branch
  // (gate 675 D2's Look at). Lifted out of the effect at 79.22b.5.4 so the two
  // cannot land differently (locked rule 14); the reasoning stays beside it.
  const openOnItsTab = useCallback(async (found) => {
    // LAND ON THE TAB THAT ACTUALLY HOLDS THIS ROW. It used to force 'all',
    // the catch-all that no longer exists now that the seven stages are
    // disjoint -- a deep link would have opened the document over an empty
    // list, which reads as a broken page the moment you close the panel.
    // #602 / gate 553 D32, Session 79.21c. THIS USED TO GUESS, AND THE GUESS
    // BECAME LOAD-BEARING THE MOMENT D28 SHIPPED. It was a three-way --
    // finished, else approvable, else `to_load` -- which was harmless while
    // every tab drew every action. Now the tab DECIDES what the sheet offers,
    // so a transfer arriving at your branch would have landed on To load and
    // been shown a signpost instead of the arrival panel: strictly worse than
    // before, on the path an emailed link uses.
    //
    // deriveStage() is the same derivation D32 specifies and the same one the
    // detail falls back to, so a deep link and an unlabelled sheet cannot
    // disagree about which job a transfer is at.
    // #675 / gate 675 D22, Session 79.22b.5.5b -- THE QUESTIONS NOW LIVE IN
    // stageAt() (src/lib/transferDesk.js), shared with the desk's "It's now on"
    // after a press, so a bell and that sentence can never name different tabs.
    // The reasoning for each question stays here, where it was found.
    //
    // FOR THIS PERSON, not merely for the row. isLoadable() is TRUE of an
    // in-transit transfer -- it can still take another truck -- so reading
    // the row alone sent a DALLAS reader to To load for goods arriving AT
    // Dallas. Found by live test; D31's signpost said "This is arriving
    // here. Open To count in", which is the signpost working and also the
    // tell that the landing was wrong.
    const atReceiving = String(found.to_branch_id) === String(profile?.branch_id);
    // #655, Session 79.22b -- THE LANDING NEVER ASKED ABOUT A PICKUP POINT, so a
    // link to a transfer waiting at Austin, or at a spot on the road, landed
    // Dallas's driver on To count in with nothing to count. His job is on To
    // collect. The open-loads list arrives AFTER this runs, so the transfer's
    // own loads are read here, once, and the sheet opens with its tab rather
    // than flickering from one to the other.
    // #730, amended at 79.22b.5.5b -- and the same loads answer "is a truck
    // coming here to count in", which the transfer's status could not: a
    // part-sent transfer counted in so far is still "in transit", and one ended
    // "nothing more coming" with a truck on the road no longer is.
    let foundLoads = [];
    if (atReceiving) {
      const { data } = await fetchLoads(found.id);
      foundLoads = data || [];
    }
    // #608 / 675 D18 (acceptance: "A bell for a transfer that finished
    // months ago opens that transfer, on Finished") -- A FINISHED TRANSFER IS
    // NEVER WORK TO MAKE, LOAD OR APPROVE. deriveStage() asks "anything left to
    // make?" before "finished?", and this asked it of the lines alone, so
    // DAL-TRF-00004 - Nothing more coming, 40 never made - opened on To make,
    // where no card for it is listed. Found on screen at 79.22b.5.3. Only a
    // pile still waiting, or a truck still to count, outranks Finished now.
    setTab(stageAt(found, profile?.branch_id, { loads: foundLoads, finishedStatuses: FINISHED_STATUSES }));
    setSelected(found);
  }, [profile?.branch_id]);

  // The deep-link destination — gate D20. A transfer notification carries a
  // branch_transfers id, which is its OWN namespace: routing it to the item
  // setter would have opened inventory item 3 for transfer 3, silently.
  useEffect(() => {
    // #608 / gate 675 D18 -- A LINK OPENS ANY TRANSFER, WHATEVER ITS AGE. This
    // waited for `transfers` to be non-empty and searched only them, which was
    // every transfer while the page read every transfer. It reads the work in
    // hand now, so a bell for a transfer that finished months ago would find
    // nothing - and on a branch with no open work the wait never ended at all.
    // It waits for the first load instead, looks in everything the page holds,
    // and reads the transfer on its own when it is not there.
    if (openTransferId == null || loading) return;
    // #682, 79.22b.6c.2b -- Houston's answer bell opens the transfer saying why,
    // { id, alert: 'extra_answered' }, so its sheet shows S6's box; every other
    // way in is the id alone.
    const wanted = typeof openTransferId === 'object' ? openTransferId.id : openTransferId;
    // #682, 79.22b.6c.3 -- "Dallas counted more than your load sheet" opens the
    // tab where the question is answered, the card in view; not the transfer.
    if (typeof openTransferId === 'object' && openTransferId.alert === 'extra_counted') {
      if (setOpenTransferId) setOpenTransferId(null);
      tabChosen.current = true;
      setTab('confirm');
      // A search left on hides the tab's question cards (found on screen at
      // 79.22b.6c.3); the bell promises the card, so it clears the search.
      setSearchText('');
      setQuestionInView(String(wanted));
      return;
    }
    setBellAlert(typeof openTransferId === 'object' && openTransferId.alert
      ? { transferId: openTransferId.id, alert: openTransferId.alert } : null);
    if (setOpenTransferId) setOpenTransferId(null);
    (async () => {
      const held = t => String(t.id) === String(wanted);
      const found = transfers.find(held) || finishedRef.current.find(held) || extraRef.current.find(held)
        || await openOneTransfer(wanted);
      if (!found) {
        showError('That transfer could not be opened. It may have been removed, or the connection dropped.');
        return;
      }
      // NO CANCEL FLAG, deliberately: this effect clears openTransferId at
      // once, which re-runs it, and a clean-up cancelling on that re-run would
      // stop the sheet ever opening. The landing itself is openOnItsTab().
      await openOnItsTab(found);
    })();
  }, [openTransferId, loading, transfers, setOpenTransferId, openOneTransfer, openOnItsTab, showError]);

  // Keep the open document in step with a reload rather than holding a stale
  // copy - wherever the page now holds it (#608: in hand, on Finished's page,
  // or read on its own).
  useEffect(() => {
    if (!selected) return;
    const held = t => String(t.id) === String(selected.id);
    const fresh = transfers.find(held) || finished.rows.find(held) || extraTransfers.find(held);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [transfers, finished.rows, extraTransfers, selected]);

  const myBranch = profile?.branch_id;

  // #553 D12. Who the load sheet may offer as the driver. Fetched once per
  // branch, and a failure returns an empty list rather than throwing -- the
  // panel then shows only the escape, which is exactly what a tenant whose
  // drivers have no logins should see anyway.
  useEffect(() => {
    let alive = true;
    if (myBranch == null) { setDrivers([]); setDriversLoaded(true); return undefined; }
    setDriversLoaded(false);
    fetchBranchDrivers(myBranch).then(rows => {
      if (alive) { setDrivers(rows); setDriversLoaded(true); }
    });
    return () => { alive = false; };
  }, [myBranch]);

  // #517 / D66 -- THE DRIVER'S DAY, soonest first.
  //
  // Colby's ask, and it is the only ordering that answers the driver's real
  // question: what has to move today. The sorting lives in transferLoads.js
  // with its own tests rather than here -- an undated load sorts LAST, because
  // sorting it first would bury the ones that actually have a date.
  // ── THE SEVEN STAGES, EACH ONE PERSON'S JOB ───────────────────────────────
  //
  // See TRANSFER_TABS for why these are the stages and why they are in this
  // order. Every list below is DISJOINT from the others by construction: the
  // old page showed the same request in three tabs at once, which is what made
  // it unreadable.
  //
  // The load-shaped tabs are sorted by promised date, soonest first (D66), so
  // the top of a driver's list is what has to move today.
  // DECLARED HERE, ABOVE THE FIRST THING THAT READS IT. It used to sit below
  // these memos, which was harmless only while nothing above referenced it --
  // and the moment something did, the whole page went blank on a
  // temporal-dead-zone error that every unit test passes straight through.
  // That happened twice in one session; this is the fix that stops a third.
  //
  // ADMIN **AND** EXECUTIVE. Colby, 79.12: executive holds 90%+ of what admin
  // holds, and naming only one of them is how a widening quietly becomes a
  // narrowing for the other.
  //
  // #553 D19, Session 79.15g — THIS IS NOW THE DEFAULT RATHER THAN THE ONLY
  // ANSWER, and `branchView` below can narrow it.
  const seesEveryBranchByRole = isAdmin || isExecutive;

  // ── LOOKING AT ANOTHER BRANCH — #553 D19, #533 ────────────────────────────
  //
  // Colby's approved line: A BRANCH MANAGER LOOKS AT ONE OTHER BRANCH AT A
  // TIME; ADMIN AND EXECUTIVE SEE ALL BRANCHES AT ONCE.
  //
  // ** THE PICKER IS A LOOKING DEVICE AND CAN NEVER GRANT AN ACTION. ** That is
  // not a policy, it is the database: `inventory_transactions`' insert rule
  // ties a stock write to the CALLER'S OWN branch with no admin or executive
  // exemption. So every action on this page keeps reading `myBranch`, and only
  // the tab CONTENTS read what is being looked at. Wire an action to the picker
  // and it draws a button Postgres refuses, which is #492 in a nicer coat.
  //
  // WHY NOT canSeeAllBranches(): it is `isStaffWithPurchasing`, which ADMITS
  // MANAGERS, and D19's line deliberately does not — a manager gets one branch
  // at a time. Reusing the shared predicate here would widen the rule Colby
  // approved, silently, by borrowing a name that reads like it fits.
  //
  // null means "whatever my role defaults to", so an admin still opens on the
  // whole company exactly as before this shipped.
  const [branchView, setBranchView] = useState(initialBranchView ?? null);
  const [showBranchPicker, setShowBranchPicker] = useState(false);
  const [usersById, setUsersById] = useState({});
  // #603 -- the runs that made the goods, for the open transfer's history.
  const [production, setProduction] = useState([]);
  // #694, 79.22b.4.6.1b -- the open transfer's collections, for its history.
  const [handovers, setHandovers] = useState([]);
  // #694, 79.22b.4.6.2 -- and its counts, for the same reason.
  const [countIns, setCountIns] = useState([]);
  // #682 / #702, 79.22b.6c.2 -- its corrections, and the questions open about a
  // truck's extra: a corrected step is one line, and a line waiting on the
  // other branch answers Correct with the next step.
  const [corrections, setCorrections] = useState([]);
  const [loadExtras, setLoadExtras] = useState([]);
  // #594, second half -- WHILE THE NAMES ARE ON THEIR WAY THE HISTORY SAYS
  // NOTHING, rather than "not recorded" about people who are recorded. Seen at
  // 79.21b opening a transfer: half a second of a false sentence, then the names.
  const [namesLoading, setNamesLoading] = useState(false);
  // #580 part (a), gate 553 F2 -- "asked by {name} with {n} on the shelf" on a
  // To make row. The detail's names are per-transfer (usersById); a list row
  // needs its own, for the open own-shelf requests only.
  const [askerNames, setAskerNames] = useState({});
  // #651, Session 79.22b.1 -- PURCHASING WAS THE ONE ROLE WITH NO BRANCH
  // CONTROL HERE, and there is a path that walks straight into it.
  //
  // Jennifer runs purchasing for the whole company from Houston. Her queue is
  // company-wide and correct -- she sees Dallas's requests. Open Transfers and
  // she saw Houston's only, with no way to look anywhere else. A refused Dallas
  // transfer becomes a purchase request on her queue carrying "Raised after an
  // inter-branch transfer was refused", and to see what was asked for, who
  // refused it and why, she would open that transfer -- a Dallas one.
  //
  // COLBY RULED IT AT 79.22a: let them look. It is #369's precedent exactly --
  // the same predicate, `isAdminManagerOrExecutive`, corrected at 78g for the
  // same reason, where his ruling was also to let the role in. CLAUDE.md
  // carries the standing instruction that goes with it: check any NEW
  // branch-scoping gate against the purchasing role before shipping it. This
  // was one, and it was not.
  //
  // ** THE CALL SITE IS WIDENED, NEVER `canSwitchBranch` ITSELF. ** Three other
  // screens call it -- the catalog, label printing and Stock Home -- and each
  // is its own decision rather than a consequence of this one.
  //
  // `isStaffWithPurchasing` is the canonical predicate for exactly this set, so
  // the combination is not spelled out inline (locked rule 8).
  //
  // ** AND IT IS THE SAME PREDICATE THE NOTE ABOVE DECLINES, DELIBERATELY. **
  // `seesEveryBranchByRole` does not use it because it ADMITS MANAGERS and
  // D19's approved line gives a manager one branch at a time. That is an
  // argument about ALL-AT-ONCE, not about who gets a picker at all: admitting
  // managers is exactly the point here, because every manager already had this
  // control. The two variables want different sets, and now they say so.
  //
  // LOOKING STAYS LOOKING, and that is what makes this safe rather than a
  // widening of power: every button on this page reads `myBranch`, the header
  // says "looking only -- you can act at ...", and `canWithdrawTransfer` admits
  // admin, executive, a branch manager or the asker -- never purchasing. Read
  // in canApprove.js rather than assumed.
  //
  // AND IT GRANTS ONE BRANCH AT A TIME, not the company at once: the All
  // branches option is gated on `seesEveryBranchByRole`, which stays admin and
  // executive. Purchasing gets what a branch manager gets.
  const canLookElsewhere = isStaffWithPurchasing({ isAdmin, isManager, isExecutive, isPurchasing });

  // All-at-once is a WIDE-WINDOW affordance — #370's W8/W9 — and on #520's
  // precedent it does not vanish silently, it says why.
  const [isWorkbench, setIsWorkbench] = useState(window.innerWidth >= WORKBENCH_BREAKPOINT);
  // #581 -- the pointer, for the controls on this page that carry a floor:
  // the branch chip and the stage tabs. #417 D2: 40 with a finger, 32 with a
  // mouse, and the WIDTH decides layout while the POINTER decides size.
  //
  // #704, gate 665 F4, Session 79.22b.4.6.1c -- and the WIDTH, for the desk
  // column: from 1024px up the tab's contents stop at 620px (below).
  const { coarse, atLeast } = useTier();
  const onDesk = atLeast('desk');
  // #757 -- a phone draws the stages as M4's one row; a tablet and a desk as
  // paper tabs (tiers.js: the phone tier is below 600px).
  const onPhone = !atLeast('tablet');
  useEffect(() => {
    const onResize = () => setIsWorkbench(window.innerWidth >= WORKBENCH_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── #675 / GATE 675 D19, Session 79.22b.5.5 -- THE DESK FILLS THE WINDOW ───
  //
  // From 1024px the page is the window's height below the top bar: the title,
  // branch control, tabs and the tab's line across the top, and under them the
  // list and the chosen transfer, each scrolling on its own so the transfer's
  // buttons stay put while the list scrolls (#663's rule, the Purchasing
  // Queue's shell). Measured from where the page actually starts rather than a
  // fixed top-bar figure, so a message above the tabs shrinks the split instead
  // of pushing it off the window. A window too short to be useful keeps a floor
  // and lets the window scroll, rather than squeezing both columns to nothing.
  const pageRef = useRef(null);
  // #755 -- the page's own width inside its padding, followed as it changes:
  // opening the left-hand menu narrows the page without resizing the window,
  // so a resize listener cannot see it. The split needs room for the list and
  // a transfer column (deskSplits()); below that the desk is one column and
  // the transfer is today's sheet.
  const [pageWidth, setPageWidth] = useState(null);
  useLayoutEffect(() => {
    const el = pageRef.current;
    if (!el) return undefined;
    const read = () => {
      const s = window.getComputedStyle(el);
      setPageWidth(Math.round(el.clientWidth - (parseFloat(s.paddingLeft) || 0) - (parseFloat(s.paddingRight) || 0)));
    };
    read();
    // A window resize too: the observer only reports while the page is being
    // drawn, and a resize listener costs nothing beside it.
    window.addEventListener('resize', read);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    if (ro) ro.observe(el);
    return () => {
      window.removeEventListener('resize', read);
      if (ro) ro.disconnect();
    };
  }, []);
  const split = deskSplits({ deskTier: onDesk, pageWidth });
  const [deskHeight, setDeskHeight] = useState(null);
  useLayoutEffect(() => {
    if (!split) { setDeskHeight(null); return undefined; }
    const measure = () => {
      const el = pageRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const below = parseFloat(window.getComputedStyle(el.parentElement || el).paddingBottom) || 0;
      setDeskHeight(Math.max(Math.round(window.innerHeight - top - below), 480));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [split]);

  // WHICH SINGLE BRANCH IS BEING READ. Falls back to my own, never to nothing:
  // an empty branch id would empty every tab and read as "there is no work".
  // #754, Session 79.22b.5.5 -- AND ALL BRANCHES ASKS THE WIDTH, as the branch
  // control below already did. The default for an admin or executive was All
  // branches at any width, so on a phone the page showed every branch while
  // the branch choices said All branches needs a wider window. Below the
  // workbench width they open on their own branch, and a choice of All
  // branches made on a wide window drops back to it when the window narrows
  // (Colby's R1: Stock Home's rule). See branchViewFor().
  const { seesEveryBranch, lookingAt } = branchViewFor({ branchView, seesEveryBranchByRole, isWorkbench, myBranch });
  const lookingElsewhere = !seesEveryBranch && String(lookingAt) !== String(myBranch);

  // ── WHOSE WORLD THIS SCREEN SHOWS -- #531, Session 79.12 ──────────────────
  //
  // COLBY, 79.12: "will this transfer screen have a branch filter, or will the
  // entire company be able to see everything?"
  //
  // IT WAS BOTH, ON THE SAME SCREEN, AND THAT WAS THE DEFECT. Four tabs read
  // `mine`, which widens for admin and executive; these three took a BRANCH
  // and no role at all, so they structurally could not widen for anybody. An
  // admin or executive standing at Dallas saw the company's work on four tabs
  // and Dallas's work on three, with nothing saying which was which. Two
  // filtering mechanisms written at different times, quietly disagreeing.
  //
  // WIDENED RATHER THAN NARROWED, and 79.6 is why: narrowing To make to the
  // viewer's own branch is exactly the bug D24 was logged for, where Houston's
  // production queue was invisible to the company's own owner.
  //
  // THE UNION IS PER BRANCH, the same shape toMake already uses below. It
  // cannot double-count: a load has ONE destination and ONE final stop, and a
  // hub pile belongs to ONE waiting branch, so each appears for exactly one id.
  const viewBranchIds = useMemo(() => (seesEveryBranch
    ? [...new Set(branches.map(b => b.id))]
    : [lookingAt]).filter(id => id != null),
    [seesEveryBranch, branches, lookingAt]);

  // #675 D7, Session 79.22b.5.4b -- each tab in its chosen order. sortByPromised()
  // still reads each truck's transfer and promised date for the card; Due soonest
  // (the default) orders by the one Due rule, dueDateOf().
  const arriving  = useMemo(() => sortRows(sortByPromised(
    viewBranchIds.flatMap(id => arrivingAt(openLoads, id))), sortFor('count_in', sorts)), [openLoads, viewBranchIds, sorts]);
  const collect   = useMemo(() => sortRows(sortByPromised(
    viewBranchIds.flatMap(id => toCollectFor(openLoads, id))), sortFor('collect', sorts)), [openLoads, viewBranchIds, sorts]);

  // ** ON THE WAY IS THE ONE TAB THAT CANNOT BE UNIONED, AND THE REASON IS
  // WORTH READING BEFORE ANYBODY "FIXES" IT. ** movingElsewhere() is defined by
  // NEGATION -- a truck that is not going to me and not finally for me. Union
  // that over four branches and one Houston-to-Austin load comes back once for
  // every branch uninvolved in it, which is both duplicated and meaningless.
  //
  // A PERSON WHO SEES EVERY BRANCH HAS NO ELSEWHERE. Every truck in motion is
  // somebody's job -- on its final leg, or standing at a hub - and they can
  // already see all of it in the two tabs above. So this is empty for them by
  // definition rather than by oversight, and the tab still carries the OWED
  // rows, which for that reader is "what has stalled anywhere in the company".
  const elsewhere = useMemo(() => (seesEveryBranch ? [] : sortRows(sortByPromised(movingElsewhere(openLoads, lookingAt)),
    sortFor('moving', sorts))),
    [openLoads, lookingAt, seesEveryBranch, sorts]);
  // What is REALLY at each yard, so a collect card names the pile rather than
  // the load that dropped part of it. See LoadCard's own note.
  const collectStops = useMemo(() => new Map(collect.map(l =>
    [String(l.id), waitingAtHub(openLoads, { id: l.transfer_id ?? null }, stopOf(l))])),
    [collect, openLoads]);

  const mine = useMemo(() => transfers.filter(t =>
    seesEveryBranch ||
    String(t.from_branch_id) === String(lookingAt) ||
    String(t.to_branch_id) === String(lookingAt)), [transfers, lookingAt, seesEveryBranch]);

  // ── #675 / GATE 675 D7-D11, Session 79.22b.5.4b -- ORDER AND NARROWING ────
  //
  // A profile that arrives after the first paint still brings its saved orders.
  useEffect(() => {
    const saved = getUiPreference(profile, SORT_PREFERENCE_KEY, null);
    if (saved && typeof saved === 'object') setSorts(saved);
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // D8 -- a choice re-sorts at once and is saved on the account, best-effort
  // (setUiPreference never blocks the list).
  const chooseSort = (tabId, sortId) => {
    const next = { ...sorts, [tabId]: sortId };
    setSorts(next);
    setUiPreference(profile, SORT_PREFERENCE_KEY, next);
  };
  // What applies on each tab: a filter a tab does not offer is neither applied
  // nor shown there (D9, F1), and comes back on a tab that offers it.
  const singleBranch = !seesEveryBranch;
  // The branches a read was made for: one branch, or 'all'.
  const viewScope = seesEveryBranch ? 'all' : String(lookingAt ?? '');
  const appliedFor = tabId => filtersApplied(filters, tabId, { singleBranch });
  const appliedHere = appliedFor(tab);
  const filtersOnHere = anyApplied(appliedHere);
  // Finished's narrowing is the database's (D11): its page and its search count.
  const finishedFilterKey = JSON.stringify(finishedArgs(appliedFor('finished')));
  const finishedFilters = useMemo(() => JSON.parse(finishedFilterKey), [finishedFilterKey]);
  const clearFilters = () => setFilters(NO_FILTERS);
  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  // ── #675 / GATE 675 D1-D4, Session 79.22b.5.4 -- ONE SEARCH, EVERY TAB ─────
  //
  // What is typed (`searchText`) and what the database last answered
  // (`search`). The answer stays applied while a newer search is on its way, so
  // the list stays as it was and says Searching… (F1); a failed search leaves
  // every list unfiltered, never half-filtered (X2). Search lives on the page,
  // so it carries across tabs within a visit and clears when the reader leaves
  // Transfers (D8).
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState({ term: null, status: 'idle', result: null });
  const searchTicket = useRef(latestOnly());
  const searchTimer = useRef(null);
  // D2 -- every branch the reader may look at: a team member their own branch
  // only (M2), everybody with the branch control every branch.
  const searchScopeIds = useMemo(
    () => (canLookElsewhere ? null : [myBranch].filter(id => id != null)),
    [canLookElsewhere, myBranch]);
  // The branches NOT in view, for D2's At other branches - none for somebody
  // looking at every branch, or who may look nowhere else.
  const otherBranchIds = useMemo(
    () => (canLookElsewhere && !seesEveryBranch
      ? branches.map(b => b.id).filter(id => String(id) !== String(lookingAt))
      : null),
    [canLookElsewhere, seesEveryBranch, branches, lookingAt]);
  const runSearch = useCallback(async (term) => {
    const ticket = searchTicket.current.next();
    setSearch(s => ({ ...s, term, status: 'searching' }));
    const r = await searchTransfers({
      term, scopeIds: searchScopeIds, viewIds: seesEveryBranch ? null : viewBranchIds, otherIds: otherBranchIds,
      // #675 D11 -- Finished's count is what Finished would list under its filters.
      finishedFilters,
    });
    // F1 -- a slow reply that lands after a newer search started is thrown away.
    if (!searchTicket.current.isLatest(ticket)) return;
    setSearch(r.error ? { term, status: 'failed', result: null } : { term, status: 'ok', result: r });
  }, [searchScopeIds, seesEveryBranch, viewBranchIds, otherBranchIds, finishedFilters]);
  // F3 -- about a third of a second after typing stops. Below two characters
  // nothing happens and nothing is said. Run again when the branch in view
  // changes and after every refresh (X2: "the search is run again against the
  // fresh lists").
  useEffect(() => {
    clearTimeout(searchTimer.current);
    // ANY CHANGE INVALIDATES A REPLY STILL ON ITS WAY, at once - not 300ms later
    // when the next search takes its own ticket. Without this, "st" answering
    // after "stake" was typed, or Dallas's answer after a switch to Houston,
    // was applied as the current one (Grok's review, 79.22b.5.4).
    searchTicket.current.next();
    const term = searchableTerm(searchText);
    if (!term) {
      setSearch({ term: null, status: 'idle', result: null });
      return undefined;
    }
    setSearch(s => ({ ...s, status: 'searching' }));
    searchTimer.current = setTimeout(() => runSearch(term), SEARCH_PAUSE_MS);
    return () => clearTimeout(searchTimer.current);
  }, [searchText, runSearch, loadTick]);
  // F3 -- Enter searches at once.
  const searchNow = () => {
    const term = searchableTerm(searchText);
    if (!term) return;
    clearTimeout(searchTimer.current);
    runSearch(term);
  };
  // What a search leaves applied: transfer id -> what matched, or null.
  const searchMatches = search.result?.usable ? search.result.open : null;
  const searchOn = !!searchMatches;
  const appliedTerm = searchOn ? search.result.term : null;

  // ── #608 / GATE 675 D16 -- FINISHED, A PAGE AT A TIME, FROM THE DATABASE ──
  //
  // Most recently finished first, with the total. Scoped the way `mine` is -
  // the transfer's FROM or TO branch is the one in view - by
  // find_branch_transfers() itself. A desk pages (Showing 1-25 of 212, Rows
  // per page, Previous, Next: the Ledger's control); a phone or tablet grows
  // the list 25 at a time (Show 25 more: the Catalog's). Read only while
  // Finished is open, again after every load(), and back to the first page
  // whenever the branch in view or the layout changes.
  // #675 D1 -- and when the search changes, because a page 3 of one search is
  // not a page of the next. Left alone when already on the first page, so the
  // reset does not read the same page twice.
  // #675 D7, D11 -- and when Finished's order or its filters change.
  const finishedSort = sortFor('finished', sorts);
  useEffect(() => {
    setFinishedAt(p => (p.offset === 0 && p.size === 25 ? p : { offset: 0, size: 25 }));
  }, [lookingAt, seesEveryBranch, onDesk, appliedTerm, finishedSort, finishedFilters]);
  useEffect(() => {
    if (tab !== 'finished') return undefined;
    let live = true;
    // #675 -- a page read for another search (or for none) is never kept on
    // screen while this one loads (Grok's review, 79.22b.5.4).
    // #675 D7, D11, Session 79.22b.5.4b -- the page is read for a search, an
    // order and a set of filters; a page read for another is never drawn.
    // Grok, 79.22b.5.4b -- and for the branches in view.
    const forTerm = finishedReadKey(appliedTerm, finishedSort, finishedFilters, viewScope);
    setFinished(f => (f.term === forTerm
      ? { ...f, state: 'loading' }
      : { rows: [], total: 0, term: forTerm, state: 'loading' }));
    (async () => {
      const r = await fetchFinishedPage({
        branchIds: seesEveryBranch ? null : [lookingAt].filter(id => id != null),
        limit: finishedAt.size,
        offset: finishedAt.offset,
        // #675 D1 -- a search reaches every finished transfer, not the 25 on screen.
        term: appliedTerm,
        // D7 and D11 -- Finished's order, and its narrowing, in the database.
        order: finishedOrder(finishedSort),
        filters: finishedFilters,
      });
      if (!live) return;
      if (r.error) { setFinished({ rows: [], total: 0, term: forTerm, state: 'failed' }); return; }
      // Past the end - the list shrank under the reader: back to the first page.
      if (!r.rows.length && finishedAt.offset > 0) { setFinishedAt(p => ({ ...p, offset: 0 })); return; }
      const rows = await withSuccessors(r.rows);
      if (!live) return;
      finishedRef.current = rows;
      setFinished({ rows, total: r.total, term: forTerm, state: 'ok' });
      const ids = itemIdsOf(rows);
      if (ids.length) {
        const facts = await readItemFacts(ids);
        if (live) applyItemFacts(facts, { merge: true });
      }
    })();
    return () => { live = false; };
  }, [tab, seesEveryBranch, lookingAt, viewScope, finishedAt, loadTick, applyItemFacts, appliedTerm, finishedSort, finishedFilters]);

  // ── #752, Session 79.22b.5.4b -- WAITING FOR {BRANCH} TO DECIDE ───────────
  //
  // Drawing A, approved (R2 at 79.22b.5.4): refusals still waiting for the
  // asking branch's decision, pinned at the top of Finished and never paged,
  // so one is two taps away however many transfers finish after it. Read only
  // while Finished is open, again after every load(); the asking branch in
  // view (every branch for somebody looking at all of them). What counts as
  // waiting is refusalAwaitsDecision(), the rule the sheet's routing panel
  // reads (#753), so a withdrawn transfer is never listed.
  //
  // A SEARCH NARROWS IT by the database's one matcher (D5): the finished
  // matches in view are read once and kept. From and To narrow it too (R1);
  // Finished's own date and How it ended do not hide it - it is work, not
  // history (D15).
  useEffect(() => {
    if (tab !== 'finished') return undefined;
    let live = true;
    // Grok's review, 79.22b.5.4b -- rows read for another branch or another
    // search are dropped at once, never shown under the new heading; a refresh
    // of the same branch and search keeps its rows while it reads.
    const key = waitingReadKey(viewScope, appliedTerm);
    setWaiting(w => (w.key === key
      ? { ...w, state: 'loading' }
      : { rows: [], cut: false, state: 'loading', key }));
    (async () => {
      const asking = seesEveryBranch ? null : [lookingAt].filter(id => id != null);
      const r = await fetchWaitingRefusals({ branchIds: asking });
      if (!live) return;
      if (r.error) { setWaiting({ rows: [], cut: false, state: 'failed', key }); return; }
      let rows = (await withSuccessors(r.rows)).filter(refusalAwaitsDecision);
      if (rows.length && appliedTerm) {
        // Grok's review, 79.22b.5.4b -- a waiting refusal is often OLD, and the
        // matches come newest first, 1,000 to a read: read on until every
        // waiting one has been seen or the matches run out, and never drop one
        // that a read cut short simply did not reach.
        const waitingIds = new Set(rows.map(t => String(t.id)));
        const found = new Set();
        let offset = 0;
        let total = 0;
        for (let page = 0; page < WAITING_SEARCH_PAGES; page += 1) {
          const { data: hits, error } = await supabase.rpc('find_branch_transfers', {
            p_scope: 'finished', p_branch_ids: asking, p_term: appliedTerm, p_limit: 1000, p_offset: offset,
          });
          if (!live) return;
          if (error) { setWaiting({ rows: [], cut: false, state: 'failed', key }); return; }
          const list = hits || [];
          total = list.length ? Number(list[0].total_count) || 0 : total;
          list.filter(h => h.matched_on).forEach(h => found.add(String(h.transfer_id)));
          offset += list.length;
          if (!list.length || offset >= total || [...waitingIds].every(id => found.has(id))) break;
        }
        const exhausted = offset >= total;
        rows = rows.filter(t => found.has(String(t.id)) || !exhausted);
      }
      if (!live) return;
      setWaiting({ rows: waitingOrder(rows), cut: r.cut, state: 'ok', key });
      const ids = itemIdsOf(rows);
      if (ids.length) {
        const facts = await readItemFacts(ids);
        if (live) applyItemFacts(facts, { merge: true });
      }
    })();
    return () => { live = false; };
  }, [tab, seesEveryBranch, lookingAt, viewScope, loadTick, appliedTerm, applyItemFacts]);

  // "Waiting on me" is the question a person opens this page to answer, and it
  // is three different questions depending on which end they are: approve it,
  // send it, or book it in.
  // `needsMe` WAS HERE AND IS GONE. It filtered three unrelated jobs into one
  // list -- approve it, send it, book it in -- under a tab called "Waiting on
  // me", and Colby's verdict at 79.10 was the right one: it answered neither
  // who "me" was nor what was being waited on. The three jobs are now three
  // stages with their own names, and `canApproveTransfer` still governs who
  // may act on any given row.

  // D24 — a filtered view, not an eighth status.
  //
  // COLBY, Session 79.6: "Where is Houston's production queue at?" He could not
  // find it, and that was a defect rather than a question.
  //
  // The first build filtered on `from_branch_id === my branch` for EVERY role,
  // so an admin at Dallas saw an empty queue and therefore NO TAB AT ALL —
  // Houston's work was invisible to the company's own owner. Every other screen
  // in Stede shows an admin everything, and the database agrees: the select rule
  // on stock movements exempts admin and executive explicitly. Hiding this one
  // was my narrowing, not the gate's.
  //
  // So admin and executive see every branch's make-work, with the branch named
  // on the row; everybody else sees their own. Note this changes only what is
  // VISIBLE — the ship button still requires being at that branch, because the
  // database refuses anyone else, and that is D4 rather than a UI rule.
  const toMake = useMemo(() => {
    // D19 — `lookingAt`, not `myBranch`. These two filters were the LAST
    // looking-rules still keyed on where the reader works, and a Houston view
    // came back almost empty because of it. That is #531's own finding in
    // miniature: two filtering mechanisms written at different times, quietly
    // disagreeing. Found by switching the picker to Houston and reading the
    // tab counts, not by reading the code.
    // #675 D7 -- in the chosen order; productionRows() below still puts other
    // branches' orders, then our own shelf, then reserve rows (553 D24).
    if (!seesEveryBranch) return sortRows(productionQueue(mine, lookingAt), sortFor('to_make', sorts));
    const branchIds = [...new Set(mine.map(t => t.from_branch_id))];
    return sortRows(branchIds
      .flatMap(id => productionQueue(mine, id))
      .sort((a, b) => new Date(a.requested_at || 0) - new Date(b.requested_at || 0)), sortFor('to_make', sorts));
  }, [mine, lookingAt, seesEveryBranch, sorts]);

  // #515 gate screen 1, callouts A and B. The queue is ORDERS AND OUR OWN RACK
  // IN ONE LIST, told apart by a chip, because a fabricator's day is one queue
  // rather than two screens.
  //
  // Reserve rows appear because stock is low, NOT because anybody asked. This
  // is the only place in Stede where a reorder point produces work rather than
  // a purchase — everywhere else a low count means somebody should buy
  // something; here it means the branch reading the row should build something.
  const reserve = useMemo(
    () => reserveCandidates({
      items: makeableItems, stock: branchStock, categories, branchId: myBranch,
    }),
    [makeableItems, branchStock, categories, myBranch]);

  // #580 part (a), gate 553 D24: other branches' orders, then our own shelf,
  // then reserve rows -- and an item's reserve row steps aside while a request
  // for it is open, confirmed or not.
  // #689 D19, Session 79.22b.5.1 -- the rule lives in transferWork.js, shared
  // with Stock Home's shortcut row so the two counts cannot disagree.
  const openOwnShelf = useMemo(
    () => openOwnShelfFor(transfers, myBranch),
    [transfers, myBranch]);

  const makeRows = useMemo(
    () => productionRows({ orders: toMake, reserve, openOwnShelf }),
    [toMake, reserve, openOwnShelf]);

  // Everything this branch makes, low or not — the list behind the second
  // doorway. Deliberately NOT filtered by reorder point: that filter is what
  // made the first build unreachable.
  const makeable = useMemo(
    () => makeableAtBranch({ items: makeableItems, categories, branchId: myBranch }),
    [makeableItems, categories, myBranch]);

  // Gate D47 — what this branch makes that nobody has priced. Anything on this
  // list is worth NOTHING on paper: a run records a costless ledger row, and
  // anything sent to another branch arrives valued at nothing. D45 decided
  // WHICH number to use; this is what happens when that number was never set.
  const unpriced = useMemo(
    () => unpricedMakeables({ items: makeableItems, categories, branchId: myBranch }),
    [makeableItems, categories, myBranch]);

  // Open the sheet for an item, working out what is on this branch's shelf.
  // Shared by the reserve row and the picker so the two cannot drift — the
  // reserve row already knows its count, the picker has to look it up.
  // #761, 79.22b.6c.3 -- `index` is where the low-shelf card sat, so its answer
  // keeps that place (T4); the picker's runs answer at the top. Opening the
  // sheet is a press, so the last answer and its Undo end here (F3).
  function startRecording(item, knownQty, index = 0) {
    const fromShelf = branchStock.find(s =>
      String(s.item_id) === String(item.id) && String(s.branch_id) === String(myBranch));
    setCardNote(null);
    setRecording({
      item,
      qtyOnHand: knownQty != null ? knownQty : (Number(fromShelf?.qty_on_hand) || 0),
      branchName: branchNames[String(myBranch)],
      index,
    });
  }

  // #761, T4 -- THE ANSWER A RUN FOR THE SHELF NEVER HAD, kept on its card until
  // the next press, even after the rack is no longer low (makeRowsShown).
  function answerShelfRun(run, result) {
    setCardNote({
      tab: 'to_make', index: run?.index ?? 0, kind: 'done',
      reserveItem: run?.item || null, qtyOnHand: result?.qtyOnHand,
      shelfRun: { txId: result?.txId },
      text: recordedLine({ qty: result?.qty, itemName: run?.item?.name, branchName: run?.branchName, qtyOnHand: result?.qtyOnHand }),
    });
  }

  // The Undo: the database takes the whole run back for the person who
  // recorded it (undo_record_production()), and sends nothing (#694). The
  // answer becomes "Undone — …"; a refusal answers in red in the same place.
  async function undoShelfRun(note) {
    setBusy(true);
    clearMessage();
    setQuestionNote(null); setQuestionRefusal(null); setBellAlert(null);
    const r = await undoRecordProduction(note?.shelfRun?.txId);
    if (r.error) {
      setCardNote({ ...note, kind: 'error', text: plainTransferRefusal(r.error.message).text });
    } else {
      setCardNote({ ...note, kind: 'undone', qtyOnHand: r.qtyOnHand, shelfRun: null });
    }
    await load();
    setBusy(false);
  }
  const shelfRunNoteFor = (itemId) => (cardNote && !cardNote.desk && cardNote.tab === 'to_make'
    && cardNote.reserveItem && String(cardNote.reserveItem.id) === String(itemId) ? cardNote : null);

  // STAGE 1 -- waiting on the SUPPLYING branch to agree and set a date. Not
  // "waiting on me": whether you can act is a separate question from what
  // stage the work is at, and mixing the two is what made the old tab
  // unreadable. Admins and executives see every branch's.
  const awaitingConfirm = useMemo(
    () => sortRows(mine.filter(t => isApprovable(t)), sortFor('confirm', sorts)),
    [mine, sorts]);

  // STAGE 3 -- something is standing at MY branch with a truck still to fill.
  // Approved or already part-sent, and not yet fully out the door.
  // #553 D3, Session 79.15e — WHAT IS STANDING READY WITH NO TRUCK UNDER IT.
  //
  // This read `requested > shipped`, which is why this tab and To make held the
  // same transfers: both were asking "is there anything left on this order",
  // and neither could ask "has it been built", because until D1 no number
  // counted the making. It reads `shipped < ready` now — the exact mirror of
  // To make's `ready < requested`.
  //
  // A TRANSFER CAN NOW SIT ON BOTH TABS AT ONCE AND THAT IS CORRECT: 40 ready
  // to load, 60 still to make. Colby was asked about that consequence
  // explicitly and took it. It is the opposite of the old duplicate, where both
  // tabs showed the same 100 for the same reason.
  // #580 part (a), found ON THE SCREEN at 79.21b: this tab carried its OWN copy
  // of "can this go on a truck", and isLoadable() -- the one rule, which refuses
  // a request for a branch's own shelf -- was never asked. So the moment 6 of
  // Houston's own-shelf frames were signed off, the request sat on To load,
  // offering a load the database refuses. Now it asks isLoadable() AND whether
  // anything is ready: two questions, one of them shared.
  // #628, Session 79.22b -- the rule now lives in ONE place, hasReadyToLoad(),
  // shared with the transfer sheet, because the two copies disagreed.
  // #628 half 2, Session 79.22b -- APPROVED BY COLBY AS DRAWN: a made-to-order
  // request the sending branch's SHELF can fill is listed here too, for the
  // people who say how a line was filled - and only them, because a driver is
  // never asked that (gate 517 D64). What each branch holds is already on the
  // page, fetched for the cost that travels.
  const howFilledAsker = canStateHowFilled(auth);
  const shelfAt = useCallback(
    branchId => itemId => Number(receiverStock[`${itemId}:${branchId}`]?.qty_on_hand) || 0,
    [receiverStock]);
  // #721, Session 79.22b.5.1b -- WHAT EACH BRANCH HAS ALREADY PROMISED OFF ITS
  // SHELF, from the transfers this page already holds: every open transfer is
  // on it, and gate 675 D15 keeps every open one on it when the list is bounded.
  // Read by the approval card, the refusal panel's spare and the To load note.
  const promisedByKey = useMemo(() => promisedFromShelf(transfers), [transfers]);
  // #697, gate 665 D23, Session 79.22b.5.2 -- WHAT IS READY AT A HUB FOR
  // ANOTHER BRANCH IS THAT BRANCH'S PICKUP. The hub's switch is read off the
  // branches this page already holds. `hubPickups` joins To collect, drawn as a
  // pile at the hub; `awaitingCollection` stays on the hub's To load, saying so,
  // with nothing to press and no count. The rules are transferWork.js's.
  const hubIds = useMemo(() => hubIdsOf(branches), [branches]);
  const hubPickups = useMemo(() => sortRows(sortByPromised(
    viewBranchIds.flatMap(id => hubPickupsFor(transfers, id, hubIds)).map(asHubPickup)), sortFor('collect', sorts)),
    [transfers, viewBranchIds, hubIds, sorts]);
  const awaitingCollection = useMemo(
    () => viewBranchIds.flatMap(id => awaitingCollectionAt(transfers, id, hubIds)),
    [transfers, viewBranchIds, hubIds]);
  // #689 D19 -- toLoadList() in transferWork.js, the same rule Stock Home counts.
  const toLoad = useMemo(
    () => sortRows(toLoadList(mine, seesEveryBranch ? null : lookingAt, { howFilledAsker, shelfAt, hubIds }),
      sortFor('to_load', sorts)),
    [mine, lookingAt, seesEveryBranch, howFilledAsker, shelfAt, hubIds, sorts]);

  // #531 -- THE FOURTH QUESTION, AND THE RECEIVING END OF THE ONE ABOVE.
  // What is owed to this branch with nothing on the road: no truck, no pile
  // at a yard, and the request still open. It shows in On the way beside
  // the other branches' trucks, because both mean "not moving on my
  // say-so" -- except these OPEN, since there is something to do.
  //
  // ** DECLARED HERE RATHER THAN WITH THE LOAD MEMOS ABOVE, AND THAT IS
  // NOT TIDINESS. ** It reads `mine`, which is declared below them, so up
  // there it is a temporal-dead-zone crash that renders the whole page
  // blank -- and every unit test passes, because the filter is pure and
  // tested on its own while nothing renders this component. Found by
  // opening the page at 79.12, which is the only thing that could have.
  //
  // WIDENS WITH THE OTHER THREE, per branch, and re-sorted afterwards because
  // owedToBranch() sorts within one branch and a flatMap would interleave four
  // separately-sorted lists. Discrepancies first, then longest-waiting.
  // #676 -- and the transfers each branch SENT that came up short, so the
  // sender can reach the write-off #531 part 2 gave it. Deduplicated by
  // transfer, because a view of every branch sees both ends of one.
  // #689 D19 -- owedList() in transferWork.js, shared with Stock Home.
  // #675 D7 -- the owed rows stay above the trucks, short ones first, as #531
  // placed them; within each kind, the tab's chosen order (a row with no truck
  // has nothing to sort by on On the road longest, and keeps its place).
  const owed = useMemo(() => {
    const rows = owedList(mine, openLoads, viewBranchIds);
    const order = sortFor('moving', sorts);
    return [...sortRows(rows.filter(r => r.kind === 'short'), order),
      ...sortRows(rows.filter(r => r.kind !== 'short'), order)];
  }, [mine, openLoads, viewBranchIds, sorts]);

  // #675 -- Finished as drawn: the page read for the search now applied, or an
  // empty page loading. The first paint on returning to Finished after the
  // search changed elsewhere would otherwise show the old search's page.
  const finishedKeyNow = finishedReadKey(appliedTerm, finishedSort, finishedFilters, viewScope);
  const finishedNow = finished.term === finishedKeyNow
    ? finished
    : { rows: [], total: 0, term: finishedKeyNow, state: 'loading' };
  // #752 -- the waiting group, narrowed by From and To (R1); Finished's own
  // date and How it ended leave it alone, because it is work.
  // Grok, 79.22b.5.4b -- only rows read for the branch and search now on screen.
  const waitingNow = waiting.key === waitingReadKey(viewScope, appliedTerm)
    ? waiting : { rows: [], cut: false, state: 'loading', key: null };
  const waitingShown = keepFiltered(waitingNow.rows, { ...appliedFor('finished'), when: null, ended: null });

  const visible = tab === 'confirm' ? awaitingConfirm
    : tab === 'to_make' ? toMake
    : tab === 'to_load' ? toLoad
    // #608 / 675 D16 -- the page of Finished the database returned, in its
    // order: most recently finished first.
    : tab === 'finished' ? finishedNow.rows
    : mine.filter(t => OPEN_STATUSES.includes(t.status));

  // ── #665 D21 -- THE CARD A PRESS JUST ACTED ON STAYS WHERE IT WAS ─────────
  //
  // A press that finishes a card's job on its tab -- the last frame signed
  // off, the last ready stake put on a truck -- would take the card out of the
  // list at the moment its answer is written on it, and the answer would go
  // with it. So until the next action or a change of tab, the card keeps its
  // place, drawn from the fresh row, with its answer (gate 665 F3: the answer
  // "stays attached to its card by transfer, so a list that re-sorts cannot
  // move it onto another card"). 79.22b.4.6's Undo lives in exactly this place.
  // The tab pills still count the real work; nothing here reaches them.
  //
  // #675 / GATE 675 D22, Session 79.22b.5.5b -- AND A PRESS ON THE DESK'S RIGHT
  // SIDE DOES THE SAME, on every tab: "The card keeps its place in the list,
  // showing its new stage, until the next choice or a change of tab." Its note
  // carries `desk: true`, and its answer is drawn on the right, never on the
  // card (see run()). A press there can take the transfer out of the work in
  // hand altogether -- refused, withdrawn, nothing more coming -- so the card is
  // drawn from wherever the page now holds it. A desk note with no `index`
  // was pressed on a transfer whose card was not on the tab: nothing is kept.
  const pinnedTransfer = useMemo(() => {
    if (!cardNote) return null;
    const held = t => String(t.id) === String(cardNote.transferId);
    return transfers.find(held) || extraTransfers.find(held) || finished.rows.find(held) || null;
  }, [cardNote, transfers, extraTransfers, finished.rows]);
  const makeRowsShown = useMemo(() => {
    // #761, T4 -- a run for the shelf usually lifts the rack past its reorder
    // point, which takes its card off the list; the card keeps its place with
    // the answer and Undo until the next press, drawn as T4 (pinned). A card
    // still low keeps its own place and shows the answer on it (call A, R1).
    const run = cardNote?.tab === 'to_make' && !cardNote.desk ? cardNote.reserveItem : null;
    if (run) {
      if (makeRows.some(r => r.kind === 'reserve' && String(r.item.id) === String(run.id))) return makeRows;
      const out = [...makeRows];
      out.splice(Math.min(cardNote.index ?? 0, out.length), 0, {
        kind: 'reserve', key: `pinned-i${run.id}`, item: run,
        qtyOnHand: cardNote.qtyOnHand, reorderPoint: null, pinned: true,
      });
      return out;
    }
    if (!pinnedTransfer || cardNote?.index == null || cardNote.tab !== 'to_make'
      || makeRows.some(r => r.kind === 'order' && String(r.transfer.id) === String(pinnedTransfer.id))) return makeRows;
    const out = [...makeRows];
    out.splice(Math.min(cardNote.index ?? 0, out.length), 0, {
      kind: 'order', key: `pinned-${pinnedTransfer.id}`, transfer: pinnedTransfer,
      own: isOwnShelf(pinnedTransfer), pinned: true,
    });
    return out;
  }, [makeRows, pinnedTransfer, cardNote]);
  const loadRowsShown = useMemo(() => {
    if (!pinnedTransfer || cardNote?.index == null || cardNote.tab !== 'to_load' || cardNote.sub === 'awaiting'
      || toLoad.some(t => String(t.id) === String(pinnedTransfer.id))) return toLoad;
    const out = [...toLoad];
    out.splice(Math.min(cardNote.index ?? 0, out.length), 0, { ...pinnedTransfer, __pinned: true });
    return out;
  }, [toLoad, pinnedTransfer, cardNote]);
  // #675 D22 -- the three tabs a card press never reaches, kept in place the
  // same way after a press on the right: Awaiting confirmation (approve, refuse,
  // answer each item, withdraw), Out & owed (close short, nothing more coming)
  // and Finished's waiting group (decide a refusal). Drawn from the fresh row,
  // so the card shows its new stage -- "Approved", "Closed short", decided.
  const keptOn = (tabId, list, isIt) => (pinnedTransfer && cardNote?.desk && cardNote.index != null
    && cardNote.tab === tabId && !list.some(isIt)
    ? [...list.slice(0, cardNote.index), { __pinned: true, transfer: pinnedTransfer }, ...list.slice(cardNote.index)]
    : list);
  const confirmShown = keptOn('confirm', awaitingConfirm, t => String(t.id) === String(pinnedTransfer?.id))
    .map(r => (r.__pinned && r.transfer ? { ...r.transfer, __pinned: true } : r));
  const owedShown = keptOn('moving', owed, r => String(r.transfer.id) === String(pinnedTransfer?.id));
  const waitingKept = keptOn('finished', waitingShown, t => String(t.id) === String(pinnedTransfer?.id))
    .map(r => (r.__pinned && r.transfer ? { ...r.transfer, __pinned: true } : r));
  // Grok's review, 79.22b.5.5b -- and a hub's "collects this here" card on To
  // load, which the sheet can still send from: it is its own list (`sub`).
  const awaitingKept = cardNote?.sub === 'awaiting'
    ? keptOn('to_load', awaitingCollection, t => String(t.id) === String(pinnedTransfer?.id))
      .map(r => (r.__pinned && r.transfer ? { ...r.transfer, __pinned: true } : r))
    : awaitingCollection;
  // A note on the right side belongs to the right side: the card it kept in
  // place draws no answer of its own (D22), while a card press keeps its note.
  const cardOnlyNote = cardNote?.desk ? null : cardNote;
  const noteFor = (tabId, transferId) => (cardOnlyNote && cardOnlyNote.tab === tabId
    && String(cardOnlyNote.transferId) === String(transferId) ? cardOnlyNote : null);

  // #694, Session 79.22b.4.6.1b -- THE TO COLLECT CARD A PRESS EMPTIED KEEPS ITS
  // PLACE, with its answer and its Undo, as To make's and To load's do. Keyed by
  // the PILE -- the transfer and the place, toCollectFor()'s own key -- because
  // one transfer can wait at two places, and a card is one pile, not one truck.
  // It is drawn from the truck the card stood for, which the list still holds.
  const collectRowsShown = useMemo(() => {
    if (!cardNote || cardNote.tab !== 'collect' || !cardNote.key || cardNote.index == null
      || collect.some(l => pileKeyOf(l) === cardNote.key)) return collect;
    const base = openLoads.find(l => String(l.id) === String(cardNote.loadId));
    if (!base) return collect;
    const out = [...collect];
    // Through the same sortByPromised() the list uses, so the kept card keeps
    // the promised date the live one showed -- found on screen, where a raw
    // truck read "No date promised" under a pile due Oct 16.
    out.splice(Math.min(cardNote.index ?? 0, out.length), 0, { ...sortByPromised([base])[0], __pinned: true });
    return out;
  }, [collect, cardNote, openLoads]);
  const noteForPile = (key) => (cardOnlyNote && cardOnlyNote.tab === 'collect' && cardOnlyNote.key === key ? cardOnlyNote : null);

  // #697 -- A HUB PICKUP A PRESS EMPTIED KEEPS ITS PLACE, with its answer and
  // its Undo, as a pile's does. Keyed "hub:<transfer>" so it can never be taken
  // for a pile's key.
  const hubRowsShown = useMemo(() => {
    const key = cardNote && cardNote.tab === 'collect' && cardNote.index != null ? String(cardNote.key || '') : '';
    if (!key.startsWith('hub:') || hubPickups.some(p => `hub:${p.transfer_id}` === key)) return hubPickups;
    const t = transfers.find(x => `hub:${x.id}` === key);
    if (!t) return hubPickups;
    const out = [...hubPickups];
    out.splice(Math.min(cardNote.index ?? 0, out.length), 0, { ...sortByPromised([asHubPickup(t)])[0], __pinned: true });
    return out;
  }, [hubPickups, cardNote, transfers]);

  // #694, Session 79.22b.4.6.2 -- THE TO COUNT IN CARD A COUNT FINISHED KEEPS ITS
  // PLACE, with its answer and its Undo, as To collect's does. Keyed by the
  // TRUCK: a card on this tab is one truck. It is drawn from the truck as the
  // count found it, carried on the answer, because a counted truck can leave
  // the page's list entirely - the list stops watching a transfer the count
  // finished. The Undo puts the truck back on the list, and its own card
  // returns with the answer on it.
  const countRowsShown = useMemo(() => {
    if (!cardNote || cardNote.tab !== 'count_in' || cardNote.loadId == null || !cardNote.load || cardNote.index == null
      || arriving.some(l => String(l.id) === String(cardNote.loadId))) return arriving;
    const out = [...arriving];
    out.splice(Math.min(cardNote.index ?? 0, out.length), 0, { ...cardNote.load, __pinned: true });
    return out;
  }, [arriving, cardNote]);
  const noteForCount = (loadId) => (cardOnlyNote && cardOnlyNote.tab === 'count_in'
    && String(cardOnlyNote.loadId) === String(loadId) ? cardOnlyNote : null);

  // ── #675 / GATE 675 D1 -- WHAT A SEARCH LEAVES ON EACH TAB ────────────────
  //
  // Every list above, narrowed to the transfers the search matched; with no
  // search, the list as it is. Declared BELOW every list it reads: reading one
  // above its declaration blanks the whole page, which no unit test can see.
  // Reserve rows on To make are stock running low, not transfers, so a search
  // never matches one. The pills count the narrowed lists - D1: "each tab's
  // count pill shows how many matches that tab holds, Finished included" -
  // and opening the page on a person's job (above) still reads the full lists.
  const byTransfer = (list, idOf) => (searchOn ? keepMatching(list, idOf, searchMatches) : list);
  const byTruck = list => (searchOn ? keepMatchingTrucks(list, searchMatches) : list);
  const isMatchedOrder = r => r.kind === 'order' && searchMatches.has(String(r.transfer.id));
  // #675 D9-D10, Session 79.22b.5.4b -- then each tab's filters, AFTER the
  // search, so a pill keeps meaning "what is on this tab now". Finished's are
  // the database's (its page and its search count, above).
  const byFilters = (tabId, list) => keepFiltered(list, appliedFor(tabId));
  const sConfirm = byFilters('confirm', byTransfer(awaitingConfirm, t => t.id));
  // #682, Session 79.22b.6c.2b -- HOUSTON'S QUESTIONS ON AWAITING CONFIRMATION
  // (P6, callout L; 682 D13). Only for the people who may answer them -- the
  // truck line's current recorder or a branch lead at the sending branch,
  // purchasing never (canAnswerTruckExtra(), the database's own rule) -- and so
  // only while the page shows that person's own branch. Not during a search,
  // which lists the transfers it matched. The one a press answered stays in its
  // place with what happened until the next press (gate 665's pinned card).
  const showsMyBranch = seesEveryBranch || String(lookingAt) === String(myBranch);
  const questionCards = showsMyBranch && !searchOn ? [
    ...(questionNote ? [{ ...questionNote.entry, pinned: questionNote.pinned, refusal: null, pressing: null, row: questionNote.row }] : []),
    ...truckQuestions
      .filter(r => r?.question && String(r.question.id) !== String(questionNote?.entry?.key))
      .filter(r => canAnswerTruckExtra(auth, { fromBranchId: r.question.from_branch_id, loadedBy: r.recorder }))
      .map(r => {
        const e = truckQuestionEntry(r, { branchNames });
        return e ? { ...e, row: r, refusal: questionRefusal?.key === e.key ? questionRefusal.text : null,
          pressing: questionPressing?.key === e.key ? questionPressing.answer : null } : null;
      })
      .filter(Boolean),
  ] : [];
  // #682, 79.22b.6c.3 -- the question's bell brings its card into view once the
  // tab has loaded; a question already answered has no card, and nothing moves.
  // It waits for a search the bell cleared to let go of the list.
  useEffect(() => {
    if (!questionInView || tab !== 'confirm' || loading || searchOn) return;
    const el = document.querySelector(`[data-question-transfer="${questionInView}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
    setQuestionInView(null);
  }, [questionInView, tab, loading, searchOn, questionCards.length]);
  // The pill counts the questions still waiting, never the one answered (L).
  const questionsWaiting = questionCards.filter(e => !e.pinned).length;
  const sMakeRows = byFilters('to_make', searchOn ? makeRows.filter(isMatchedOrder) : makeRows);
  const sMakeRowsShown = byFilters('to_make', searchOn ? makeRowsShown.filter(r => r.pinned || isMatchedOrder(r)) : makeRowsShown);
  const sToLoad = byFilters('to_load', byTransfer(toLoad, t => t.id));
  const sLoadRowsShown = byFilters('to_load', byTransfer(loadRowsShown, t => t.id));
  const sAwaitingCollection = byFilters('to_load', byTransfer(awaitingCollection, t => t.id));
  // #675 D22 -- as drawn, with a card a press on the right kept in place.
  const sAwaitingShown = byFilters('to_load', byTransfer(awaitingKept, t => t.id));
  const sElsewhere = byFilters('moving', byTruck(elsewhere));
  const sOwed = byFilters('moving', byTransfer(owed, r => r.transfer.id));
  // #675 D22 -- as drawn, with a card a press on the right kept in place.
  const sOwedShown = byFilters('moving', byTransfer(owedShown, r => r.transfer.id));
  const sCollect = byFilters('collect', byTransfer(collect, l => l.transfer_id));
  const sCollectRowsShown = byFilters('collect', byTransfer(collectRowsShown, l => l.transfer_id));
  const sHubPickups = byFilters('collect', byTransfer(hubPickups, p => p.transfer_id));
  const sHubRowsShown = byFilters('collect', byTransfer(hubRowsShown, p => p.transfer_id));
  const sArriving = byFilters('count_in', byTruck(arriving));
  const sCountRowsShown = byFilters('count_in', byTruck(countRowsShown));
  // #675 D22 -- Awaiting confirmation as drawn, with an ask approved or refused
  // on the right kept in its place.
  const sVisible = tab === 'finished' ? visible
    : byFilters(tab, byTransfer(tab === 'confirm' ? confirmShown : visible, t => t.id));
  const tabCounts = {
    // #682 -- and the truck questions this person may answer (callout L).
    confirm: sConfirm.length + questionsWaiting,
    to_make: sMakeRows.length,
    to_load: sToLoad.length,
    // #531 -- trucks PLUS what is owed with nothing moving. The pill answers
    // "is there anything in here", so it counts both.
    moving: sElsewhere.length + sOwed.length,
    // #697 -- and what is ready at a hub for this branch to pick up.
    collect: sCollect.length + sHubPickups.length,
    count_in: sArriving.length,
    // D1 -- Finished counted in the database, every finished transfer in view.
    finished: searchOn ? search.result.finishedTotal : 0,
  };
  // #675 D12 -- the applied filters on this tab, as chips, and the sheet's live
  // count: this tab's list as it now stands; Finished's total from its own read.
  const chipsHere = appliedChips(appliedHere, { branchNames, lookingAt, singleBranch });
  const sheetCount = tab === 'finished'
    ? (finishedNow.state === 'ok' ? finishedNow.total : null)
    : (tabCounts[tab] ?? 0);
  // F1 -- filters (not the search) leave this tab empty, and no other tab holds
  // a match either: say so, with Clear filters, rather than "No transfer matches".
  const filtersHideAll = filtersOnHere
    && !TRANSFER_TABS.some(x => x.id !== tab && tabCounts[x.id] > 0);
  // D3 -- why a card matched, when it is not on the card's face.
  const matchedFor = (transferId, row = null) => (searchOn
    ? matchLine(row?.__match || searchMatches.get(String(transferId)))
    : null);

  // ── #675 / GATE 675 D21, Session 79.22b.5.5 -- CHANGING TAB ON THE DESK ────
  //
  // The transfer stays open on the right when the new tab holds it, as the
  // reader would find it there now (after the search and the filters), and
  // otherwise the right side returns to "Choose a transfer to see it here."
  // Only a tab the READER chooses asks this: a signpost inside the transfer
  // (onGoToStage) and a link's landing (openOnItsTab) set the tab themselves
  // and keep it open, as D21 says. Below the desk the transfer is a sheet over
  // the page and the tabs cannot be reached while it is open.
  const chosenId = split && selected ? String(selected.id) : null;
  const chooseTab = (id) => {
    setTab(id);
    // #682 -- a change of tab ends an answered question's kept card.
    if (id !== tab) { setQuestionNote(null); setQuestionRefusal(null); }
    if (split && selected && id !== tab && !tabHoldsTransfer(id, selected, {
      confirm: sConfirm, toMake: sMakeRows, toLoad: sToLoad, awaiting: sAwaitingCollection,
      elsewhere: sElsewhere, owed: sOwed, collect: sCollect, hubPickups: sHubPickups,
      arriving: sArriving, waiting: waitingShown,
    }, { finishedStatuses: FINISHED_STATUSES, awaitsDecision: refusalAwaitsDecision })) {
      setSelected(null);
    }
  };
  // D2 -- MATCHES AT BRANCHES NOT IN VIEW: open ones from the work in hand the
  // page already holds, finished ones from the search's own read. Only for a
  // reader with the branch control who is looking at one branch.
  const otherBranchMatches = useMemo(() => {
    if (!searchOn || !otherBranchIds) return [];
    const touchesView = t => String(t.from_branch_id) === String(lookingAt) || String(t.to_branch_id) === String(lookingAt);
    const open = transfers
      .filter(t => searchMatches.has(String(t.id)) && !touchesView(t))
      .map(t => ({ ...t, __match: searchMatches.get(String(t.id)) }));
    return [...open, ...(search.result.otherFinished || [])];
  }, [searchOn, searchMatches, otherBranchIds, transfers, lookingAt, search.result]);
  // D2 -- Look at {branch}: the view switches to that branch and the transfer
  // opens on the tab that holds it. Looking stays looking - every button still
  // acts at the branch the reader works at.
  async function lookAtMatch(t) {
    setBranchView(lookAtBranchFor(t, myBranch));
    const held = x => String(x.id) === String(t.id);
    const found = transfers.find(held) || finished.rows.find(held) || extraTransfers.find(held)
      || await openOneTransfer(t.id);
    if (!found) {
      showError('That transfer could not be opened. It may have been removed, or the connection dropped.');
      return;
    }
    await openOnItsTab(found);
  }
  // A change of tab ends the answer, as the gate draws it.
  useEffect(() => { setCardNote(null); }, [tab]);
  // #675 / gate 675 D22, Session 79.22b.5.5b -- SO DOES THE NEXT CHOICE, for an
  // answer on the right: another card, Next, or closing the transfer. And the
  // desk giving way to one column, where there is no right side to hold it. A
  // card's own answer is left alone, exactly as before.
  useEffect(() => {
    setCardNote(n => (n?.desk && String(n.transferId) !== String(selected?.id ?? '') ? null : n));
  }, [selected?.id]);
  useEffect(() => {
    if (!split) setCardNote(n => (n?.desk ? null : n));
  }, [split]);

  // D22 -- WHERE THE CHOSEN TRANSFER'S CARD SITS ON THIS TAB, as drawn, so the
  // card keeps that place after a press on the right. On a truck tab the card is
  // the truck or the pile the press was about (`at`), drawn from the row as it
  // stood, as a press on the card itself does. No place when it is not drawn.
  // GROK'S REVIEW, 79.22b.5.5b -- THE PLACE IS COUNTED IN THE LIST IT GOES BACK
  // INTO: the tab's whole list, before any search or filter, because that is
  // the list each kept-card memo splices into. Counted in the list as drawn,
  // a card pressed second of two visible asks came back second of all ten,
  // somewhere else on screen. The same for a press on a card (cardRun).
  // And a hub's "collects this here" card on To load is its own list (`sub`).
  function placeInTab(tabId, transferId, { key = null, loadId = null } = {}) {
    const isIt = id => String(id) === String(transferId);
    const place = i => (i >= 0 ? i : null);
    if (tabId === 'confirm') return { index: place(awaitingConfirm.findIndex(t => isIt(t.id))) };
    if (tabId === 'to_make') return { index: place(makeRows.findIndex(r => r.kind === 'order' && isIt(r.transfer.id))) };
    if (tabId === 'to_load') {
      const i = toLoad.findIndex(t => isIt(t.id));
      if (i >= 0) return { index: i };
      const h = awaitingCollection.findIndex(t => isIt(t.id));
      return h >= 0 ? { index: h, sub: 'awaiting' } : { index: null };
    }
    if (tabId === 'moving') return { index: place(owed.findIndex(r => isIt(r.transfer.id))) };
    if (tabId === 'finished') return { index: place(waitingShown.findIndex(t => isIt(t.id))) };
    if (tabId === 'count_in' && loadId != null) {
      const i = arriving.findIndex(l => String(l.id) === String(loadId));
      return i >= 0 ? { index: i, loadId: arriving[i].id, load: arriving[i] } : { index: null };
    }
    if (tabId === 'collect' && key) {
      if (String(key).startsWith('hub:')) return { index: place(hubPickups.findIndex(p => `hub:${p.transfer_id}` === key)) };
      const i = collect.findIndex(l => pileKeyOf(l) === key);
      return i >= 0 ? { index: i, loadId: collect[i].id } : { index: null };
    }
    return { index: null };
  }
  function deskPinFor(at = null) {
    if (!selected) return { index: null };
    if (tab === 'count_in') return at?.load ? placeInTab(tab, selected.id, { loadId: at.load.id }) : { index: null };
    if (tab === 'collect') {
      if (!at?.stop) return { index: null };
      const key = `${selected.id}:${stopKey(at.stop)}`;
      const p = placeInTab(tab, selected.id, { key });
      return p.index == null ? p
        : { ...p, key, stop: { branchId: at.stop.branchId ?? null, place: at.stop.place ?? null } };
    }
    return placeInTab(tab, selected.id);
  }

  // ── #689 first half, gate 665 D18 -- TRANSFERS OPENS ON YOUR JOB ──────────
  //
  // Once, after the first load, so the page never jumps after a press. Every
  // tab stays visible (79.6), and the strip scrolls so the chosen one is in
  // view on a phone. Declared BELOW every list it reads: reading one above its
  // declaration blanks the whole page, which no unit test can see.
  //
  // The jobs are MINE -- at the branch I work at -- whatever branch the page
  // is showing, because an action is always taken at my own branch.
  //
  // #699, gate 665 D28, Session 79.22b.5.1 -- AND IT WAITS FOR THE DRIVER LIST.
  // To load and To collect are a person's job only if they drive, once anybody
  // at the branch is switched on; choosing before the list arrives would land
  // a Dallas installer on the Austin pickup and then never move. The rule and
  // the pill counts come from transferJobs(), the one Stock Home's row uses.
  useEffect(() => {
    if (tabChosen.current || loading || !driversLoaded) return;
    tabChosen.current = true;
    const next = openingTab(transferJobs({
      lists: { awaitingConfirm, toMake, makeRows, toLoad, owed, elsewhere, collect, hubPickups, arriving },
      openLoads, myBranch, profile, auth, drivers,
      // #682, 665 D18 -- a truck question this person may answer is something
      // to do on Awaiting confirmation (F3).
      truckQuestions: truckQuestions.filter(r => r?.question
        && canAnswerTruckExtra(auth, { fromBranchId: r.question.from_branch_id, loadedBy: r.recorder })),
    }));
    setTab(next);
    // THE STRIP ONLY, never the window. scrollIntoView() scrolls every
    // scrollable ancestor, the page included: found on screen at 79.22b.4.4,
    // where landing on To count in moved the whole page 278px sideways at an
    // 800px window. So the strip's own scroll is set, centring the tab in it.
    requestAnimationFrame(() => {
      const btn = document.querySelector(`[data-transfer-tab="${next}"]`);
      const strip = btn?.parentElement;
      if (!strip) return;
      const b = btn.getBoundingClientRect();
      const s = strip.getBoundingClientRect();
      strip.scrollLeft += (b.left - s.left) - (s.width - b.width) / 2;
    });
  }, [loading, driversLoaded, drivers, awaitingConfirm, toMake, toLoad, owed, openLoads, makeRows, elsewhere,
      collect, hubPickups, arriving, myBranch, profile, auth, truckQuestions]);

  // #517 -- the loads on the open transfer, refreshed whenever it changes.
  //
  // #675 D19, Session 79.22b.5.5 (Grok's review) -- ONLY THE NEWEST READ IS
  // APPLIED. On the desk a reader goes from one card straight to the next, never
  // through "nothing open", so two reads overlap all the time: the first
  // transfer's answer landing after the second's put its runs, collections,
  // counts and names on the second transfer's sheet, and left its trucks
  // waiting on "…" for good (`loadsFor` named the wrong transfer). #671 fixed
  // the load rows alone. Every await now checks it is still the newest read,
  // and a different transfer starts from an empty history rather than the
  // last one's.
  const loadsTicket = useRef(latestOnly());
  const loadsShownFor = useRef(null);
  const refreshLoads = useCallback(async (transferId, transfer = null) => {
    const ticket = loadsTicket.current.next();
    const stale = () => !loadsTicket.current.isLatest(ticket);
    if (transferId == null) {
      loadsShownFor.current = null;
      setLoads([]); setLoadsFor(null); setUsersById({}); setProduction([]); setHandovers([]); setCountIns([]);
      setCorrections([]); setLoadExtras([]); setNamesLoading(false); return;
    }
    if (String(loadsShownFor.current) !== String(transferId)) {
      loadsShownFor.current = transferId;
      setUsersById({}); setProduction([]); setHandovers([]); setCountIns([]); setCorrections([]); setLoadExtras([]);
    }
    setNamesLoading(true);
    // #682 / #702, 79.22b.6c.2 -- THE CORRECTIONS AND OPEN QUESTIONS ARRIVE WITH
    // THE TRUCKS, never after them: a truck's corrected number lives on its own
    // row, and drawn without its correction for a moment it read as a plain
    // "Loaded 3" with Correct offered to the wrong person (found on screen at
    // 79.22b.6c.2). Read on every tier (F4: never behind a width guard). The
    // corrector's name is kept on the record itself, so no extra name read.
    const [{ data }, { data: fixes }, { data: asks }] = await Promise.all([
      fetchLoads(transferId), fetchCorrections(transferId), fetchLoadExtras(transferId),
    ]);
    if (stale()) return;
    setLoads(data || []);
    setCorrections(fixes || []);
    setLoadExtras(asks || []);
    // #671 -- WHOSE loads these are. Opening transfer B straight from A never
    // passed through null, so A's rows sat in `loads` while B's fetch was in
    // flight and the load sheet named the next truck from A's count --
    // HOU-TRF-00010-1, a reference already on a truck that left that morning.
    setLoadsFor(transferId);
    // #603 -- read beside the loads, so the history has the whole journey
    // before it draws: the runs are part of it even though nothing moved.
    const { data: runs } = await fetchProductionRuns(transferId);
    if (stale()) return;
    setProduction(runs || []);
    // #694, 79.22b.4.6.1b -- and the collections: one taken back is part of
    // the journey, and the truck it closed no longer says so itself.
    const { data: collections } = await fetchHandovers(transferId);
    if (stale()) return;
    setHandovers(collections || []);
    // #694, 79.22b.4.6.2 -- and the counts: one taken back is in the journey,
    // and the truck it was on no longer says so itself.
    const { data: counts } = await fetchCountIns(transferId);
    if (stale()) return;
    setCountIns(counts || []);
    // #553 D20. The history names a person on every line, and those people sit
    // at whichever branch they were standing at -- so this is read off the
    // loads rather than from the branch driver list.
    //
    // #570 H3: AND off the transfer itself. The transfer's own events -
    // requested, approved by each branch, closed - name people who may never
    // have touched a truck, and on a transfer with no loads they are the ONLY
    // people in the history. Reading loads alone rendered every one of those
    // lines as "not recorded".
    //
    // #594, Session 79.21b -- READ FROM THE DATABASE WHEN NOT HANDED ONE. run()
    // calls this with the id alone after every action, so every name on the
    // transfer's own lines read "not recorded" the moment somebody acted - and
    // the person who just acted (who decided a refusal, who withdrew it) is on
    // the fresh row and on no copy the screen was holding.
    let row = transfer;
    if (!row) {
      const { data: fresh } = await supabase
        .from('branch_transfers').select('*').eq('id', transferId).maybeSingle();
      row = fresh || null;
      if (stale()) return;
    }
    const names = await fetchUserNames([
      ...peopleInLoads(data || []),
      ...peopleInTransfer(row),
      ...peopleInProduction(runs || []),
      ...peopleInHandovers(collections || []),
      ...peopleInCountIns(counts || []),
    ]);
    if (stale()) return;
    setUsersById(names);
    setNamesLoading(false);
  }, []);

  useEffect(() => { refreshLoads(selected?.id ?? null, selected); }, [selected, refreshLoads]);
  // #671 -- the open transfer only ever sees ITS OWN loads. Until they arrive
  // it sees none, and the load sheet says "…" and holds Send, rather than a
  // confident number read off the transfer opened before it.
  const loadsReady = selected != null && String(loadsFor) === String(selected.id);
  const selectedLoads = loadsReady ? loads : [];

  // `successText` may be a function of the RESULT, so a confirmation can name
  // what actually happened -- "HOU-TRF-00013-2 is on its way to Dallas with 15"
  // rather than a sentence that would read the same whatever was sent.
  //
  // #675 / GATE 675 D22, Session 79.22b.5.5b -- ON THE DESK, A PRESS ON THE RIGHT
  // SIDE IS ANSWERED THERE. The page's answer sits above the tabs, far from the
  // button, and scrolls the page to it -- "right when a sheet has closed over a
  // long phone list, and wrong beside a list" (the gate's own words). So with
  // the transfer open in the right-hand column, the answer goes to the top of
  // that column (answerOnDesk), the page and the list stay where they are, and
  // the card keeps its place (`at` names the truck or pile a press was about).
  // Below the desk nothing changes.
  async function run(fn, successText, { at = null } = {}) {
    const desk = split && !!selected;
    const pin = desk ? deskPinFor(at) : null;
    setBusy(true);
    clearMessage();
    // #665 D21 -- any other action ends a card's answer.
    setCardNote(null);
    // #682 -- and an answered question's kept card, and a bell's S6 box.
    setQuestionNote(null); setQuestionRefusal(null); setBellAlert(null);
    const result = await fn();
    if (desk) return answerOnDesk(result, successText, pin);
    setBusy(false);
    if (result?.error) {
      // #585 -- the database's shorthand becomes a sentence a person would say,
      // and a refusal that means somebody else got there first reloads the
      // screen, so the person sees where the transfer stands now.
      const plain = plainTransferRefusal(result.error.message);
      // A race is not an error -- the answer is already given (gate 567 F1
      // draws it as an ordinary message) -- so it reads as information.
      if (plain.race) showInfo(plain.text); else showError(plain.text);
      if (plain.race) {
        await load();
        await refreshLoads(selected?.id ?? null);
      }
      return false;
    }
    const text = typeof successText === 'function' ? successText(result) : successText;
    // #517 / D67 -- A HALF-SUCCESS IS NOT A SUCCESS, AND SAYING SO IS THE WHOLE
    // POINT. The approval genuinely happened, so this is not an error; but if a
    // per-item date failed to save, "Approved" alone would tell the approver
    // their dates are in when they are not, and the requester would be shown
    // the whole-ask date as though it had been chosen for their line.
    if (result?.datesWarning) {
      showError(datesDidNotSave(text, result.datesWarning));
      await load();
      await refreshLoads(selected?.id ?? null);
      return true;
    }
    showSuccess(text);
    // ** BRING IT INTO VIEW. ** Colby, 79.10: a team member "needs to see a
    // confirmation when they accept the load quantity." The sheet closes on
    // success and the message renders at the TOP of the page -- so somebody
    // scrolled down a long To-load list on a phone presses Send, the sheet
    // vanishes, and the only confirmation is off-screen above them. That reads
    // as nothing having happened, which is the silent-success shape this
    // project keeps meeting from the other direction.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    await load();
    // THE LOADS ARE RE-READ, NOT PATCHED IN MEMORY. Every one of these actions
    // changes them -- a new load, an arrival, a rollup the database trigger
    // wrote -- and a screen showing what it THINKS happened rather than what
    // did is how #504 reported a write that never landed.
    await refreshLoads(selected?.id ?? null);
    return true;
  }

  // #675 / gate 675 D22 -- THE ANSWER TO A PRESS ON THE DESK'S RIGHT SIDE, the
  // same answers run() gives (a refusal in plain words, "somebody else got there
  // first" as information that reloads, a half-success said as one), held on the
  // right with the press's place in the list. As a card press does (cardRun),
  // the screen stays busy until the lists are read again, so the answer, "It's
  // now on…" and Next are drawn once, from the fresh rows, never from the old
  // ones. It does not fade: Next lives inside it, so it stays until the next
  // choice, a change of tab or another press. A reader who chose another card
  // while it read is not handed an answer for the one they left.
  // #682 / #702, Session 79.22b.6c.2 -- AFTER A CORRECTION, THE PAGE AND THE
  // TRANSFER ARE READ AGAIN, and only then does the answer show (gate B3): the
  // correction sheet stays on "Saving…" until this returns. The answer itself
  // lands on the transfer (P3), not above the tabs, because the transfer stays
  // open -- so any older page answer is cleared, as every press clears one.
  async function readAfterCorrection() {
    clearMessage();
    setCardNote(null);
    await load();
    await refreshLoads(selected?.id ?? null);
  }

  async function answerOnDesk(result, successText, pin) {
    deskSeq.current += 1;
    const base = { desk: true, seq: deskSeq.current, tab, transferId: selected.id, ...pin };
    // Cards stay pressable beside the right side, so a reader can choose another
    // while this reads: the history read is then left to that card (Grok's
    // review at 79.22b.5.5 -- only the newest read is applied).
    const stillChosen = () => String(selectedRef.current?.id) === String(selected.id);
    if (result?.error) {
      const plain = plainTransferRefusal(result.error.message);
      if (plain.race) {
        await load();
        if (stillChosen()) await refreshLoads(selected.id);
      }
      if (stillChosen()) setCardNote({ ...base, kind: plain.race ? 'info' : 'error', text: plain.text });
      setBusy(false);
      return false;
    }
    const text = typeof successText === 'function' ? successText(result) : successText;
    await load();
    if (stillChosen()) await refreshLoads(selected.id);
    if (stillChosen()) {
      setCardNote(result?.datesWarning
        ? { ...base, kind: 'error', text: datesDidNotSave(text, result.datesWarning) }
        : { ...base, kind: 'done', text });
    }
    setBusy(false);
    return true;
  }

  // #630, Session 79.22b.1 -- ONE IMPLEMENTATION, TWO ENTRY POINTS.
  //
  // The collect panel inside the sheet and the new action on the To collect
  // card do the same thing to the same rows, so they call the same function
  // rather than each assembling the call and its confirmation. Locked rule 14:
  // what must not be duplicated is the mutation and its side effects, not the
  // shape of the control that starts it. #358 is the record of what happens
  // when one lives in several places and gets fixed in only one.
  //
  // THE LOADS ARE PASSED IN RATHER THAN READ FROM STATE, because `loads` is the
  // sheet's -- fetched for whichever transfer is open -- and the card has no
  // sheet open. Both callers hand in the same set: `fetchLoads()` is
  // `transfer_id = X` and nothing else, and handOverLoad only ever looks at
  // `in_transit` rows, so the page's open-loads list filtered to this transfer
  // is the identical input.
  //
  // #694, gate 665 D21, Session 79.22b.4.6.1b -- THE CARD NOW ANSWERS ON THE
  // CARD, through collectFromCard() below, which makes the same handOverLoad()
  // call; this one answers the sheet's collect panel at the top of the page.
  function collectFrom(t, theLoads, { stop, taking, counts, note, leaving, toUser, toName }) {
    return run(
      () => handOverLoad(t, stop.place ? { place: stop.place } : stop.branchId, {
        loads: theLoads, taking, counts, note, toUser, toName,
        // #631 -- the bell names both people and the place.
        byName: profile?.full_name || null, branchNames,
      }),
      (result) => {
        // A place is free text somebody typed, so it is read back as given --
        // D10. A yard is a branch and gets its proper name.
        const yard = stop.place
          || branchNames[String(stop.branchId)] || 'the handover';
        const where = branchNames[String(t?.to_branch_id)] || 'here';
        if (result?.collectedNothing) {
          return `Nothing went on your truck. Everything is still at ${yard}.`;
        }
        const ref = result?.loadRef || 'your leg';
        // ** THE LEFTOVER IS NAMED OUT LOUD. ** A driver who takes half needs
        // to be told the rest is still somebody's job, and where.
        // #705 -- the card's own sentence, right for one and for many; it read
        // "3 is still at Austin — it stays" here.
        return leaving > 0
          ? `${ref} is on its way to ${where} with ${result?.taken ?? 0}. `
            + collectAfterNote({ leaving, place: yard })
          : `Collected. ${ref} is now on its way to ${where} with ${result?.taken ?? 0}.`;
      },
      // #675 D22 -- the pile this press emptied keeps its place on the desk.
      { at: { stop } });
  }

  function countInFrom(t, theLoad, counts, note) {
    // #682 -- P5's sentence, handed back so the transfer can hold it on a phone.
    let askedLine = null;
    return run(
      () => countInLoad(theLoad, counts, { transfer: t, branchNames, note }),
      // SAYS WHAT WAS ACCEPTED, not just that something was. Colby, 79.10: a
      // team member "needs to see a confirmation when they accept the load
      // quantity" -- and "Booked in." reads identically whether they counted 60
      // or 6. It names the load, the number that landed, and any gap, so the
      // person who typed it can check the app heard them.
      (result) => {
        const counted = countedTotal(theLoad, counts);
        const ref = theLoad.load_ref || 'The load';
        const missing = Number(result?.result?.missing) || 0;
        const done = result?.result?.transfer_complete ? ' That completes this request.' : '';
        // #682 -- P5: a count above the truck booked the truck's number and asked
        // the sender about the rest. Said in the drawn words, and nothing else
        // calls the extra "more than was sent": it is a question now.
        const asked = countAskedSentence({
          booked: counted, asked: result?.result?.asked,
          sender: branchNames[String(theLoad.from_branch_id)] || 'The sending branch',
        });
        if (asked) {
          askedLine = `${asked}${missing > 0 ? ` ${missing} short of what was sent.` : ''}${done}`;
          return askedLine;
        }
        const gap = missing > 0 ? ` ${missing} short of what was sent.` : '';
        return `${ref} booked in — ${counted} added here.${gap}${done}`;
      },
      // #675 D22 -- the truck this count finished keeps its place on the desk.
      { at: { load: theLoad } })
      // #682 -- P5 is drawn INSIDE the open transfer, until the next press (callout
      // J). On a phone the transfer stays open over the page, whose own answer
      // fades after four seconds behind it, so the transfer holds the sentence
      // (TransferDetail's answer, as a correction's is held). On a desk the
      // right side's answer already stays.
      .then(ok => (ok && askedLine ? { ok, asked: askedLine } : ok));
  }

  // The card's own action has no "who is taking it?" picker -- the person
  // pressing it is the person standing at the yard. This is the SAME default
  // the collect panel's picker opens on, read the same way, so the two entry
  // points write the same name on the leg.
  // #699, gate 665 D28 -- presserDriver(): once anybody at the branch drives,
  // a presser who does not is named nobody rather than wrongly (553 B6).
  function defaultTaker() {
    const { driverId, driverName } = driverPayload(presserDriver(drivers, auth?.user?.id), '');
    return { toUser: driverId, toName: driverName };
  }

  // ── #665 D21, Session 79.22b.4.5 -- A PRESS ON A CARD IS ANSWERED ON THE CARD ──
  //
  // run() answers at the top of the page and scrolls there, which is right for
  // the sheet and wrong for a card on a phone: the thumb is on the card, and
  // the answer belongs under it ("Signed off 50 Stake H-Wire · 50 of 100 made").
  // So a card press is answered in `cardNote`, attached to its card by transfer
  // and by where the card sat -- see makeRowsShown.
  //
  // The screen stays busy until the list has been read again, so the card
  // never offers a second press on the numbers from before the first.
  // A refusal is answered on the card too, with the typed numbers kept; one
  // that means somebody else got there first reloads, as run() does (#585).
  //
  // `keepNote` (#694): an Undo is pressed ON the answer, so the answer stays on
  // screen while it runs -- its button shows "Undoing…" where the thumb is, and
  // a card kept in place does not vanish and come back.
  //
  // `extra` (#694, 79.22b.4.6.1b): what else ties an answer to its card. A To
  // collect card is a PILE, not a transfer, so its answer carries the pile's key,
  // the truck the card stood for and where it stood -- on a refusal as well as
  // an answer, and through an Undo.
  async function cardRun(tabId, t, drawnIndex, fn, answer, { keepNote = false, extra = null } = {}) {
    // Grok's review, 79.22b.5.5b -- the place in the tab's whole list, read
    // before the press changes it (placeInTab). An Undo's card is already kept
    // and in no list, so it keeps the place its note recorded.
    const index = placeInTab(tabId, t.id, { key: extra?.key ?? null, loadId: extra?.loadId ?? null }).index ?? drawnIndex;
    setBusy(true);
    clearMessage();
    if (!keepNote) setCardNote(null);
    // #682 -- any other press ends an answered question's kept card, and S6's box.
    setQuestionNote(null); setQuestionRefusal(null); setBellAlert(null);
    const result = await fn();
    if (result?.error) {
      const plain = plainTransferRefusal(result.error.message);
      if (plain.race) {
        await load();
        await refreshLoads(selected?.id ?? null);
      }
      setCardNote({ tab: tabId, transferId: t.id, index, ...(extra || {}), kind: 'error', text: plain.text });
      setBusy(false);
      return false;
    }
    await load();
    await refreshLoads(selected?.id ?? null);
    setCardNote({ tab: tabId, transferId: t.id, index, ...(extra || {}), kind: 'done', ...answer(result) });
    setBusy(false);
    return true;
  }

  // #665 D7 -- the To make card: every item whose box says more than 0, in one
  // press. A box at 0 is left out (Colby's 50-and-0 case), never sent.
  function signOffFromCard(t, cardLines, values, index) {
    const entries = cardLines
      .filter(l => !l.noBox)
      .map(l => ({ line: l.line, qty: Number(values[String(l.item_id)]) || 0 }))
      .filter(e => e.qty > 0);
    return cardRun('to_make', t, index,
      () => signOffProductionLines(t, entries, { branchNames, items }),
      (r) => ({
        text: signOffDoneLine(r.lines, t),
        after: signOffAfterNote(t, r.lines, branchNames, { finished: r.finished }),
        // #694 -- what the answer's Undo takes back: exactly this press's runs.
        undo: signOffUndo(r.lines),
      }));
  }

  // #694, gate 665 D21, Session 79.22b.4.6 -- UNDO, WHERE THE ANSWER IS.
  //
  // One tap takes the press back through the same card run as the press
  // itself: the page reads everything again, and the answer then says so and
  // offers nothing more, because a press that was taken back offers no second
  // Undo (gate B2). A refusal is answered on the card like any other, and one
  // meaning the transfer moved on since reloads it (plainTransferRefusal's
  // races). It sends nothing (B5); the history keeps both rows (B6).
  // Session 79.22b.4.6.1 -- a Send on the To load card too: the truck comes
  // off the road and its stock goes back on the shelf.
  // Session 79.22b.4.6.1b -- and a Collect on the To collect card: the pile is
  // back where it was and the driver's truck comes off the road.
  // Session 79.22b.4.6.2 -- and a Count in on the To count in card: the stock
  // comes back off the receiving branch's shelf, its average cost goes back to
  // exactly what it was, and the truck is waiting to be counted again.
  function undoFromCard(note) {
    const job = note?.undo?.job;
    const takeBack = job === 'sign_off' ? () => undoSignOff(note.undo.signed)
      : job === 'send' ? () => undoSend(note.undo)
        : job === 'collect' ? () => undoCollect(note.undo)
          : job === 'count_in' ? () => undoCountIn(note.undo)
            : null;
    if (!takeBack) return Promise.resolve(false);
    // #694, R1 at 79.22b.4.6.1b -- THE ANSWER SAYS WHAT CAME BACK, AND WHERE. A
    // sign-off's and a send's goods go back on the shelf of the branch that
    // made or sent them; a collection's pile is where its card stood; a
    // count's come back off the shelf of the branch that received them.
    const tr = transfers.find(x => String(x.id) === String(note.transferId)) || null;
    const giver = tr ? branchNames[String(tr.from_branch_id)] : null;
    const receiver = tr ? branchNames[String(tr.to_branch_id)] : null;
    const shelfOf = job === 'count_in' ? receiver : giver;
    const place = note.stop ? (note.stop.place || branchNames[String(note.stop.branchId)] || null) : null;
    return cardRun(note.tab, { id: note.transferId }, note.index,
      takeBack,
      (r) => ({
        text: undoneLine({ undo: note.undo, result: r, at: new Date(), shelf: shelfOf ? `${shelfOf}'s shelf` : null, place }),
        after: job === 'count_in'
          ? countInUndoneAfter({ costRestored: r?.costRestored, reopened: r?.reopened, branch: receiver })
          : null,
      }),
      { keepNote: true, extra: { key: note.key, loadId: note.loadId, stop: note.stop, load: note.load } });
  }

  // #694 / gate 665 D21, Session 79.22b.4.6.2 -- THE TO COUNT IN CARD ANSWERS
  // WHERE THE THUMB IS: "Counted in 10 at Dallas from HOU-TRF-00047-2", what the
  // count left behind under it, and its Undo (the words Colby approved, R1).
  // The same countInLoad() call the sheet's arrival panel makes (locked rule
  // 14), bell and email included; only where the answer lands differs. The
  // truck it counted leaves To count in, so the card keeps its place, drawn
  // from the truck as it stood - see countRowsShown.
  function countInFromCard(t, theLoad, counts, index) {
    const branch = branchNames[String(t.to_branch_id)] || null;
    const ref = theLoad.load_ref || null;
    return cardRun('count_in', t, index,
      () => countInLoad(theLoad, counts, { transfer: t, branchNames, note: null }),
      (r) => ({
        text: countInDoneLine({ total: countedTotal(theLoad, counts), branch, ref }),
        after: countInAfterNote({
          missing: r?.result?.missing, extra: r?.result?.extra, ref,
          complete: !!r?.result?.transfer_complete,
          // #682 -- P5's second sentence when the count asked the sender.
          asked: r?.result?.asked, sender: branchNames[String(theLoad.from_branch_id)] || null,
        }),
        // #694 -- what the answer's Undo takes back: exactly this count.
        undo: countInUndo({ countInId: r?.result?.count_in_id }),
      }),
      { extra: { loadId: theLoad.id, load: theLoad } });
  }

  // #682, Session 79.22b.6c.2b -- HOUSTON ANSWERS ON THE CARD (P6, callout M;
  // 682 D7, D16). One press: answerLoadExtra() proves it landed by the
  // database's own reply, then the page is read again, and only then does the
  // card show what happened, kept in its place until the next press (gate B3;
  // gate 665's pinned card). Somebody else first -- another answer, or the
  // count taken back -- reads the page again and keeps the card with the
  // database's sentence naming who or what. Any other refusal ("Only 10 are
  // signed off as made…") is answered under the buttons, which stay (F3).
  async function answerQuestion(entry, answer) {
    const row = entry?.row;
    const q = row?.question;
    if (!q || busy) return;
    setBusy(true);
    clearMessage();
    setCardNote(null);
    setQuestionNote(null);
    setQuestionRefusal(null);
    setQuestionPressing({ key: entry.key, answer });
    // #682, 79.22b.6c.3 -- the names the counter's bell says (B5).
    const out = await answerLoadExtra({
      question: q, answer, branchNames, itemName: row?.itemName || null,
      transferNumber: row?.transfer?.transfer_number || null,
    });
    const kept = { key: entry.key, head: entry.head, text: entry.text, room: entry.room,
      yesLabel: entry.yesLabel, noLabel: entry.noLabel, settleLabel: entry.settleLabel };
    if (out.result || (out.refusal && out.race)) {
      await load();
      await refreshLoads(selected?.id ?? null);
      setQuestionNote({
        entry: kept, row,
        pinned: out.result
          ? { tone: 'done', text: truckAnswerText({
            answer, loadRef: q.load_ref, counted: q.counted_qty, loaded: q.loaded_qty, extra: q.extra_qty,
            dest: branchNames[String(q.to_branch_id)] || 'The branch that counted it',
          }) }
          : { tone: 'info', text: out.refusal },
      });
    } else {
      setQuestionRefusal({ key: entry.key, text: out.refusal || out.failure });
    }
    setQuestionPressing(null);
    setBusy(false);
  }

  // #694 / gate 665 D21, Session 79.22b.4.6.1b -- THE TO COLLECT CARD ANSWERS
  // WHERE THE THUMB IS: "Collected 5 from Austin as HOU-TRF-00032-2", what stays
  // behind said out loud, and its Undo. The same handOverLoad() call the sheet's
  // panel makes (locked rule 14); only where the answer lands differs.
  function collectFromCard(t, theLoads, { stop, taking, leaving, index, pileKey, loadId }) {
    const yard = stop.place || branchNames[String(stop.branchId)] || 'the handover';
    return cardRun('collect', t, index,
      () => handOverLoad(t, stop.place ? { place: stop.place } : stop.branchId, {
        loads: theLoads, taking, counts: null, note: null, ...defaultTaker(),
        // #631 -- the bell names both people and the place.
        byName: profile?.full_name || null, branchNames,
      }),
      (r) => ({
        text: collectDoneLine({
          total: r?.taken ?? 0, place: yard, ref: r?.loadRef || null, nothing: !!r?.collectedNothing,
        }),
        after: r?.collectedNothing ? null : collectAfterNote({ leaving, place: yard }),
        // #694 -- what the answer's Undo takes back: exactly this collection.
        undo: collectUndo({ handoverId: r?.handoverId }),
      }),
      { extra: { key: pileKey, loadId, stop: { branchId: stop.branchId ?? null, place: stop.place ?? null } } });
  }

  // #665 B3 -- ONE SEND, TWO ENTRY POINTS. The load sheet and the To load card
  // hand a truck to loadTransfer() the same way, as countInFrom() does for a
  // count (locked rule 14), and the departure bell (#648) goes with it.
  function loadFrom(t, { fromBranchId, lines, toBranchId, toPlace, driverId, driverName, truck }) {
    return loadTransfer(t, fromBranchId, lines, {
      toBranchId, toPlace, driverId, driverName, truck,
      // #648 -- the departure bell names the branch it left.
      branchNames,
    });
  }

  // The cost that travels with an item, by the rule the load sheet has always
  // used (D16/D17, #515 D45): costToTravel() on the maker, the sender's
  // average and the item's standard cost.
  const travelCost = (t, itemId) => costToTravel({
    madeAtBranchId: t.made_at_branch_id,
    senderAvgCost: receiverStock[`${itemId}:${t.from_branch_id}`]?.avg_unit_cost,
    standardCost: standardCosts[String(itemId)],
  });

  // #665 D8/D9/D11 -- the To load card: what is ready, to the hub unless the
  // loader tapped Straight to, driven by whoever the ONE driver rule names.
  function sendFromCard(t, { cardLines, toBranchId, place, pick }, values, index) {
    const lines = cardLines
      .filter(l => !l.noBox)
      .map(l => ({
        item_id: l.item_id,
        qty: Number(values[String(l.item_id)]) || 0,
        unit_cost: travelCost(t, l.item_id),
        // The card acts only on what is made and ready, so the line was filled
        // the way the request was approved -- the sheet's own default (D64).
        sourced: t.fulfilment_mode,
      }))
      .filter(l => l.qty > 0);
    const total = lines.reduce((n, l) => n + l.qty, 0);
    const driver = loaderNamesNobody(loaderModel)
      ? { driverId: null, driverName: null }
      : driverPayload(pick, '');
    return cardRun('to_load', t, index,
      () => loadFrom(t, { fromBranchId: myBranch, lines, toBranchId, toPlace: null, ...driver, truck: null }),
      (r) => ({
        text: sendDoneLine({ total, place, ref: r?.loadRef || null }),
        // #694 -- what the answer's Undo takes back: exactly this truck.
        undo: sendUndo({ transferId: t.id, loadRef: r?.loadRef }),
      }));
  }

  // #697, gate 665 D23, Session 79.22b.5.2 -- A PICKUP AT A HUB, from the To
  // collect card. The SAME loadTransfer() the Send makes (locked rule 14), from
  // the hub's shelf straight to the branch it is for, driven by the person
  // pressing it - the collect card's own taker rule (presserDriver). The
  // database admits them because they are at the branch the goods are for
  // (assert_may_load_at). Its Undo is the Send's, said as a collection.
  function collectHubGoodsFromCard(t, cardLines, values, index) {
    const lines = cardLines
      .filter(l => !l.noBox)
      .map(l => ({
        item_id: l.item_id,
        qty: Number(values[String(l.item_id)]) || 0,
        unit_cost: travelCost(t, l.item_id),
        sourced: t.fulfilment_mode,
      }))
      .filter(l => l.qty > 0);
    const total = lines.reduce((n, l) => n + l.qty, 0);
    const standing = cardLines.reduce((n, l) => n + (l.noBox ? 0 : (Number(l.max) || 0)), 0);
    const hubName = branchNames[String(t.from_branch_id)] || 'the hub';
    const { driverId, driverName } = driverPayload(presserDriver(drivers, auth?.user?.id), '');
    return cardRun('collect', t, index,
      () => loadFrom(t, {
        fromBranchId: t.from_branch_id, lines, toBranchId: null, toPlace: null, driverId, driverName, truck: null,
      }),
      (r) => ({
        text: collectDoneLine({ total, place: hubName, ref: r?.loadRef || null }),
        after: collectAfterNote({ leaving: Math.max(standing - total, 0), place: hubName }),
        undo: sendUndo({ transferId: t.id, loadRef: r?.loadRef, collected: true }),
      }),
      { extra: { key: `hub:${t.id}` } });
  }

  // ── #675 / GATE 675 D22, Session 79.22b.5.5b -- THE ANSWER ON THE RIGHT ──────
  //
  // The action's own answer, then -- when the press has moved the transfer off
  // this tab -- where it went, "It's now on To make at Houston.", and Next on
  // this tab, which opens the next card still waiting in the list's own order
  // and filters. Whether it has left is asked of the tab's whole lists, before
  // any search or filter: where the transfer stands, not what the reader is
  // narrowing to. Not after a refusal, which moved nothing.
  const deskNote = split && cardNote?.desk && selected && String(cardNote.transferId) === String(selected.id)
    ? cardNote : null;
  const deskMoved = !!deskNote && deskNote.kind !== 'error' && !tabHoldsTransfer(deskNote.tab, selected, {
    confirm: awaitingConfirm, toMake: makeRows, toLoad, awaiting: awaitingCollection,
    elsewhere, owed, collect, hubPickups, arriving, waiting: waitingNow.rows,
  }, { finishedStatuses: FINISHED_STATUSES, awaitsDecision: refusalAwaitsDecision });
  const deskNowOn = deskMoved && loadsReady
    ? nowOnSentence(whereItNowIs(selected, {
      loads: selectedLoads, finishedStatuses: FINISHED_STATUSES, awaitsDecision: refusalAwaitsDecision,
    }), { branchNames, myBranch })
    : null;
  const deskNext = deskMoved ? nextCardAfter(cardsOnTab(deskNote.tab, {
    confirm: sVisible, toMake: sMakeRowsShown, toLoad: sLoadRowsShown, awaiting: sAwaitingShown,
    owed: sOwedShown, elsewhere: sElsewhere, hubPickups: sHubRowsShown, collect: sCollectRowsShown,
    arriving: sCountRowsShown, waiting: waitingKept,
  }, (id, row) => transfers.find(x => String(x.id) === String(id)) || row?.__transfer || row?.branch_transfers || null),
  deskNote.transferId) : null;
  // One click: the next card is chosen, tinted, and opens on the right from its
  // top; the answer and the kept card end with that choice.
  function openNext() {
    if (deskNext?.transfer) { setSelected(deskNext.transfer); return; }
    if (deskNext?.transferId == null) return;
    openOneTransfer(deskNext.transferId).then(row => {
      if (row) setSelected(row);
      else showError('That transfer could not be opened. It may have been removed, or the connection dropped.');
    });
  }
  const deskAnswerEl = deskNote ? (
    <DeskAnswer
      note={deskNote}
      nowOn={deskNowOn}
      nextLabel={deskNext ? nextButtonLabel(deskNote.tab, deskNext.transfer, branchNames) : null}
      onNext={openNext}
      onDismiss={() => setCardNote(null)}
      coarse={coarse}
    />
  ) : null;

  // #675 / gate 675 D19 and F5, Session 79.22b.5.5 -- ONE SHEET, TWO FRAMES. The
  // transfer is drawn once, here, and shown in the right-hand column on a desk
  // and as today's sheet below it (TransferDetail's `column`), rather than as a
  // second copy. Keyed on the transfer, because on the desk another card can be
  // chosen while one is open: without the key the next transfer would open
  // holding the last one's typed reason and boxes.
  const detailEl = selected ? (
    <TransferDetail
      key={selected.id}
      column={split}
      usersById={usersById}
      production={production}
      handovers={handovers}
      countIns={countIns}
      corrections={corrections}
      extras={loadExtras}
      // #682 -- S6's box, when Houston's answer bell opened this transfer.
      bellAlert={bellAlert && String(bellAlert.transferId) === String(selected.id) ? bellAlert.alert : null}
      onCorrected={readAfterCorrection}
      // Pressing Correct is a press, so a card's answer and its Undo end
      // there (#665 D21; the gate's X2: "any press clears the card's answer,
      // so the Undo has already gone").
      onCorrectPress={() => setCardNote(null)}
      namesLoading={namesLoading}
      isWorkbench={isWorkbench}
      transfer={selected}
      // #553 D19, second half. TransferDetail was NEVER TOLD WHICH TAB
      // opened it, so one component served all seven stages and could not
      // vary by stage even in principle -- which is why the same buttons
      // appeared everywhere and is half of what #553 was logged for.
      // #730 -- a transfer opened through "See" derives its own stage.
      stage={seeOpenedId != null && String(selected.id) === seeOpenedId ? null : tab}
      // #602 / D31. The signpost switches the tab UNDERNEATH and leaves the
      // sheet open, so reaching an action that lives on another tab costs one
      // tap rather than close-retab-reopen. The tab state lives here already,
      // so this is a callback rather than new machinery.
      onGoToStage={setTab}
      // #621 -- only a transfer this page holds could be opened here, and
      // that was every transfer while the page read them all. #608 / gate
      // 675 D18: the page holds the work in hand now, so a "See
      // HOU-TRF-00031" whose transfer has finished is read on its own and
      // opened - whatever its age. Every transfer is linkable.
      onOpenTransfer={(id) => {
        setSeeOpenedId(String(id));
        const held = x => String(x.id) === String(id);
        const found = transfers.find(held) || finished.rows.find(held) || extraTransfers.find(held);
        if (found) { setSelected(found); return true; }
        openOneTransfer(id).then(row => {
          if (row) setSelected(row);
          else showError('That transfer could not be opened. It may have been removed, or the connection dropped.');
        });
        return true;
      }}
      canOpenTransfer={(id) => id != null}
      // A purchase request lives in the Requests inbox, where the manager
      // deciding a refusal is the approver it waits on.
      onOpenRequest={setActive && setOpenRequestId
        ? (id) => { setOpenRequestId(id); setActive('requests_inbox'); }
        : null}
      // #622 -- the sheet's own Sign off a run. The document closes and the
      // sign-off sheet opens, exactly as the To make card does it, so two
      // overlays never stack and a save lands on the list that just changed.
      // #675 / gate 675 D22, Session 79.22b.5.5b -- ON THE DESK THE TRANSFER
      // STAYS. It is a column there, not an overlay, so nothing stacks; the
      // sign-off answers in its own sheet as before, and once it is saved the
      // right side says where the transfer went if it has left To make.
      onSignOff={(t) => {
        if (split) setSignOffPin({ tab, transferId: t.id, ...deskPinFor() });
        else setSelected(null);
        setSigningOff(t);
      }}
      // D19 again: WHICH BRANCH IS BEING READ, so the detail can say
      // "loading is done by the branch holding the material" instead of
      // the generic not-signed-in-there sentence. It is NOT used to decide
      // what anybody may do -- that stays on `myBranch`.
      lookingElsewhere={lookingElsewhere}
      items={items}
      receiverStock={receiverStock}
      promisedByKey={promisedByKey}
      // #721's reader of #608's cut-off -- promises counted from a cut list
      // are incomplete, so "Try another branch" says it could not check.
      promisesUnknown={inHandCutAt != null}
      standardCosts={standardCosts}
      branchNames={branchNames}
      profile={profile}
      myBranch={myBranch}
      busy={busy}
      isMobile={isMobile}
      onClose={() => setSelected(null)}
      onApprove={(confirmedDate, promisedDates) => run(
        // #583 / 553 D26 -- who pressed it, so an own-shelf request tells
        // its asker and never the person confirming.
        () => approveTransfer(selected, { branchNames, confirmedDate, promisedDates, approvedBy: auth?.profile?.id || null }),
        'Approved. The sending branch has been told.')}
      onDeny={(reason) => run(
        () => denyTransfer(selected, reason, { branchNames }),
        // #565 D10, Session 79.15i — THIS SENTENCE USED TO NAME A PURCHASE
        // REQUEST AND NO LONGER CAN. D18 raised one the instant Deny was
        // pressed; under D5/D6 the ASKING branch's manager now chooses the
        // route, so at this moment no successor exists to name.
        //
        // Leaving the old wording would promise a document that was never
        // created — which is exactly the shape #559 was logged for, a person
        // sent looking for a record nobody raised. It differs by STAGE for
        // the reason it always did: refusing your own team member's ask
        // routes nothing, because they have decided the material is not
        // needed.
        (result) => (result?.stage === 'awaiting_confirmation'
          ? 'Refused. The branch that asked has been told, and their manager will decide what happens next.'
          : 'Denied, and the person who asked has been told why.'))}
      // #567 — ANSWERING EACH LINE. The success line reports what actually
      // happened rather than a generic confirmation, because after a
      // per-line answer the outcome is genuinely ambiguous.
      onAnswerLines={(answers) => run(
        // #646 -- the item names, so each quoted reason says which item it is for.
        () => answerTransferLines(selected, answers, { branchNames, items }),
        // #698 half (a) -- one sentence, shared with the Requests inbox card.
        (result) => answeredLinesSentence(result, branchNames[String(selected?.to_branch_id)] || null))}
      // #567 / #565 D8 — what happens to the refused lines.
      onRouteRefused={({ route, qty, itemIds, toBranchId }) => run(
        () => routeDeniedTransfer(selected, { route, qty, itemIds, branchNames,
          toBranchId: route === 'make' ? (selected?.made_at_branch_id ?? null)
            : route === 'branch' ? (toBranchId ?? null) : null }),
        // #578 -- EACH ROUTE SAYS WHAT HAPPENED. "Nothing more is needed"
        // closes a wholly refused transfer; on a partly refused one it is
        // RECORDED and the agreed items keep coming, and the screen used to
        // say "Closed with nothing ordered" for both while the second wrote
        // nothing at all.
        (result) => {
          if (route === 'none') {
            return result?.closed
              ? 'Closed with nothing ordered.'
              : 'Recorded: nothing more is needed for the refused items. What was agreed is still coming.';
          }
          // #620 -- NAME WHO WAS TOLD, which is not always where it went:
          // a bought request waits on this branch's approval first.
          return routedSentence(route, result, {
            askingBranch: branchNames[String(selected?.to_branch_id)] || null,
            destination: route === 'branch' ? (branchNames[String(toBranchId)] || null)
              : (branchNames[String(selected?.made_at_branch_id)] || null),
          });
        })}
      // D9/D10 — the default quantity is the SUPPLYING VENDOR'S pack
      // quantity, which Colby placed: "it would live in the vendor catalog
      // page for houston under associated items and item uom pack quantity
      // details like all other vendor info". Houston is an internal vendor,
      // so `vendor_items.pack_qty` already holds it and no schema was added.
      //
      // Returns null when nothing is known, and the screen then says only
      // what was asked for — never "pallets of 0". That is the LIVE case:
      // three of five made items have no internal-vendor row yet.
      //
      // #578, Session 79.21b -- every supplier's pack for the line, and the
      // panel picks the one its chosen route uses (routePack()).
      packsFor={(line) => packQtyByItem[String(line.item_id)] || []}
      loads={selectedLoads}
      loadsReady={loadsReady}
      auth={auth}
      hubs={branches.filter(b => b.is_transfer_hub)}
      drivers={drivers}
      // #690 -- the load sheet names the driver by the same rule as the card.
      loaderModel={loaderModel}
      // #517 -- SHIPPING IS NOW LOADING, AND IT CAN HAPPEN MORE THAN ONCE.
      // ship_branch_transfer() assigned qty_shipped rather than adding to
      // it and finished by setting the transfer in_transit, so a second
      // truck had nothing to run against and would have overwritten the
      // first. load_branch_transfer() runs as many times as there are
      // trucks and takes a FROM branch, because the second load leaves
      // Austin rather than Houston.
      onLoad={({ fromBranchId, lines, toBranchId, toPlace, driverId, driverName, truck }) => run(
        // #665 B3 -- the same send the To load card makes. See loadFrom().
        () => loadFrom(selected, { fromBranchId, lines, toBranchId, toPlace, driverId, driverName, truck }),
        // NAMES WHAT WENT, so the person who just typed a number can check
        // the app heard the number they typed. A generic "Sent." reads the
        // same whether it took 15 or 150.
        (result) => {
          const total = lines.reduce((n, l) => n + (Number(l.qty) || 0), 0);
          // D10 — a road spot is not a branch, so it is named from what was
          // typed. Without this the confirmation says "its destination" for
          // a load that is being handed over on I-45, which is the one
          // sentence telling somebody where their material actually went.
          const where = toPlace
            ? toPlace
            : (branchNames[String(toBranchId || selected.to_branch_id)] || 'its destination');
          const ref = result?.loadRef ? `${result.loadRef} is` : 'The load is';
          return `${ref} on its way to ${where} with ${total}.`;
        })}
      // ** THE TRUCK IS HERE, AND NOBODY HAS COUNTED IT — #553 D17. **
      // One tap, no sheet. It tells nobody, because the arrival message
      // names what arrived and what is still owed and neither number
      // exists yet. Pressing it twice is not an error and does not say so.
      onMarkArrived={({ load: theLoad }) => run(
        () => markArrived(theLoad),
        (result) => {
          const ref = theLoad.load_ref || 'The load';
          return result?.alreadyArrived
            ? `${ref} was already marked as here. Nothing changed.`
            : `${ref} is marked as here. Count it in when somebody unloads it.`;
        })}
      // #630 -- the sheet and the To count in card book a load in the same
      // way, so both call countInFrom(). The confirmation moved with it.
      onArrive={({ load: theLoad, counts, note }) => countInFrom(selected, theLoad, counts, note)}
      // #529 -- ONE PRESS CLOSES THEIR LEG AND STARTS OURS. Neither half
      // moves stock at a handover, which is why one person standing at a
      // yard may record both.
      //
      // #553 D11, Session 79.15f: it is now ONE atomic database call rather
      // than two from the browser. `toUser` / `toName` name who took
      // custody when this is a swap rather than a plain drop, and a `place`
      // may stand in for the yard entirely -- D10, a handover half way
      // between two branches.
      // #630 -- the sheet and the To collect card hand a load over the same
      // way, so both call collectFrom(). The sheet passes its OWN loads,
      // which is the set it has always passed.
      onCollect={(payload) => collectFrom(selected, selectedLoads, payload)}
      onCloseUnfilled={(reason) => run(
        () => closeTransferUnfilled(selected, reason, { branchNames }),
        'Recorded. The person who asked has been told nothing more is coming.')}
      onCloseShort={(reason) => run(
        () => closeTransferShort(selected, reason, { branchNames }),
        'Closed short. Both branches’ managers have been told.')}
      // 371 F2 Done: "Withdrawn. {branch} has been told." -- and the asker
      // too, when somebody else pressed it (W8).
      onWithdrawn={(result, offer) => {
        const told = offer.alsoToldName ? `${offer.who} and ${offer.alsoToldName}` : offer.who;
        const verb = /( and |approvers$|managers$|executives$)/.test(told) ? 'have' : 'has';
        return run(
          async () => result,
          result?.ending === 'closed_unfilled'
            ? `Recorded: nothing more is needed. ${told} ${verb} been told, and what is on the road still arrives.`
            : `Withdrawn. ${told} ${verb} been told.`);
      }}
    />
  ) : null;

  // #675 D1, D12, D13 -- THE SEARCH BOX AND WHAT NARROWS THE LIST. On a phone
  // and a tablet: Filter & sort beside the box, the applied chips under it
  // (D12, D14). On a desk it heads the list column (D19), and the sort and each
  // filter are buttons in plain sight under it (D13): a button showing a value
  // is the applied filter and carries its own x, so the chips row would say
  // the same thing twice and is not drawn there.
  // #757 -- the stage on screen, whose colour runs along the sheet's top edge.
  const stageNow = TRANSFER_TABS.find(x => x.id === tab) || TRANSFER_TABS[0];

  const searchBox = (
    <TransferSearchBox
      value={searchText}
      onChange={setSearchText}
      onEnter={searchNow}
      onClear={() => setSearchText('')}
      placeholder={onDesk ? SEARCH_PLACEHOLDER_DESK : SEARCH_PLACEHOLDER_PHONE}
      coarse={coarse}
      status={search.status}
      onRetry={() => { if (search.term) runSearch(search.term); }}
    />
  );
  const searchBlock = split ? (
    <div style={{ marginBottom: 10 }}>
      {searchBox}
      <DeskFilterBar
        tab={tab}
        coarse={coarse}
        offered={filtersOffered(tab, { singleBranch })}
        applied={appliedHere}
        filters={filters}
        setFilter={setFilter}
        sortId={sortFor(tab, sorts)}
        onSort={id => chooseSort(tab, id)}
        branches={branches}
        branchNames={branchNames}
        lookingAt={singleBranch ? lookingAt : null}
      />
    </div>
  ) : (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>{searchBox}</div>
        <FilterSortButton count={chipsHere.length} coarse={coarse} onClick={() => setSheetOpen(true)} />
      </div>
      <AppliedChips
        chips={chipsHere}
        spectator={!onDesk && lookingElsewhere
          ? `Looking at ${branchNames[String(lookingAt)] || 'another branch'} · you act at ${branchNames[String(myBranch)] || 'your own branch'}`
          : null}
        coarse={coarse}
        onRemove={key => setFilter(key, null)}
        onClearAll={clearFilters}
        onStopLooking={() => setBranchView(myBranch)}
      />
    </div>
  );

  return (
    // #675 D14 -- on a phone and a tablet the search box is the first thing under
    // the top bar, and the page's own 16px top padding sat on the 14px the app
    // already gives: measured at 375 x 667, the first card started at 227px
    // against the gate's 222 budget. 6px puts it at about 217.
    // #675 D19, Session 79.22b.5.5 -- on a desk the page is the window's height
    // (deskHeight, measured above): the header keeps its size and the split
    // below it takes the rest.
    // #757, Session 79.22b.5.5c -- on a phone the sides are 8px, not 16: the
    // tab's sheet now has its own edge and padding (8px a side), and this keeps
    // the cards inside it at today's width, 315px at 375.
    <div ref={pageRef} style={{
      padding: onDesk ? '1rem' : (onPhone ? '6px 8px 1rem' : '6px 1rem 1rem'), maxWidth: 1180, margin: '0 auto',
      ...(split && deskHeight ? { height: deskHeight, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' } : null),
    }}>
      <div style={split ? { flexShrink: 0 } : undefined}>
      {/* #675 / gate 675 D14, Session 79.22b.5.4b -- ON A PHONE AND A TABLET THE
          SEARCH BOX GOES FIRST, where the title and its sub-line were, and the
          branch control moves into Filter & sort (the Purchasing Queue's phone
          shape, callout H). The branch in view is still named in the tab's
          one-line description, and looking at another branch shows as an amber
          chip. The desk keeps its title and branch chip. */}
      {onDesk && (
        <>
          <h1 style={{ fontFamily: 'var(--font-head)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 22, margin: '0 0 4px' }}>
            Transfers
          </h1>
          <p style={{ color: '#5C5C5C', fontSize: 13, margin: '0 0 10px' }}>
            Material moving between branches — off a shelf, or made to order.
          </p>
        </>
      )}

      {/* #553 D19 / #533 — ONE BRANCH CONTROL ON THIS SCREEN.
          Until now the page had no way to say whose work it was showing: an
          admin got the whole company, everybody else got their own branch, and
          nothing on screen said which. The chip is now the answer to "whose
          transfers am I looking at", and it is the ONLY thing the picker
          changes — every button on this page still reads `myBranch`.
          #675 D14 -- on the desk only; a phone or tablet finds it in Filter & sort. */}
      {onDesk && canLookElsewhere && (
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowBranchPicker(v => !v)}
              aria-expanded={showBranchPicker}
              // #581 -- 28px where gate 553's F4 asks for 40 with a finger.
              // The chip keeps its size on a mouse (32), which is that gate's
              // fine-pointer floor, so nothing moves on a desk.
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#F0F0F0', border: 'none', borderRadius: 12, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: '#5C5C5C', cursor: 'pointer', minHeight: controlHeight(coarse, 'min') }}
            >
              {seesEveryBranch ? 'All branches' : (branchNames[String(lookingAt)] || 'My branch')}
              <span aria-hidden="true" style={{ fontSize: 9 }}>▼</span>
            </button>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>· tap to switch branch</span>
            {/* THE SPECTATOR SENTENCE. Looking somewhere else is READING, and
                saying so up front is what stops somebody hunting for a button
                that was never going to be there. */}
            {lookingElsewhere && (
              <span style={{ fontSize: 11, color: '#8A5A10', fontWeight: 600 }}>
                · looking only — you can act at {branchNames[String(myBranch)] || 'your own branch'}
              </span>
            )}
          </div>

          {showBranchPicker && (
            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 100, background: 'white', border: '1px solid #E0E0E0', borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', overflow: 'hidden', minWidth: 190 }}>
              {/* ALL BRANCHES IS ADMIN AND EXECUTIVE ONLY — D19's approved line.
                  A branch manager looks at one other branch at a time.
                  On a narrow window it is offered and REFUSED WITH A REASON
                  rather than hidden: #520 is the precedent, where Colby went
                  looking for this on Stock Home, could not find it, and
                  reasonably concluded it had been deleted. */}
              {seesEveryBranchByRole && (
                isWorkbench ? (
                  <button onClick={() => { setBranchView('all'); setShowBranchPicker(false); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14, fontWeight: seesEveryBranch ? 700 : 400, color: seesEveryBranch ? '#1D9E75' : '#0F0F0F', background: seesEveryBranch ? '#F0FFF4' : 'white', border: 'none', borderBottom: '1px solid #E0E0E0', cursor: 'pointer' }}>
                    All branches
                  </button>
                ) : (
                  <div aria-disabled="true"
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #E0E0E0', background: '#FAFAFA' }}>
                    <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>All branches</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 3, lineHeight: 1.35 }}>
                      Needs a wider window — widen this one and it comes back.
                    </div>
                  </div>
                )
              )}
              {branches.map(b => {
                const on = !seesEveryBranch && String(lookingAt) === String(b.id);
                return (
                  <button key={b.id} onClick={() => { setBranchView(b.id); setShowBranchPicker(false); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14, fontWeight: on ? 700 : 400, color: on ? '#1D9E75' : '#0F0F0F', background: on ? '#F0FFF4' : 'white', border: 'none', borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}>
                    {b.name}
                    {String(b.id) === String(myBranch) && (
                      <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 400 }}> · where you work</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* #757 -- THE LINE UNDER THE PAGE HEADER (Colby's request; grey, R4). On a
          desk the header is the title block. Below the desk the search box took
          the title's place (D14), so the line goes under the search box. */}
      {onDesk && <HeaderLine margin="0 0 14px" />}

      {/* #675 / gate 675 D1 -- THE SEARCH BOX, ABOVE THE TABS, never inside one,
          because it searches all of them (callout A). On a phone it is the
          first thing under the top bar (D14). On a desk it heads the left-hand
          list column instead (D19, Session 79.22b.5.5), with the filter
          buttons under it (D13); see searchBlock. */}
      {!split && searchBlock}
      {!onDesk && <HeaderLine margin={PHONE_LINE_MARGIN} />}
      {sheetOpen && (
        <FilterSortSheet
          phone={!onDesk}
          coarse={coarse}
          count={sheetCount}
          tab={tab}
          offered={filtersOffered(tab, { singleBranch })}
          filters={filters}
          setFilter={setFilter}
          sortId={sortFor(tab, sorts)}
          onSort={id => chooseSort(tab, id)}
          branchSection={!onDesk && canLookElsewhere ? (
            <ChoiceGroup title="Branch" coarse={coarse}
              onPick={id => setBranchView(id)}
              choices={[
                // ALL BRANCHES IS ADMIN AND EXECUTIVE ONLY (D19), and on a narrow
                // window it is offered and refused with its reason (#520).
                ...(seesEveryBranchByRole ? [isWorkbench
                  ? { id: 'all', label: 'All branches', on: seesEveryBranch }
                  : { id: 'all', label: 'All branches', disabled: true, note: 'Needs a wider window — widen this one and it comes back.' }] : []),
                ...branches.map(b => ({
                  id: b.id,
                  label: String(b.id) === String(myBranch) ? `${b.name} · where you work` : b.name,
                  on: !seesEveryBranch && String(lookingAt) === String(b.id),
                })),
              ]} />
          ) : null}
          branches={branches}
          branchNames={branchNames}
          lookingAt={singleBranch ? lookingAt : null}
          onClose={() => setSheetOpen(false)}
        />
      )}

      <InlineMessage type={type} message={message} onDismiss={clearMessage} />

      {/* #608 / gate 675 D17 -- THE ONE PLACE A CUT COULD STILL HAPPEN NAMES
          ITSELF. The page asks for a count beside the work in hand and the
          trucks; more counted than arrived means the database's row limit cut
          the list, and the page says so instead of passing part for the whole. */}
      {(inHandCutAt != null || trucksCutAt != null) && (
        <div role="status" style={{
          margin: '0 0 12px', padding: '9px 12px', background: '#FFF8EC', border: '1px solid #F3D9A8',
          borderRadius: 8, fontSize: 13, color: '#8A5A08', lineHeight: 1.45,
        }}>
          {inHandCutAt != null
            ? `Showing the first ${inHandCutAt.toLocaleString('en-US')} open transfers. Search or filter to find the rest.`
            : `Showing the first ${trucksCutAt.toLocaleString('en-US')} trucks on the road. Search or filter to find the rest.`}
        </div>
      )}

      {/* #757, Session 79.22b.5.5c -- THE STAGES AS BINDER DIVIDERS (Colby's
          request; R2-R4 at 79.22b.5.5b, R1 here). A phone: M4's one row, which
          opens the list of all seven. A tablet and a desk: paper tabs, the chosen
          one joined to its sheet below.
          #700, Session 79.22b.4.4 -- `width: 0; minWidth: '100%'` FILLS THE
          COLUMN WITHOUT EVER ASKING IT TO GROW (now on PaperTabStrip's wrapper).
          Without it the page column grew to fit all seven tabs (~940px), so at a
          tablet or narrow laptop width the page was wider than the window, the
          overflow was hidden, and To count in and Finished could not be reached
          at all. With it the strip scrolls inside the page, which gate 665 F4
          asks for at tablet width. */}
      {onPhone ? (
        <PhoneStageRow tabs={TRANSFER_TABS} tab={tab} onChoose={chooseTab} counts={tabCounts} searching={searchOn} />
      ) : (
        <PaperTabStrip color={stageNow.color} coarse={coarse} chosenId={tab}>
          {/* SEVEN STAGES, LEFT TO RIGHT IN THE ORDER A REQUEST TRAVELS.
              See TRANSFER_TABS. Every tab is shown even when empty, deliberately:
              a tab that appears only when it has contents is a tab nobody can
              find when it is empty, and that is how Houston's production queue
              went missing at 79.6. The count pill is what says whether there is
              anything to do. */}
          {/* #675 D1 -- while a search is on, every pill is that tab's MATCH
              count, Finished included; see tabCounts. */}
          {TRANSFER_TABS.map(t => (
            <Tab key={t.id} id={t.id} tab={tab} setTab={chooseTab} color={t.color} ink={t.ink} tint={t.tint}
              count={tabCounts[t.id] || 0} searching={searchOn}>
              {t.label}
            </Tab>
          ))}
        </PaperTabStrip>
      )}

      {/* #704, gate 665 F4 -- ON A DESK THE TAB'S CONTENTS STOP AT 620px.
          F4's desk rule ("cards in one column, at most 620 px") was approved and
          never built, so a card's button stretched about 1,120px across the
          window. Everything under the tab's one-line description sits in this
          column -- the empty sentence, To make's no-cost warning and its Record
          something else button, the cards -- so a button above the list lines
          up with the cards it belongs to (Colby's R1 at 79.22b.4.6.1c). The
          title, branch picker, message, tabs and description keep the page's
          width, and the space to the right is #675's side-by-side layout
          (79.22b.5). Phone and tablet: unchanged, full width.
          SUPERSEDED IN PART at Session 79.22b.5.5 (gate 675 D19): the space to
          the right is now the chosen transfer, so on a desk this column is the
          400px list column of DeskSplit, and the 620px cap went with it. Every
          reason above still holds: everything under the description lines up
          in the one column. */}
      </div>

      {/* #757 -- THE TAB'S SHEET: its sentence and everything under it, on a
          desk the list and the chosen transfer too. On a desk it takes the
          page's remaining height, as the split did. */}
      <TabSheet phone={onPhone} fill={!!split}>
      {/* WHAT THIS TAB IS, IN A SENTENCE, ALWAYS ON SCREEN.
          Colby's standard: a new hire opens any page of Stede and understands
          what everything means. A label alone cannot carry that -- "To collect"
          is only obvious once you already know there are yards between
          branches. One line under the row costs nothing and removes the guess. */}
      {(() => {
        const t = TRANSFER_TABS.find(x => x.id === tab);
        if (!t) return null;
        return (
          // #757 -- inside the sheet now: the sheet's own padding is above it.
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
            margin: onPhone ? PHONE_BLURB_MARGIN : '0 0 12px', fontSize: 13, color: '#5C5C5C',
          }}>
            <span style={{ width: 3, alignSelf: 'stretch', minHeight: 18, background: t.color, borderRadius: 2, flexShrink: 0 }} />
            {/* #531, widened to all seven tabs at #652 -- THE BLURB CHANGES
                WITH WHOSE WORK IS ON SCREEN, because the stock sentence becomes
                false the moment the reader is not standing in the one building
                it was written for. "Other branches’ trucks" describes an empty
                list for somebody who sees every branch: every truck in motion is
                already in To collect or To count in, so what is left here is the
                stalled transfers and nothing else. A tab promising trucks it
                cannot hold is the same class of untruth as the "nothing here
                needs you" line this replaced -- and so is "your own rack" over
                Houston's. See tabBlurb(); it returns null when the tab's own
                sentence is the right one. */}
            {/* #681/#652, gate 665 D17 -- one sentence per tab, naming the
                branch in view (yours, or the one you are looking at). */}
            {tabBlurb(t, {
              seesEveryBranch,
              branchName: branchNames[String(lookingAt)] || null,
            })}
          </div>
        );
      })()}
      <DeskSplit
        onDesk={split}
        head={searchBlock}
        detail={detailEl}
        chosenId={chosenId}
        // #675 D22 -- the answer to a press on the right, at the top of the right.
        answer={deskAnswerEl}
        answerKey={deskNote?.seq ?? null}
      >
      {loading &&<div style={{ color: '#5C5C5C', fontSize: 13 }}>Loading…</div>}

      {/* #752 -- drawing A: refusals waiting for the asking branch's decision,
          pinned above Finished's pages and never paged. Not counted in the
          Finished pill: every other count is unchanged, as drawn. */}
      {/* #675 D22 -- with a refusal decided on the right kept in its place. */}
      {tab === 'finished' && !loading && (waitingKept.length > 0 || waitingNow.state === 'failed') && (
        <WaitingForDecision
          chosenId={chosenId}
          rows={waitingKept}
          cut={waitingNow.cut}
          failed={waitingNow.state === 'failed'}
          branchName={seesEveryBranch ? null : (branchNames[String(lookingAt)] || null)}
          branchNames={branchNames}
          items={items}
          coarse={coarse}
          onOpen={t => setSelected(t)}
        />
      )}

      {/* #682 -- HOUSTON'S QUESTIONS, first on Awaiting confirmation (P6, callout
          L), above the asks: the card asks for a yes, which is the tab's job. */}
      {tab === 'confirm' && !loading && questionCards.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
          {questionCards.map(e => (
            <div key={`q:${e.key}`} data-question-transfer={e.row?.question?.transfer_id ?? ''}>
              <TruckExtraCard entry={e} busy={busy} onAnswer={a => answerQuestion(e, a)} />
            </div>
          ))}
        </div>
      )}

      {/* EVERY EMPTY STATE SAYS WHAT WOULD APPEAR HERE, not just that nothing
          has. An empty tab reading "Nothing here yet" teaches a new hire
          nothing about what the tab is for, and seven of them would teach
          nothing seven times over. */}
      {!loading && (
        // #665 D21 -- a card kept in place after its press is still on screen,
        // so the tab is not empty while it shows.
        // #675 -- the s-lists are each list narrowed by any search on.
        tab === 'to_make'  ? sMakeRowsShown.length === 0
        // #694, 79.22b.4.6.2 -- a counted card kept in place is not an empty tab.
        : tab === 'count_in' ? sCountRowsShown.length === 0
        // #694 -- a card kept in place with its answer is not an empty tab.
        // #697 -- nor is one holding a pickup at a hub.
        : tab === 'collect'  ? sCollectRowsShown.length + sHubRowsShown.length === 0
        // #675 D22 -- nor is a card a press on the right kept in place.
        : tab === 'moving'   ? sElsewhere.length + sOwedShown.length === 0
        // #697 -- the hub's "collects this here" cards are on screen too.
        : tab === 'to_load'  ? sLoadRowsShown.length + sAwaitingShown.length === 0
        // #608 / 675 D16 -- Finished's page is its own read: empty only once it
        // has answered, never while it is on its way or after it failed.
        : tab === 'finished' ? finishedNow.state === 'ok' && finishedNow.rows.length === 0
        // #682 -- a truck question, or one answered and kept, is not an empty tab.
        : tab === 'confirm'  ? sVisible.length + questionCards.length === 0
        : sVisible.length === 0) && ((searchOn && !filtersHideAll) ? (
          // #675 D6 -- TWO SENTENCES, BECAUSE THEY MEAN OPPOSITE THINGS. Found
          // elsewhere is a signpost with a button per tab; found nowhere is a
          // dead end that says what was searched. Never a jump by itself.
          <SearchSignpost
            tabLabel={TRANSFER_TABS.find(x => x.id === tab)?.label || ''}
            others={TRANSFER_TABS
              .filter(x => x.id !== tab && tabCounts[x.id] > 0)
              .map(x => ({ id: x.id, label: x.label, count: tabCounts[x.id] }))}
            elsewhereAtBranches={otherBranchMatches.length > 0 || !!search.result?.otherFinishedMore}
            term={appliedTerm}
            coarse={coarse}
            onShow={chooseTab}
            onClear={() => setSearchText('')}
          />
        ) : filtersOnHere ? (
          // #675 F1/F2 -- FILTERS LEAVE NOTHING: said as the filters' doing, with
          // the way out, never as "nothing here".
          <FiltersEmpty
            tabLabel={TRANSFER_TABS.find(x => x.id === tab)?.label || ''}
            coarse={coarse}
            onClear={clearFilters}
          />
        ) : (
        <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#5C5C5C', fontSize: 13 }}>
          {/* gate 665 D17 -- the branch named here too. See tabEmptyText(). */}
          {tabEmptyText(tab, {
            seesEveryBranch,
            branchName: branchNames[String(lookingAt)] || null,
            makeable: makeable.length > 0,
          })}
        </div>
      ))}

      {/* THE SECOND DOORWAY, and it sits ABOVE the list rather than under it.
          #194's rule, which that issue's own session then stepped around: a
          control below a list longer than a screen is a control nobody can
          reach. It also has to be visible when the list is EMPTY, which is the
          state Houston is actually in — and the empty state below points at it
          by name rather than leaving somebody looking at nothing. */}
      {/* GATE D47 — THE THINGS THIS BRANCH MAKES THAT NOBODY HAS PRICED.
          It sits on the production screen rather than in the catalog because
          this is where the person who notices it is standing, and it names the
          consequence rather than the condition: "worth nothing" is checkable,
          "missing standard cost" is a field name.

          NOT A BLOCK. D41's reasoning: refusing a run that really happened
          leaves the COUNT wrong, and the count is what people work from. SAP
          blocks the material; Stede makes the gap impossible to miss instead.

          Each row opens the set-cost panel for that item, for whoever may use
          it. Somebody who may not still sees the list, because knowing the
          figure is missing is useful even when asking a manager is the fix. */}
      {tab === 'to_make' && unpriced.length > 0 && (
        <div style={{
          background: '#FFF8EC', border: '1px solid #F3D9A8', borderLeft: '3px solid #F3D9A8',
          borderRadius: 8, padding: '11px 13px', marginBottom: 10, color: '#8A5A08',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            {unpriced.length === 1
              ? 'One thing you make has no cost set.'
              : `${unpriced.length} things you make have no cost set.`}
          </div>
          <div style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
            Anything recorded or sent goes on the books worth nothing, and the branch
            receiving it books it in at nothing too.
          </div>
          {/* CAPPED AT SIX, AND THE CAP IS A DEFECT FIX RATHER THAN A STYLE
              CHOICE. The first build rendered every one. Houston makes 25
              things and 24 have no cost, so the banner ran three screens deep
              and pushed the actual work queue out of sight — #518's wall of
              cards, on the very screen a warning was being added to, and
              visible the second it was loaded.

              A warning that buries the thing it warns about stops being a
              warning. Six, then a count, then everything: the same shape
              #518's own fix used on Stock Home. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {(showAllUnpriced ? unpriced : unpriced.slice(0, 6)).map(i => (
              <button
                key={i.id}
                onClick={() => setCostFor(i)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '6px 10px', minHeight: controlHeight(coarse, 'min'),
                  background: '#fff', border: '1px solid #F3D9A8', borderRadius: 999,
                  color: '#8A5A08', cursor: 'pointer',
                }}
              >
                {i.name}
              </button>
            ))}
            {unpriced.length > 6 && (
              <button
                onClick={() => setShowAllUnpriced(v => !v)}
                style={{
                  fontSize: 12, fontWeight: 700, padding: '6px 10px', minHeight: controlHeight(coarse, 'min'),
                  background: 'none', border: 'none', color: '#8A5A08',
                  textDecoration: 'underline', cursor: 'pointer',
                }}
              >
                {showAllUnpriced ? 'show fewer' : `and ${unpriced.length - 6} more`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* #608's guard (R3, approved as drawn in
          docs/audits/79.22b.6.2d/to_make_shelf_line_APPROVED.html) -- the same
          amber line as the cut list's above. */}
      {tab === 'to_make' && shelfReadFailed && (
        <div role="status" style={{
          margin: '0 0 12px', padding: '9px 12px', background: '#FFF8EC', border: '1px solid #F3D9A8',
          borderRadius: 8, fontSize: 13, color: '#8A5A08', lineHeight: 1.45,
        }}>
          Couldn't load every item, so the shelf rows and Record something else aren't shown. Reload to try again.
        </div>
      )}

      {tab === 'to_make' && makeable.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <button
            onClick={() => setPicking(true)}
            className="btn btn-ghost"
            style={{
              minHeight: controlHeight(coarse, 'min'),
              fontSize: isMobile ? 15 : 13,
              width: isMobile ? '100%' : undefined,
              justifyContent: 'center',
            }}
          >
            Record something else
          </button>
        </div>
      )}

      {/* SCREEN 5 -- THE DRIVER'S DAY. D66, and the whole thing is built to
          Colby's constraint: open, tap a card, type how many, done.

          A card names the place, the items and the quantities waiting, ordered
          by promised date so the top of the list is what has to move today.
          Tapping one opens the transfer, where the count goes in. */}
      {/* THE THREE LOAD-SHAPED TABS. They count TRUCKS, not requests, which is
          why they render a different card -- and why the old page was confusing
          when one tab counted one and its neighbour counted the other.

          Each carries its stage colour down onto the card, so a driver holding
          a phone sees orange for a truck to fill and blue for one to fetch
          without reading a word. Colby's ask. */}
      {['moving', 'collect', 'count_in'].includes(tab) ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {/* #531 -- ABOVE the trucks, because these are the only rows in this
              tab that anybody has to act on, and owedToBranch already sorts a
              discrepancy ahead of a wait. */}
          {/* #675 D22 -- a transfer closed short (or ended) by a press on the
              right keeps its place, drawn as a transfer card so its chip shows
              where it now stands: the owed card deliberately has no status chip. */}
          {tab === 'moving' && sOwedShown.map(row => (row.__pinned ? (
            <TransferCard
              chosenId={chosenId}
              key={`owed-pinned-${row.transfer.id}`}
              transfer={row.transfer}
              branchNames={branchNames}
              items={items}
              myBranch={myBranch}
              stage="moving"
              onOpen={() => setSelected(row.transfer)}
            />
          ) : (
            <OwedCard
              chosenId={chosenId}
              key={`owed-${row.transfer.id}`}
              row={row}
              branchNames={branchNames}
              items={items}
              matched={matchedFor(row.transfer.id)}
              onOpen={() => setSelected(row.transfer)}
            />
          )))}
          {/* #697, gate 665 D23 and Screen 10's right-hand card -- THE HUB'S OWN
              GOODS, waiting on its rack for this branch's driver. Drawn as a
              pile at the hub, because to the driver that is what it is: go to
              Austin, take it. The box holds what is ready; untouched means all
              of it. Only the branch the goods are for gets the box and button,
              as with every pile. */}
          {tab === 'collect' && sHubRowsShown.map((p, index) => {
            const t = p.branch_transfers;
            const open = () => setSelected(t);
            const hubName = branchNames[String(t.from_branch_id)] || 'the hub';
            const boxed = loadCardLines(t, items).filter(l => !l.noBox);
            const standing = boxed.reduce((n, l) => n + (Number(l.max) || 0), 0);
            const stop = {
              branchId: t.from_branch_id, place: null,
              total: p.__pinned ? 0 : standing,
              rows: p.__pinned ? [] : boxed.map(l => ({ item_id: l.item_id, available: l.max, name: l.name })),
            };
            const hubIsMine = String(t.to_branch_id) === String(myBranch);
            const action = p.__pinned
              ? <CardNoteOnly note={cardOnlyNote} onUndo={undoFromCard} busy={busy} onOpen={open} />
              : hubIsMine ? (
                <LoadCardAction
                  // The boxes re-fill on the database's numbers after a press.
                  key={`hub-${t.id}:${standing}`}
                  busy={busy}
                  accent="#378ADD"
                  lines={boxed.map(l => ({ item_id: l.item_id, name: l.name, max: l.max, sub: `${l.max} at ${hubName}` }))}
                  lineNote={(line, v) => {
                    const left = Math.max(line.max - v, 0);
                    return left > 0 ? `${left} ${left === 1 ? 'stays' : 'stay'} at ${hubName}` : null;
                  }}
                  guard={(values, total) => {
                    const over = boxed.find(l => values[String(l.item_id)] > l.max);
                    if (over) return overLoadCeiling(over);
                    return total > 0 ? null : TYPE_HOW_MANY_LOAD;
                  }}
                  label={(total) => hubPickupLabel(total, Math.max(standing - total, 0), hubName)}
                  footer={(total) => loadCardFooter(t, total)}
                  note={noteForPile(`hub:${t.id}`)}
                  onUndo={undoFromCard}
                  onOpen={open}
                  onSubmit={(values) => collectHubGoodsFromCard(t, boxed, values, index)}
                />
              ) : null;
            return (
              <LoadCard
                chosenId={chosenId}
                key={`hub-${t.id}`}
                load={p}
                branchNames={branchNames}
                items={items}
                isMobile={isMobile}
                stop={stop}
                subline={hubPickupSubline(t, branchNames, p.__pinned ? null : standing)}
                stage={TRANSFER_TABS.find(x => x.id === tab)}
                matched={matchedFor(t.id)}
                pinned={!!p.__pinned}
                action={action}
                onOpen={open}
              />
            );
          })}
          {(tab === 'moving' ? sElsewhere : tab === 'collect' ? sCollectRowsShown : sCountRowsShown).map((l, index) => {
            const t = transfers.find(x => String(x.id) === String(l.transfer_id))
              || l.__transfer || l.branch_transfers;

            // #694, 79.22b.4.6.1b -- A CARD KEPT IN PLACE after the press took
            // its whole pile: where it stood, its answer and its Undo, nothing
            // to press again. The pile's own card returns if the Undo is used.
            // #694, 79.22b.4.6.2 -- the same for a truck the press counted in:
            // where it came from, its answer and its Undo. The truck's own card
            // returns if the Undo is used.
            if (tab === 'count_in' && l.__pinned) {
              return (
                <LoadCard
                  chosenId={chosenId}
                  key={`pinned-count-${l.id}`}
                  load={l}
                  branchNames={branchNames}
                  items={items}
                  isMobile={isMobile}
                  stop={null}
                  stage={TRANSFER_TABS.find(x => x.id === tab)}
                  pinned
                  action={<CardNoteOnly note={cardOnlyNote} onUndo={undoFromCard} busy={busy} onOpen={() => { if (t) setSelected(t); }} />}
                  onOpen={() => { if (t) setSelected(t); }}
                />
              );
            }

            if (tab === 'collect' && l.__pinned) {
              return (
                <LoadCard
                  chosenId={chosenId}
                  key={`pinned-${cardNote?.key || l.id}`}
                  load={l}
                  branchNames={branchNames}
                  items={items}
                  isMobile={isMobile}
                  stop={{ ...(cardNote?.stop || stopOf(l) || {}), rows: [], total: 0 }}
                  stage={TRANSFER_TABS.find(x => x.id === tab)}
                  pinned
                  action={<CardNoteOnly note={cardOnlyNote} onUndo={undoFromCard} busy={busy} onOpen={() => { if (t) setSelected(t); }} />}
                  onOpen={() => { if (t) setSelected(t); }}
                />
              );
            }

            const stop = tab === 'collect' ? collectStops.get(String(l.id)) : null;

            // ** WHOSE JOB IT IS, ASKED THE WAY THE PANELS ASK IT. **
            // These three lists widen for an admin or executive, who sees every
            // branch -- so the card for a Houston pickup is drawn for somebody
            // standing at Dallas. CollectPanel refuses that with `isMine` and
            // ArrivalPanel by filtering to loads heading HERE, and the card's
            // action has to ask the same question or it would offer a press the
            // database would refuse. It is NOT an unexplained absence: the
            // branch chip at the top of this page says whose work is on screen.
            const collectIsMine = !!t && String(t.to_branch_id) === String(myBranch);
            const countInIsMine = String(l.to_branch_id) === String(myBranch);

            let action = null;

            if (tab === 'collect' && collectIsMine && stop && (stop.rows || []).length) {
              const where = stop.place || branchNames[String(stop.branchId)] || 'the handover';
              const totalAvail = stop.rows.reduce((n, r) => n + (Number(r.available) || 0), 0);
              action = (
                <LoadCardAction
                  // ** THE TYPED NUMBERS RESET WHEN THE PILE CHANGES. ** Found
                  // proving this on staging: a partial collect of 5 left 7 at
                  // Austin, the list re-read correctly -- and the box still said
                  // 5, so the card offered "Take 5, leave 2" on a pile of 7 when
                  // the default is meant to be all of it. Keying on the total
                  // remounts the boxes on the new numbers, the same trick
                  // SignOffRunSheet uses so reopening it re-reads rather than
                  // redrawing whatever was on screen last time.
                  key={`${l.id}:${totalAvail}`}
                  busy={busy}
                  accent="#378ADD"
                  lines={stop.rows.map(r => ({
                    item_id: r.item_id,
                    name: r.name || items[String(r.item_id)]?.name || `Item ${r.item_id}`,
                    max: Number(r.available) || 0,
                    sub: `${r.available} at ${where}`,
                  }))}
                  lineNote={(line, v) => {
                    const left = Math.max(line.max - v, 0);
                    // #705 -- "3 stays at Austin" read wrong for any pile but one.
                    return left > 0 ? `${left} ${left === 1 ? 'stays' : 'stay'} at ${where}` : null;
                  }}
                  label={(total) => {
                    const leaving = Math.max(totalAvail - total, 0);
                    if (total === 0) return `Take nothing from ${where}`;
                    return leaving > 0
                      ? `Take ${total}, leave ${leaving}`
                      : `Collect ${total} from ${where}`;
                  }}
                  footer={() => collectCardDoor(stop, branchNames)}
                  // #694 / D21, 79.22b.4.6.1b -- the answer lands HERE, with its
                  // Undo, attached to the pile rather than to one truck.
                  note={noteForPile(pileKeyOf(l))}
                  onUndo={undoFromCard}
                  // #704 -- the footer's door, as a button.
                  onOpen={() => setSelected(t)}
                  onSubmit={(values, total) => collectFromCard(
                    t,
                    // The sheet hands in `fetchLoads(transfer_id)`; this is the
                    // same set out of the page's open-loads list. See collectFrom.
                    openLoads.filter(x => String(x.transfer_id) === String(t.id)),
                    {
                      stop,
                      taking: stop.rows
                        .map(r => ({
                          item_id: r.item_id,
                          qty: values[String(r.item_id)] ?? 0,
                          unit_cost: r.unit_cost ?? null,
                          sourced: r.sourced ?? null,
                        }))
                        .filter(x => x.qty > 0),
                      leaving: Math.max(totalAvail - total, 0),
                      index,
                      pileKey: pileKeyOf(l),
                      loadId: l.id,
                    })}
                />
              );
            }

            if (tab === 'count_in' && countInIsMine && (l.branch_transfer_load_items || []).length) {
              const sent = l.branch_transfer_load_items;
              const countsFrom = (values) => sent.map(r => ({
                item_id: r.item_id, counted: String(values[String(r.item_id)] ?? ''),
              }));
              action = (
                <LoadCardAction
                  // Same reset as the collect card above.
                  key={`${l.id}:${sent.reduce((n, r) => n + (Number(r.qty_loaded) || 0), 0)}`}
                  busy={busy}
                  accent="#14503B"
                  // ** A COUNT ABOVE WHAT WAS SENT IS NOT CAPPED, IT IS ASKED
                  // ABOUT. ** #587 catches it here in plain words rather than
                  // letting the database refuse it after the press, and the
                  // refusal is the panel's own, not a second copy of it.
                  allowOver
                  lines={sent.map(r => ({
                    item_id: r.item_id,
                    name: r.inventory_items?.name || items[String(r.item_id)]?.name || `Item ${r.item_id}`,
                    max: Number(r.qty_loaded) || 0,
                    sub: `${r.qty_loaded} sent`,
                  }))}
                  lineNote={(line, v) => {
                    if (v < line.max) return `${line.max - v} short — recorded against ${l.load_ref}`;
                    // #682 -- above a truck its sender can put right, the extra
                    // is a question for the sender, in P4's words.
                    if (v > line.max) {
                      return senderCanPutRight(l, t) && String(l.to_branch_id) === String(t.to_branch_id)
                        ? `${v - line.max} more than ${branchNames[String(l.from_branch_id)] || 'the sending branch'}'s load sheet`
                        : `${v - line.max} more than was sent`;
                    }
                    return null;
                  }}
                  guard={(values) => {
                    const preview = discrepancy(sent.map(r => ({
                      ...r, qty_arrived: values[String(r.item_id)],
                    })));
                    const over = overCountRefusal({ load: l, transfer: t, preview, branchNames });
                    return over ? over.blockedReason : null;
                  }}
                  // #673 -- names the branch the goods are going onto. #682, F2:
                  // "Count in at {Dallas}, without a number while a line is
                  // above its truck" -- the number typed is not the number booked.
                  label={(total, values) => {
                    const dest = branchNames[String(t.to_branch_id)] || 'the receiving branch';
                    const preview = discrepancy(sent.map(r => ({ ...r, qty_arrived: values?.[String(r.item_id)] })));
                    return overCountAsk({ load: l, transfer: t, preview, branchNames, items })
                      ? `Count in at ${dest}` : `Count in ${total} at ${dest}`;
                  }}
                  footer={(total, values) => {
                    const preview = discrepancy(sent.map(r => ({
                      ...r, qty_arrived: values[String(r.item_id)],
                    })));
                    const over = overCountRefusal({ load: l, transfer: t, preview, branchNames });
                    if (over) return over.notice;
                    // #682 -- P4's notice: what Count in will do with the extra.
                    const ask = overCountAsk({ load: l, transfer: t, preview, branchNames, items });
                    if (ask) return ask.notice;
                    return preview.missing > 0
                      ? `${preview.missing} short of what was sent. Only what you counted is booked in, and the gap is recorded against ${l.load_ref}.`
                      : `Everything that was sent goes onto the rack at ${branchNames[String(t.to_branch_id)] || 'the receiving branch'}.`;
                  }}
                  // #694 / D21, 79.22b.4.6.2 -- the answer lands HERE, with its
                  // Undo; the sheet's arrival panel still answers at the top.
                  note={noteForCount(l.id)}
                  onUndo={undoFromCard}
                  onSubmit={(values) => countInFromCard(t, l, countsFrom(values), index)}
                  // #704 -- the same door as every other card that acts.
                  onOpen={() => { if (t) setSelected(t); }}
                />
              );
            }

            return (
              <LoadCard
                chosenId={chosenId}
                key={l.id}
                load={l}
                branchNames={branchNames}
                items={items}
                isMobile={isMobile}
                stop={stop}
                stage={TRANSFER_TABS.find(x => x.id === tab)}
                matched={matchedFor(l.transfer_id)}
                matchedTruck={searchOn && String(searchMatches.get(String(l.transfer_id))?.loadId) === String(l.id)}
                action={action}
                // #636, Session 79.22b.1 -- ON THE WAY NOW OPENS, LIKE EVERY
                // OTHER TAB. It was made watch-only on Sep 11 with a comment
                // citing #492 -- but the card is a <button> with a pointer
                // cursor either way, so passing null did not remove the control,
                // it made it a dead press: the exact thing #492 forbids.
                //
                // AND THE TAB HAS A JOB THAT WAS UNREACHABLE. `moving` is the
                // one stage carrying `closeShort`, put there by gate D30 on
                // purpose: a balance with nothing rolling and nothing at a yard
                // is final, and this is the tab holding it, so the write-off has
                // to be reachable from here or an owed transfer is the dead end
                // #559 exists to stop. The sheet already offers it correctly.
                onOpen={() => { if (t) setSelected(t); }}
              />
            );
          })}
        </div>
      ) : tab === 'to_make' ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {sMakeRowsShown.map((row, index) => row.kind === 'order' ? (
            <TransferCard
              chosenId={chosenId}
              key={row.key}
              transfer={row.transfer}
              branchNames={branchNames}
              items={items}
              myBranch={myBranch}
              stage="to_make"
              matched={matchedFor(row.transfer.id)}
              own={!!row.own}
              askerName={row.own ? (askerNames[String(row.transfer.requested_by)]?.full_name || null) : null}
              onOpen={() => setSelected(row.transfer)}
              // #665 D6 -- the items are listed WITH their boxes in the job
              // below, so the card body does not list them a second time.
              linesInAction={!row.pinned && String(row.transfer.from_branch_id) === String(myBranch)}
              action={
                <MakeCardJob
                  transfer={row.transfer}
                  items={items}
                  branchNames={branchNames}
                  myBranch={myBranch}
                  auth={auth}
                  busy={busy}
                  lookingElsewhere={lookingElsewhere}
                  pinned={!!row.pinned}
                  note={noteFor('to_make', row.transfer.id)}
                  onUndo={undoFromCard}
                  onOpen={() => setSelected(row.transfer)}
                  onSignOff={() => setSigningOff(row.transfer)}
                  onSubmit={(cardLines, values) => signOffFromCard(row.transfer, cardLines, values, index)}
                />
              }
            />
          ) : (
            <ReserveCard
              key={row.key}
              row={row}
              isMobile={isMobile}
              onRecord={() => startRecording(row.item, row.qtyOnHand, index)}
              note={shelfRunNoteFor(row.item.id)}
              onUndo={undoShelfRun}
              busy={busy}
            />
          ))}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {(tab === 'to_load' ? sLoadRowsShown : sVisible).map((t, index) => (
            <TransferCard
              chosenId={chosenId}
              key={t.id}
              transfer={t}
              branchNames={branchNames}
              items={items}
              myBranch={myBranch}
              // #665 D3 -- every tab, so each item says the number THIS tab moves.
              stage={tab}
              // #675 D3 -- on Finished the match rides on the row its own read
              // returned; on the working tabs it comes from the search's map.
              matched={matchedFor(t.id, t)}
              // #665 D15 -- the giving shelf, on Awaiting confirmation only.
              // #675 D22 -- never on a card kept in place after it was approved:
              // its own promise is now counted among the shelf's, so the line
              // would take the same goods off twice.
              shelfFor={tab === 'confirm' && !t.__pinned ? (l => approvalShelfLine({
                qty: l.qty_requested,
                giver: branchNames[String(t.from_branch_id)],
                stock: receiverStock[`${l.item_id}:${t.from_branch_id}`],
                isMake: t.fulfilment_mode === 'make',
                state: stockState,
                // #721 -- what that shelf has already promised elsewhere.
                promised: promisedByKey[`${l.item_id}:${t.from_branch_id}`],
              })) : null}
              // #721's reader of #608's cut-off -- no shelf note when the
              // promises it subtracts may be missing (D17).
              shelfNote={tab === 'to_load' && inHandCutAt == null && !hasReadyToLoad(t, t.branch_transfer_items || [])
                ? shelfNoteFor(t, t.branch_transfer_items || [], shelfAt(t.from_branch_id), branchNames,
                    // #721 (drawing B) -- this card's own items are not ready, so
                    // every promise counted here is another transfer's.
                    itemId => promisedByKey[`${itemId}:${t.from_branch_id}`] || 0) : null}
              onOpen={() => setSelected(t)}
              // #665 D8 -- To load's card sends what is ready, from the card,
              // and lists the items with their boxes there instead of here.
              linesInAction={tab === 'to_load' && !t.__pinned
                && String(t.from_branch_id) === String(myBranch)
                && hasReadyToLoad(t, t.branch_transfer_items || [])}
              action={tab === 'to_load' ? (
                <LoadCardJob
                  transfer={t}
                  items={items}
                  branchNames={branchNames}
                  myBranch={myBranch}
                  auth={auth}
                  busy={busy}
                  hubs={branches.filter(b => b.is_transfer_hub)}
                  drivers={drivers}
                  loaderModel={loaderModel}
                  // #671 -- the load number waits for the page's own read of
                  // what is already on trucks, and the driver list with it.
                  ready={!loading && driversLoaded}
                  openLoads={openLoads}
                  pinned={!!t.__pinned}
                  note={noteFor('to_load', t.id)}
                  onUndo={undoFromCard}
                  onOpen={() => setSelected(t)}
                  onSubmit={(sending, values) => sendFromCard(t, sending, values, index)}
                />
              ) : null}
            />
          ))}
          {/* #697, gate 665 D23 -- WHAT THE HUB HOLDS FOR A BRANCH THAT COLLECTS
              IT HERE. On the hub's To load so nobody wonders where it went, with
              nothing to press and no count: it is the other branch's job. The
              transfer still opens, and the sheet can still send it. */}
          {tab === 'to_load' && sAwaitingShown.map(t => (
            <TransferCard
              chosenId={chosenId}
              key={`await-${t.id}`}
              transfer={t}
              branchNames={branchNames}
              items={items}
              myBranch={myBranch}
              stage="to_load"
              matched={matchedFor(t.id)}
              heading={hubCollectHeading(t, branchNames)}
              onOpen={() => setSelected(t)}
              action={(
                <div style={{ borderTop: '1px solid #F0F0F0', padding: '10px 13px', fontSize: 13, color: '#5C5C5C', lineHeight: 1.45 }}>
                  {hubCollectNote(t, branchNames)}
                </div>
              )}
            />
          ))}
        </div>
      )}

      {/* #608 / GATE 675 D16 -- FINISHED'S PAGES. While a page is on its way the
          list stays as it was and says so; a failed read says so with a way to
          try again (F1, F2). A desk pages with the Ledger's control; a phone or
          tablet grows the list 25 at a time with the Catalog's. Nothing shows
          when there is nothing finished, and no pager. */}
      {tab === 'finished' && !loading && (
        <FinishedPager
          finished={finishedNow}
          at={finishedAt}
          onDesk={onDesk}
          coarse={coarse}
          onPage={setFinishedAt}
          onRetry={() => setFinishedAt(p => ({ ...p }))}
        />
      )}

      {/* #675 / gate 675 D2 -- A MATCH AT ANOTHER BRANCH IS LISTED, NOT HIDDEN
          (callout B). Jennifer, looking at Houston, types a Dallas number: the
          answer is never "no match". The view switches only when she taps Look
          at, and looking stays looking. A team member never sees this list
          (M2): their search reaches their own branch only. */}
      {/* Shown too when every row was dropped but more may exist, so the
          "type more of the number" line is never hidden (Grok's review). */}
      {searchOn && (otherBranchMatches.length > 0 || !!search.result?.otherFinishedMore) && (
        <OtherBranchMatches
          rows={otherBranchMatches}
          more={!!search.result?.otherFinishedMore}
          branchNames={branchNames}
          myBranch={myBranch}
          coarse={coarse}
          phoneLayout={!onDesk}
          onLookAt={lookAtMatch}
        />
      )}
      </DeskSplit>
      </TabSheet>

      {/* D47 — set a missing cost without leaving the production screen. The
          SAME panel the recording sheet and the item page open, not a third
          copy: #358 is the record of what happens when a control lives in
          several places and gets fixed in only one of them. */}
      <SetProductionCostPanel
        visible={!!costFor}
        onClose={() => setCostFor(null)}
        onSaved={() => { setCostFor(null); load(); }}
        isMobile={isMobile}
        item={costFor}
        auth={auth}
      />

      {/* #553 D5, Session 79.15e -- the control that closes this issue's
          headline. Keyed on the transfer so reopening it re-reads the
          numbers rather than rendering whatever was on screen last time. */}
      <SignOffRunSheet
        visible={!!signingOff}
        key={signingOff?.id || 'none'}
        onClose={() => { setSigningOff(null); setSignOffPin(null); }}
        // #675 D22 -- opened from the desk's right side: once the list is read
        // again, the right side says where the transfer went (the sheet itself
        // has already said what was signed off), and its card keeps its place.
        // Grok's review, 79.22b.5.5b -- not after a race: somebody else signed it
        // off, the sheet says so, and the right side claims nothing for this press.
        onSaved={async (saved) => {
          await load();
          if (!saved?.race && signOffPin && split && String(selectedRef.current?.id) === String(signOffPin.transferId)) {
            deskSeq.current += 1;
            setCardNote({ desk: true, seq: deskSeq.current, ...signOffPin, kind: 'done', text: null });
          }
        }}
        transfer={signingOff}
        branchNames={branchNames}
        // #641 -- the batched name map, rather than widening the page's fetch.
        // Every entry point opens THIS instance: the To make card, the record-a-
        // run sheet's "sign off against it" (553 D27) and the transfer sheet (#622).
        items={items}
        auth={auth}
      />

      <PickWhatYouMadeSheet
        visible={picking}
        onClose={() => setPicking(false)}
        onPick={item => { setPicking(false); startRecording(item); }}
        isMobile={isMobile}
        items={makeable}
        branchName={branchNames[String(myBranch)]}
      />

      <RecordProductionSheet
        visible={!!recording}
        onClose={() => setRecording(null)}
        onDone={result => {
          // A cost change alone is not a production run and must not claim to
          // be one — it reports itself and the sheet stays open so the person
          // can carry on with the run they came here for.
          if (result?.costOnly) { showSuccess('Cost updated.'); load(); return; }
          // #761, T4 -- the answer lands on the card, with its Undo, and stays
          // until the next press; it used to fade after four seconds.
          answerShelfRun(recording, result);
          load();
        }}
        isMobile={isMobile}
        item={recording?.item}
        branchName={recording?.branchName}
        qtyOnHand={recording?.qtyOnHand}
        auth={auth}
        // 553 D27 -- the run counts against the request instead.
        onSignOffInstead={transfer => { setRecording(null); setSigningOff(transfer); }}
      />

      {/* Below the desk the chosen transfer is today's sheet over the page; on a
          desk DeskSplit draws it in the right-hand column (D19). */}
      {!split && detailEl}
    </div>
  );
}

// ── THE STAGES OF A TRANSFER, IN THE ORDER ONE ACTUALLY MOVES ───────────────
//
// Session 79.10, and every part of this is Colby's ruling after he read the
// old tabs and could not tell them apart. His standard, stated plainly:
// "a new hire should be able to open any page of Stede and easily understand
// what everything means."
//
// WHAT WAS WRONG, because the names were only half of it:
//
//   * "Waiting on me" was ONE tab doing THREE unrelated jobs -- approving,
//     loading a truck and counting a delivery are different people on
//     different days. And "me" answered neither who nor what.
//   * "On the road" and "In flight" were two names for the same idea, and one
//     of them counted TRUCKS while every tab beside it counted REQUESTS, so
//     the numbers in the pills did not mean the same thing.
//   * "To make" looked like it might also hold shelf transfers. It never did.
//   * The order was not the order anything happens in.
//
// EVERY TAB IS NOW ONE PERSON'S JOB ON ONE DAY, and they run left to right in
// the order a request travels: somebody agrees to it, somebody makes it,
// somebody loads it, it travels, somebody fetches it from a hub, somebody
// counts it in, it is done.
//
// COLOUR CARRIES THE STAGE, at Colby's instruction, so a driver can tell two
// trucks apart without reading. THE TWO DRIVER TABS TAKE THE TWO MOST
// SEPARATED COLOURS -- orange to FILL a truck, blue to FETCH one -- because
// that is the pair that must never be confused at a yard.
//
// THE TWO TABS WITH NOTHING TO DO ARE BOTH GREY, and that is the point rather
// than an oversight: grey means this is not your job.
//
// ** #581, Session 79.21b -- EVERY SELECTED TAB'S LABEL FAILED THE FLOOR. **
// The selected tab fills with its stage colour and writes the label in white,
// and all seven measured below 4.5: To make 2.17, On the way and Finished
// 2.81, To load 3.07, To count in 3.39, To collect 3.59, Awaiting confirmation
// 3.76 -- with the count badge at 1.75. The stage colour stays: it is what
// tells a driver two trucks apart, and it still paints the dot, the cards and
// the rule. `ink` is a DARKENED VARIANT OF THE SAME HUE used only where white
// text sits on it, which is the fix this module already made twice -- gate
// 550/551's Make control (#0F6E56, 6.20) and the history's hub blue (#1F6FBF,
// 5.14). Measured white-on-fill, every one: 6.06, 5.92, 6.06, 6.69, 5.14,
// 6.20, 6.69.
// #681, gate 665 D16 -- every label comes from TAB_LABELS, the one list, so
// the tab strip and every signpost naming a tab cannot disagree.
// #757, Session 79.22b.5.5c -- `tint` is the stage's PALE PAPER, the colour of an
// unchosen paper tab, with its label in `ink` on it. Measured ink on tint, every
// one at or above 4.5: 5.37, 5.43, 5.33, 5.97, 4.68, 5.58, 5.97. To collect's is
// a shade lighter than the drawing's #EAF2FC, which measured 4.56.
export const TRANSFER_TABS = [
  { id: 'confirm',  label: TAB_LABELS.confirm, color: '#7F77DD', ink: '#5B51C9', tint: '#F1F0FC' },
  { id: 'to_make',  label: TAB_LABELS.to_make, color: '#EF9F27', ink: '#8A5A08', tint: '#FDF4E6' },
  { id: 'to_load',  label: TAB_LABELS.to_load, color: '#E2761B', ink: '#9A4E09', tint: '#FCEEE3' },
  { id: 'moving',   label: TAB_LABELS.moving, color: '#9A9A9A', ink: '#5C5C5C', tint: '#F2F2F2' },
  { id: 'collect',  label: TAB_LABELS.collect, color: '#378ADD', ink: '#1F6FBF', tint: '#EEF5FD' },
  { id: 'count_in', label: TAB_LABELS.count_in, color: '#1D9E75', ink: '#0F6E56', tint: '#E8F6F0' },
  { id: 'finished', label: TAB_LABELS.finished, color: '#9A9A9A', ink: '#5C5C5C', tint: '#F2F2F2' },
];

// #757 -- the phone's spacing around M4's row, held to gate 675 D14: the first
// card at or above 222px at 375 x 667 (measured in the session notes).
const PHONE_LINE_MARGIN = '0 0 6px';
const PHONE_BLURB_MARGIN = '0 0 8px';

// ── THE LINE UNDER THE TABS -- #681 and #652, gate 665 D17, Session 79.22b.4.4 ──
//
// Colby at 79.22b.3 (#673): "The system should say where a load is actually
// going and not use vague words like 'Here'." The line under the tab strip used
// to say "Standing here" and "your own rack" whenever the reader was at their own
// branch -- and #652 had carefully kept those words, adding named sentences only
// for the all-branches view and for a branch you are looking at. D17 SUPERSEDES
// that rule on Colby's ruling: ONE template per tab, naming the branch in view,
// serves your own branch and a branch you are looking at alike; the all-branches
// view keeps its own sentence. The words are gate 665 Screen 6's table, word for
// word, and "Sign off what your branch makes" -- #652's one leftover -- goes.
//
// Earlier history the old per-tab sentences carried, kept because it still
// explains the words: #553 made To make about signing off an ORDER, not only
// recording reserve work; #531 made the owed tab stop promising "nothing here
// needs you" over a balance somebody has to write off; #676 put what a branch
// SENT that came up short on it, so the sender can reach its write-off.
//
// `branchName` is the branch in view. With none (a name not yet loaded) the
// line says "this branch" -- never "here".
export function tabBlurb(tab, { seesEveryBranch = false, branchName = null } = {}) {
  if (!tab) return null;
  const b = branchName || 'this branch';
  const bs = branchName ? `${branchName}’s` : 'this branch’s';
  if (seesEveryBranch) {
    switch (tab.id) {
      // #682, 682 D13 and callout K -- both kinds of question; gate 665 D17
      // narrowed for this tab (no branch named, so it stays on one line).
      case 'confirm':  return 'Asks and truck extras at any branch still waiting on an answer.';
      case 'to_make':  return 'Made to order at every branch that makes things. Each branch signs off its own.';
      case 'to_load':  return 'Standing at any branch, ready to go on a truck.';
      case 'moving':   return 'Anything owed or short, at any branch. Nothing is moving on these.';
      case 'collect':  return 'Waiting at a yard for the branch that asked to go and get it.';
      case 'count_in': return 'Coming to the branch that asked — on the road, or there waiting to be counted.';
      case 'finished': return 'Received, refused or closed.';
      default:         return null;
    }
  }
  switch (tab.id) {
    // #682 -- P6, callout K, word for word.
    case 'confirm':  return 'Asks and truck extras still waiting on an answer.';
    case 'to_make':  return `Made to order at ${b}, and for ${bs} own shelf. Sign off what is made.`;
    case 'to_load':  return `Made and standing at ${b}, ready to go on a truck.`;
    case 'moving':   return `${bs.charAt(0).toUpperCase()}${bs.slice(1)} trucks still on the road, other branches’ trucks, and anything owed to ${b} or short from it.`;
    case 'collect':  return `Waiting at a yard, or on its way there, for ${b} to go and get it.`;
    case 'count_in': return `Coming to ${b} on its last leg — on the road, or there waiting to be counted. Count it and say if anything is short.`;
    case 'finished': return 'Received, refused or closed.';
    default:         return null;
  }
}

// THE EMPTY TAB, IN THE SAME VOICE -- gate 665 D17 and F2: every empty-state
// sentence names the branch too. An empty tab still says what WOULD appear here,
// which is the older rule and still right: "Nothing here yet" teaches a new hire
// nothing about what the tab is for. F2 writes the single-branch sentences; the
// all-branches ones for Awaiting confirmation, To collect and To count in follow
// the tab line's own all-branches wording.
export function tabEmptyText(tabId, { seesEveryBranch = false, branchName = null, makeable = false } = {}) {
  const b = branchName || 'this branch';
  switch (tabId) {
    case 'confirm':
      return seesEveryBranch
        ? 'Nothing is waiting for a yes. New asks at any branch land here.'
        : `Nothing is waiting for a yes. New asks to or from ${b} land here.`;
    case 'to_make':
      // The empty state NAMES THE WAY IN. An empty work queue does not mean
      // nothing can be built -- it means nothing is short -- and saying only
      // "nothing to make" is what made the whole feature read as unreachable.
      return makeable
        ? 'Nothing is short and no orders are waiting. To record a run anyway, use “Record something else” above.'
        : `Nothing to make. No orders waiting, and nothing is on file as made at ${seesEveryBranch ? 'any branch' : b}.`;
    case 'to_load':
      // #673 -- the branch in view, named.
      return seesEveryBranch
        ? 'Nothing at any branch is ready to put on a truck yet.'
        : `Nothing at ${b} is ready to put on a truck yet.`;
    case 'moving':
      return seesEveryBranch
        ? 'Nothing has stalled anywhere. Trucks on the road are in To collect and To count in.'
        : `${b} has no truck on the road, nothing is owed to ${b}, and nothing ${b} sent came up short.`;
    case 'collect':
      return seesEveryBranch
        ? 'Nothing is waiting at a yard for any branch.'
        : `Nothing for ${b} is waiting at a yard.`;
    case 'count_in':
      return seesEveryBranch
        ? 'Nothing is on its way to any branch. A truck heading to the branch that asked shows up here to be counted in.'
        : `Nothing is on its way to ${b}. A truck heading there shows up here to be counted in.`;
    case 'finished':
      return 'Nothing has finished yet.';
    default:
      return 'Nothing yet.';
  }
}

// ── #675 / GATE 675 D1, D4, F1-F3, Session 79.22b.5.4 -- THE SEARCH BOX ─────
//
// The Purchasing Queue's box (#304), with two things it lacks: Enter searches at
// once, and the clear button meets the control floor - 40 with a finger, 32 with
// a mouse - where that one is a 22px circle. The circle stays as the drawing;
// the press area around it is the floor. No font size: index.css holds every
// text input at 16px (locked rule 12). 44 tall with a finger (F4).
// Module level, never inside the page (locked rule 2).
function TransferSearchBox({ value, onChange, onEnter, onClear, placeholder, coarse, status, onRetry }) {
  const press = coarse ? controlHeight(true, 'min') : controlHeight(false, 'commit');
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ position: 'relative' }}>
        <IconSearch size={16} color="#7A7A7A" aria-hidden="true"
          style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        <input
          type="text" value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } }}
          placeholder={placeholder}
          aria-label={SEARCH_ARIA_LABEL}
          enterKeyHint="search"
          style={{
            // 44 with a finger (F4); 40 with a mouse, the transfer screens' own
            // floor (#727), where the Purchasing Queue's box is 38.
            width: '100%', height: coarse ? 44 : 40, boxSizing: 'border-box',
            border: '1px solid #D8D8D8', borderRadius: 8, background: 'white', color: '#0F0F0F',
            padding: `0 ${press + 4}px 0 34px`,
          }}
        />
        {value && (
          <button type="button" onClick={onClear} aria-label={CLEAR_SEARCH}
            style={{
              position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
              width: press, height: press, padding: 0, border: 'none', background: 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}>
            <span aria-hidden="true" style={{
              width: 22, height: 22, borderRadius: '50%', background: '#EDEDED', color: '#3A3A3A',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconX size={13} />
            </span>
          </button>
        )}
      </div>
      {status === 'searching' && (
        <div role="status" style={{ fontSize: 12, color: '#5C5C5C', padding: '5px 2px 0' }}>{SEARCHING}</div>
      )}
      {status === 'failed' && (
        <div role="alert" style={{ padding: '7px 2px 0', fontSize: 13, color: '#8A1F1F', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {SEARCH_FAILED}
          <button type="button" onClick={onRetry}
            style={{ minHeight: controlHeight(coarse, 'min'), padding: '0 12px', border: '1px solid #D0D0D0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {TRY_AGAIN}
          </button>
        </div>
      )}
    </div>
  );
}

// #675 / gate 675 D6 and F2 -- AN EMPTY TAB WHILE A SEARCH IS ON.
// Found elsewhere: "Nothing on Awaiting confirmation matches. 1 on To count in."
// with a Show button per tab holding matches - a signpost, so it reads quietly
// rather than as a failure, and it never switches tab by itself. Found only at
// another branch: the first sentence, and the At other branches list below
// answers it. Found nowhere: what was searched, and Clear search.
function SearchSignpost({ tabLabel, others, elsewhereAtBranches, term, coarse, onShow, onClear }) {
  const btn = {
    minHeight: controlHeight(coarse, 'min'), padding: '0 14px', borderRadius: 8,
    border: '1px solid #CFE6DA', background: '#F3FAF6', color: '#0F6E56',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  };
  if (others.length || elsewhereAtBranches) {
    return (
      <div style={{ padding: '12px 2px 4px' }}>
        <div style={{ fontSize: 13, color: '#3A3A3A', lineHeight: 1.5 }}>
          {foundElsewhereSentence(tabLabel, others)}
        </div>
        {others.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {others.map(o => (
              <button key={o.id} type="button" style={btn} onClick={() => onShow(o.id)}>Show {o.label}</button>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ background: 'white', borderRadius: 10, padding: '22px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#3A3A3A' }}>{notFoundSentence(term)}</div>
      <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 6, lineHeight: 1.5 }}>{SEARCH_SCOPE}</div>
      <button type="button" onClick={onClear}
        style={{ ...btn, marginTop: 12, border: '1px solid #D0D0D0', background: 'white', color: '#0F0F0F' }}>
        {CLEAR_SEARCH}
      </button>
    </div>
  );
}

// #675 / gate 675 D2 and F2 -- AT OTHER BRANCHES. Each row: the transfer's label,
// why it matched when that is not its number, and Look at {branch}. `more`: the
// list holds the first 25 finished matches at other branches and more exist -
// said out loud, never passed off as the whole (D17's rule).
function OtherBranchMatches({ rows, more, branchNames, myBranch, coarse, phoneLayout, onLookAt }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        fontFamily: 'var(--font-head)', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: '#5C5C5C', marginBottom: 8,
      }}>
        {AT_OTHER_BRANCHES}
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map(t => {
          const branch = lookAtBranchFor(t, myBranch);
          const why = matchLine(t.__match);
          return (
            <div key={t.id} style={{
              background: '#fff', border: '1px solid #E9E9E9', borderRadius: 10, padding: '10px 12px',
              display: 'flex', flexDirection: phoneLayout ? 'column' : 'row',
              alignItems: phoneLayout ? 'stretch' : 'center', gap: 10, justifyContent: 'space-between',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0F0F0F', overflowWrap: 'anywhere' }}>
                  {transferLabel(t, branchNames)}
                </div>
                <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>
                  {isOwnShelf(t) && t.status === 'received' ? 'On the shelf' : (STATUS_TEXT[t.status] || t.status)}
                </div>
                {why && <div style={{ fontSize: 12, fontWeight: 600, color: '#0F6E56', marginTop: 2 }}>{why}</div>}
              </div>
              <button type="button" onClick={() => onLookAt(t)}
                style={{
                  minHeight: controlHeight(coarse, 'min'), padding: '0 14px', borderRadius: 8, flex: 'none',
                  border: '1px solid #D0D0D0', background: 'white', color: '#0F0F0F',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>
                Look at {branchNames[String(branch)] || 'that branch'}
              </button>
            </div>
          );
        })}
      </div>
      {more && (
        <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 8, lineHeight: 1.5 }}>
          More finished transfers match at other branches than are listed here. Type more of the number to narrow it.
        </div>
      )}
    </div>
  );
}

// #675 D1 / callout D -- `searching`: the pills are match counts, so the tab you
// are on shows its count even at 0 ("Awaiting confirmation 0"), and a tab
// holding matches is marked green - the one place to look.
//
// #757, Session 79.22b.5.5c -- A PAPER TAB (desk and tablet; Colby's R1, "approve
// as drawn, bar breaks"). Unchosen: the stage's pale paper, its colour along the
// top, the label in its ink. Chosen: white, joined to the sheet below - it
// reaches TAB_BAR pixels down over the coloured bar at the foot of the row, so
// the bar breaks under it - and 2px taller than the others. The count rules are
// tabCountPill() (src/lib/transferTabs.js): white on the chosen tab's own ink,
// the stage's ink on white elsewhere, the green pair for a search's matches.
function Tab({ id, tab, setTab, count, color = '#1D9E75', ink = null, tint = '#F2F2F2', searching = false, children }) {
  const on = tab === id;
  // #581 -- white sits on the DARKENED variant, never on the stage colour.
  const fill = ink || color;
  // 553 F4: 40px minimum with a finger; 32 with a mouse (#417 D2).
  const { coarse } = useTier();
  const h = tabHeights(coarse);
  const pill = tabCountPill({ count, on, searching, ink: fill });
  const side = `1px solid ${on ? '#D6D6D6' : '#DEDEDE'}`;
  return (
    <button
      onClick={() => setTab(id)}
      aria-pressed={on}
      data-transfer-tab={id}
      style={{
        boxSizing: 'border-box', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
        height: on ? h.chosen : h.base, marginBottom: on ? 0 : TAB_BAR,
        padding: on ? `0 14px ${TAB_BAR}px` : '0 12px',
        background: on ? '#FFFFFF' : tint, color: fill,
        fontFamily: 'inherit', fontSize: 13, fontWeight: on ? 700 : 600, whiteSpace: 'nowrap', cursor: 'pointer',
        borderLeft: side, borderRight: side, borderBottom: 'none',
        borderTop: `${on ? 4 : 3}px solid ${color}`,
        borderRadius: on ? '9px 9px 0 0' : '8px 8px 0 0',
        boxShadow: on ? '0 -2px 6px rgba(0,0,0,0.07)' : 'inset 0 -6px 6px -6px rgba(0,0,0,0.12)',
        position: 'relative', zIndex: on ? 2 : 'auto',
      }}
    >
      {children}
      {pill && <CountPill pill={pill} n={count} />}
    </button>
  );
}

// THE BUTTON 79.8 DELIBERATELY DID NOT DRAW — #553 D5, Session 79.15e.
//
// `src/lib/production.js` carried a long comment explaining why order rows had
// no Record production button: built naively it DOUBLED Houston's stock, because
// recording a run credited the rack and then answering "we made them" at ship
// time credited it again. Two honest answers, a hundred frames that do not
// exist. That comment was right and it is now spent: signing off is the only
// writer of production, and `load_branch_transfer()` no longer records any.
//
// TWO SEPARATE QUESTIONS, AND BOTH MUST HOLD. May this ROLE sign off at all —
// `canSignOffProduction`, branch managers and above plus the per-person switch.
// And is this person AT the branch making the goods, which is a fact about the
// transfer rather than the role, and which the database enforces for everybody
// including an admin. So an admin at Dallas SEES Houston's make-work, which is
// deliberate and long-standing, and still cannot sign it off.
//
// The refusal ANSWERS THE PRESS rather than hiding — #492. An executive reads
// a reason instead of hunting for a missing button.
//
// #704, Session 79.22b.4.6.1c (Colby's R1) -- ON A DESK IT IS THE WIDTH OF ITS
// WORDS, WITH OPEN TRANSFER BESIDE IT, like every other card that acts. For the
// person this row is drawn for -- somebody who may sign off, but not here -- the
// press is refused, so Open transfer is the useful door. Its 40px height is
// unchanged: above the mouse's 32 and at the finger's minimum.
function SignOffAction({ transfer, myBranch, auth, onSignOff, onOpen = null }) {
  // Above the early return -- a hook after one breaks the build (79.15b).
  const { phoneLayout } = useTier();
  const atThisBranch = String(transfer.from_branch_id) === String(myBranch);
  const mayByRole = canSignOffProduction(auth);
  if (!mayByRole && !atThisBranch) return null;

  const blockedReason = !atThisBranch
    ? 'Signing off production is done by the branch making it.'
    : !mayByRole
      ? 'Signing off production needs a branch manager, or the switch on your account.'
      : null;

  return (
    <div style={{ borderTop: '1px solid #F0F0F0', padding: '8px 10px' }}>
      <CardButtonRow phoneLayout={phoneLayout} onOpen={onOpen}>
        <ActionButton
          onClick={onSignOff}
          blockedReason={blockedReason}
          reasonStyle={phoneLayout ? undefined : REASON_OWN_LINE}
          style={{
            ...(phoneLayout ? { width: '100%' } : { flex: 'none', padding: '0 16px' }),
            minHeight: 40, borderRadius: 8, border: 0,
            background: '#14503B', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}
        >
          Sign off a run
        </ActionButton>
      </CardButtonRow>
    </div>
  );
}

// #665, gate 665 D1-D3, Session 79.22b.4.4 -- EVERY ITEM, BY NAME, ON EVERY
// CARD. Colby: "the cards need to list every item and it's requested
// quantity." A card that said "2 items" sent the reader into the sheet to learn
// what they were, which is the defect. ALL of them, never "and N more": twelve
// lines on a phone is twelve lines (X2). The words come from lineProgressText()
// so the three card designs cannot say the same fact three ways.
// #698 (b), 79.22b.4.7.1 -- `transfer` lets an item the asking branch turned
// down read "not needed" rather than "refused" (Colby's ruling A).
function ItemLines({ lines, items, stage, status = null, shelfFor = null, transfer = null }) {
  const rows = [...(lines || [])].sort((a, b) => Number(a.id) - Number(b.id));
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 6, display: 'grid', gap: 2 }}>
      {rows.map(l => {
        const refused = l.line_status === 'refused';
        // #665 D15 -- on an approval card, the giving shelf beneath each item.
        const shelf = !refused && shelfFor ? shelfFor(l) : null;
        // #800 -- the item's own promise where it differs from the request's (B, C).
        const own = transfer ? itemPromise(l, transfer) : null;
        return (
          <div key={l.id || l.item_id} style={{ fontSize: 13, color: '#1A1A1A', overflowWrap: 'anywhere' }}>
            <span style={{ fontWeight: 600 }}>
              {itemLabel(items, l.item_id)}
            </span>
            <span style={{ color: refused ? '#8A2B22' : '#3A3A3A', fontWeight: refused ? 700 : 400 }}>
              {' · '}{lineProgressText(stage, l, { status, transfer })}
            </span>
            {own && (
              <span style={{ color: own.late ? '#8A3A0F' : '#3A3A3A', fontWeight: own.late ? 600 : 400 }}>
                {' · '}{own.text}
              </span>
            )}
            {shelf && <ShelfLine shelf={shelf} />}
          </div>
        );
      })}
    </div>
  );
}

// `stage` and `action` are #553, Session 79.15e.
//
// The card is a single <button>, so an action cannot be nested inside it — a
// button in a button is invalid and the inner click never arrives cleanly. The
// card is therefore wrapped and the action sits BESIDE it, sharing the border
// so it still reads as one object.
//
// `stage` decides which sub-line the card carries, because the same transfer
// means a different thing on each tab: on To make it is 60 still to build, on
// To load it is 40 standing ready. Before D1 there was no number that could
// tell those apart, which is why both tabs said the same thing.
// #608 / gate 675 D16 and F1-F4, Session 79.22b.5.3 -- FINISHED'S PAGES.
// Desk: "Showing 1–25 of 212", Rows per page 25 / 50 / 100, Previous and Next -
// the Ledger's control, Previous on the first page and Next on the last
// disabled as the Ledger has them. Phone and tablet: "Showing 25 of 212" and
// Show 25 more (Show 7 more when fewer remain) - the Catalog's. Module level,
// never inside the page (locked rule 2).
const FINISHED_PAGE_SIZES = [25, 50, 100];

// ── #675 / GATE 675 D12 and D14, Session 79.22b.5.4b -- FILTER & SORT ───────
//
// One button beside the search box, showing a number when anything is applied.
// It opens a sheet; a choice applies the moment it is tapped and the list and
// the live count update behind it, so filtering is two taps (D12, callout F).
// Applied filters show as named chips under the search, each removable, with
// Clear all - never a dot (Baymard; MaintainX's dot is the anti-pattern). On a
// phone and a tablet the branch control lives in the sheet too (D14). The desk
// gets its own row of buttons at 79.22b.5.5 (D13, moved there by R1); until then
// it uses this button and sheet.
function FilterSortButton({ count, coarse, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-haspopup="dialog"
      aria-label={count > 0 ? `${FILTER_BUTTON}, ${count} applied` : FILTER_BUTTON}
      style={{
        flexShrink: 0, height: coarse ? 44 : 40, padding: '0 12px', borderRadius: 8,
        border: count > 0 ? '1px solid #0F6E56' : '1px solid #D8D8D8',
        background: count > 0 ? '#F3FAF6' : 'white', color: count > 0 ? '#0F6E56' : '#3A3A3A',
        fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
      {FILTER_BUTTON}
      {count > 0 && (
        <span style={{ background: '#0F6E56', color: 'white', borderRadius: 999, padding: '1px 7px', fontSize: 11 }}>{count}</span>
      )}
    </button>
  );
}

// The chips: each applied filter by its own words, read aloud as "Remove
// filter: …", and the amber "Looking at Houston · you act at Dallas" on a phone
// or tablet, where the branch chip no longer sits above the tabs (D14).
function AppliedChips({ chips, spectator, coarse, onRemove, onClearAll, onStopLooking }) {
  if (!chips.length && !spectator) return null;
  const chip = (tone) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: controlHeight(coarse, 'min'),
    padding: '0 4px 0 11px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: tone === 'amber' ? '1px solid #F3D9A8' : '1px solid #CFE6DA',
    background: tone === 'amber' ? '#FFF8EC' : '#F3FAF6',
    color: tone === 'amber' ? '#8A5A08' : '#0F6E56',
  });
  const x = (
    <span aria-hidden="true" style={{ width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <IconX size={13} />
    </span>
  );
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 10px' }}>
      {spectator && (
        <button type="button" onClick={onStopLooking} style={chip('amber')} aria-label={`Remove filter: ${spectator}`}>
          {spectator}{x}
        </button>
      )}
      {chips.map(c => (
        <button key={c.key} type="button" onClick={() => onRemove(c.key)} style={chip('green')} aria-label={`Remove filter: ${c.words}`}>
          {c.words}{x}
        </button>
      ))}
      {chips.length > 0 && (
        <button type="button" onClick={onClearAll}
          style={{
            minHeight: controlHeight(coarse, 'min'), padding: '0 8px', border: 'none', background: 'transparent',
            color: '#0F6E56', fontSize: 13, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline',
          }}>
          {CLEAR_ALL}
        </button>
      )}
    </div>
  );
}

// F1/F2 -- "Nothing on {tab} matches these filters." with Clear filters. The
// search and the sort are left alone.
function FiltersEmpty({ tabLabel, coarse, onClear }) {
  return (
    <div role="status" style={{ padding: '1.5rem 1rem', textAlign: 'center', color: '#3A3A3A', fontSize: 13 }}>
      <div>{filtersEmptySentence(tabLabel)}</div>
      <button type="button" onClick={onClear}
        style={{
          marginTop: 10, minHeight: controlHeight(coarse, 'min'), padding: '0 14px', borderRadius: 8,
          border: '1px solid #CFE6DA', background: '#F3FAF6', color: '#0F6E56', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
        {CLEAR_FILTERS}
      </button>
    </div>
  );
}

// One group of choices in the sheet: one tap applies, the chosen one is marked.
function ChoiceGroup({ title, choices, value, onPick, coarse }) {
  return (
    <fieldset style={{ border: 'none', margin: '0 0 14px', padding: 0 }}>
      <legend style={{
        fontSize: 12, fontWeight: 700, color: '#5C5C5C', textTransform: 'uppercase',
        letterSpacing: '0.04em', padding: 0, marginBottom: 6,
      }}>
        {title}
      </legend>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {choices.map(c => {
          const on = c.on != null ? c.on : value === c.id;
          return c.disabled ? (
            <div key={String(c.id)} aria-disabled="true"
              style={{ padding: '6px 11px', borderRadius: 8, border: '1px dashed #D8D8D8', background: '#FAFAFA', fontSize: 13, color: '#5C5C5C' }}>
              {c.label}
              {c.note && <div style={{ fontSize: 12, marginTop: 2 }}>{c.note}</div>}
            </div>
          ) : (
            <button key={String(c.id)} type="button" aria-pressed={on} onClick={() => onPick(c.id)}
              style={{
                minHeight: controlHeight(coarse, 'min'), padding: '0 12px', borderRadius: 999, cursor: 'pointer',
                border: on ? '1px solid #0F6E56' : '1px solid #D8D8D8',
                background: on ? '#E6F4EE' : 'white', color: on ? '#0F6E56' : '#3A3A3A',
                fontSize: 13, fontWeight: on ? 700 : 500,
              }}>
              {c.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

// Pick dates…: two date boxes, From and To; either may be empty, and From after
// To swaps them (F3). Both empty is no filter at all.
function DatePair({ value, onChange }) {
  const v = value && typeof value === 'object' ? value : { since: null, until: null };
  const set = (k, x) => {
    const next = { ...v, [k]: x || null };
    onChange(next.since || next.until ? ordered(next) : null);
  };
  const box = {
    height: 40, fontSize: 16, padding: '0 8px', border: '1px solid #D8D8D8',
    borderRadius: 8, background: 'white', color: '#0F0F0F',
  };
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '-6px 0 14px' }}>
      <label style={{ fontSize: 13, color: '#3A3A3A', display: 'grid', gap: 3 }}>
        From
        <input type="date" value={v.since || ''} onChange={e => set('since', e.target.value)} style={box} />
      </label>
      <label style={{ fontSize: 13, color: '#3A3A3A', display: 'grid', gap: 3 }}>
        To
        <input type="date" value={v.until || ''} onChange={e => set('until', e.target.value)} style={box} />
      </label>
    </div>
  );
}

function FilterSortSheet({
  phone, coarse, count, tab, offered, filters, setFilter, sortId, onSort,
  branchSection = null, branches, branchNames, lookingAt, onClose,
}) {
  const [picking, setPicking] = useState({ due: false, when: false });
  const name = id => branchNames[String(id)] || 'that branch';
  const same = (a, b) => a != null && b != null && String(a) === String(b);
  const branchChoices = [{ id: null, label: ANY_BRANCH }, ...branches.map(b => ({ id: b.id, label: b.name }))];
  const ranged = key => filters[key] != null && typeof filters[key] === 'object';
  const pickable = (key, list) => [
    ...list.map(c => ({ ...c, on: !picking[key] && !ranged(key) && filters[key] === c.id })),
    { id: '__pick', label: PICK_DATES, on: picking[key] || ranged(key) },
  ];
  const pick = key => id => {
    if (id === '__pick') { setPicking(p => ({ ...p, [key]: true })); return; }
    setPicking(p => ({ ...p, [key]: false }));
    setFilter(key, id);
  };
  const showPick = key => picking[key] || ranged(key);
  return (
    <Overlay variant={phone ? 'sheet' : 'modal'} onClose={onClose} maxWidth={520}>
      <div role="dialog" aria-label={SHEET_TITLE} style={{ padding: '14px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
          <h2 style={{ fontFamily: 'var(--font-head)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 18, margin: 0 }}>
            {SHEET_TITLE}
          </h2>
          <span role="status" style={{ fontSize: 13, color: '#5C5C5C' }}>{count == null ? '…' : liveCount(count)}</span>
        </div>
        <ChoiceGroup title="Sort" coarse={coarse} value={sortId} onPick={onSort}
          choices={(TAB_SORTS[tab] || []).map(id => ({ id, label: SORT_LABELS[id] }))} />
        {branchSection}
        {offered.presets && lookingAt != null && (
          <ChoiceGroup title="Coming and going" coarse={coarse}
            onPick={id => (id === 'coming'
              ? setFilter('to', same(filters.to, lookingAt) ? null : lookingAt)
              : setFilter('from', same(filters.from, lookingAt) ? null : lookingAt))}
            choices={[
              { id: 'coming', label: `Coming to ${name(lookingAt)}`, on: same(filters.to, lookingAt) },
              { id: 'leaving', label: `Leaving ${name(lookingAt)}`, on: same(filters.from, lookingAt) },
            ]} />
        )}
        {offered.from && (
          <ChoiceGroup title="From" coarse={coarse} onPick={id => setFilter('from', id)}
            choices={branchChoices.map(c => ({ ...c, on: String(filters.from ?? '') === String(c.id ?? '') }))} />
        )}
        {offered.to && (
          <ChoiceGroup title="To" coarse={coarse} onPick={id => setFilter('to', id)}
            choices={branchChoices.map(c => ({ ...c, on: String(filters.to ?? '') === String(c.id ?? '') }))} />
        )}
        {offered.due && (
          <>
            <ChoiceGroup title="Due" coarse={coarse} onPick={pick('due')} choices={pickable('due', DUE_CHOICES)} />
            {showPick('due') && <DatePair value={filters.due} onChange={v => setFilter('due', v)} />}
          </>
        )}
        {offered.when && (
          <>
            <ChoiceGroup title="Finished" coarse={coarse} onPick={pick('when')} choices={pickable('when', WHEN_CHOICES)} />
            {showPick('when') && <DatePair value={filters.when} onChange={v => setFilter('when', v)} />}
          </>
        )}
        {offered.ended && (
          <ChoiceGroup title="How it ended" coarse={coarse} value={filters.ended} onPick={id => setFilter('ended', id)}
            choices={ENDED_CHOICES} />
        )}
        <div style={{ position: 'sticky', bottom: 0, background: 'white', padding: '10px 0 14px', borderTop: '1px solid #F0F0F0' }}>
          <button type="button" onClick={onClose}
            style={{
              width: '100%', minHeight: controlHeight(coarse, 'commit'), borderRadius: 10, border: 'none',
              background: '#0F6E56', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}>
            {count == null ? 'Close' : showButton(count)}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

// ── #675 / GATE 675 D13, Session 79.22b.5.5 -- THE DESK'S FILTER BUTTONS ─────
//
// What a person sees on a desk: under the search box, "Sort: Due soonest ▾ ·
// From ▾ · To ▾ · Due ▾" (on Finished: "Finished ▾ · How it ended ▾"). Each
// opens its menu right under it, the way the branch control's menu does: two
// clicks, no sheet. A choice applies and closes it; Esc or a click elsewhere
// closes it. A button showing a value is the applied filter, in the chip's own
// words ("Coming to Dallas"), and carries its own x (callout G).
//
// The same state, choices and words as the phone's sheet (D12): the page's
// `filters` and `sorts`, filtersOffered(), appliedChips(). Only the frame
// differs. Pick dates… opens the two date boxes inside the menu, which stays
// open until a click elsewhere, because a range takes two answers.
function DeskFilterBar({ tab, coarse, offered, applied, filters, setFilter, sortId, onSort, branches, branchNames, lookingAt }) {
  const [open, setOpen] = useState(null);       // 'sort' or a filter's key
  const [picking, setPicking] = useState(null); // the key whose date boxes show
  const barRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') setOpen(null); };
    const onDown = e => { if (barRef.current && !barRef.current.contains(e.target)) setOpen(null); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);
  // A menu belongs to the tab it was opened on.
  useEffect(() => { setOpen(null); setPicking(null); }, [tab]);

  const same = (a, b) => a != null && b != null && String(a) === String(b);
  const name = id => branchNames[String(id)] || 'that branch';
  const chips = appliedChips(applied, { branchNames, lookingAt, singleBranch: lookingAt != null });
  const wordsFor = key => chips.find(c => c.key === key)?.words || null;
  const ranged = key => filters[key] != null && typeof filters[key] === 'object';
  const height = controlHeight(coarse, 'min');

  const menuFor = (key) => {
    if (key === 'sort') {
      return (TAB_SORTS[tab] || []).map(id => ({ id, label: SORT_LABELS[id], on: sortId === id }));
    }
    if (key === 'from' || key === 'to') {
      // The two presets read back as the branch in view's own row: "Coming to
      // Dallas" is To set to Dallas, as the chip says it.
      const preset = offered.presets && lookingAt != null;
      return [
        { id: null, label: ANY_BRANCH, on: filters[key] == null },
        ...branches.map(b => ({
          id: b.id,
          label: preset && same(b.id, lookingAt) ? `${key === 'to' ? 'Coming to' : 'Leaving'} ${name(b.id)}` : b.name,
          on: same(filters[key], b.id),
        })),
      ];
    }
    if (key === 'due' || key === 'when') {
      const list = key === 'due' ? DUE_CHOICES : WHEN_CHOICES;
      return [
        ...list.map(c => ({ ...c, on: picking !== key && !ranged(key) && filters[key] === c.id })),
        { id: '__pick', label: PICK_DATES, on: picking === key || ranged(key) },
      ];
    }
    return ENDED_CHOICES.map(c => ({ ...c, on: filters.ended === c.id }));
  };

  const choose = (key, id) => {
    if (key === 'sort') { onSort(id); setOpen(null); return; }
    if (id === '__pick') { setPicking(key); return; }
    setPicking(null);
    setFilter(key, id);
    setOpen(null);
  };

  const button = (key, label, value) => {
    const isOpen = open === key;
    return (
      <div key={key} style={{ position: 'relative', display: 'inline-flex' }}>
        <button type="button" aria-haspopup="menu" aria-expanded={isOpen}
          onClick={() => setOpen(isOpen ? null : key)}
          style={{
            minHeight: height, padding: value ? '0 6px 0 11px' : '0 11px',
            borderRadius: value ? '999px 0 0 999px' : 999, cursor: 'pointer',
            // Longhand only: the right edge comes and goes with the x, and a
            // shorthand beside a changing longhand warns on every re-render (#224).
            borderTop: `1px solid ${value ? '#0F6E56' : '#D8D8D8'}`,
            borderBottom: `1px solid ${value ? '#0F6E56' : '#D8D8D8'}`,
            borderLeft: `1px solid ${value ? '#0F6E56' : '#D8D8D8'}`,
            borderRight: value ? '0 none transparent' : '1px solid #D8D8D8',
            background: value ? '#F3FAF6' : 'white', color: value ? '#0F6E56' : '#3A3A3A',
            fontSize: 13, fontWeight: value ? 700 : 600, whiteSpace: 'nowrap',
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
          {value || label}
          {!value && <span aria-hidden="true" style={{ fontSize: 9 }}>▼</span>}
        </button>
        {value && key !== 'sort' && (
          <button type="button" aria-label={`Remove filter: ${value}`} onClick={() => { setFilter(key, null); setPicking(null); }}
            style={{
              minHeight: height, width: 28, borderRadius: '0 999px 999px 0', cursor: 'pointer',
              border: '1px solid #0F6E56', borderLeft: 'none', background: '#F3FAF6', color: '#0F6E56',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
            }}>
            <IconX size={13} />
          </button>
        )}
        {isOpen && (
          <div role="menu" style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 100, background: 'white',
            border: '1px solid #E0E0E0', borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
            overflow: 'hidden', minWidth: 200,
          }}>
            {menuFor(key).map(c => (
              <button key={String(c.id)} type="button" role="menuitemradio" aria-checked={!!c.on}
                onClick={() => choose(key, c.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', minHeight: height,
                  fontSize: 14, fontWeight: c.on ? 700 : 400, color: c.on ? '#1D9E75' : '#0F0F0F',
                  background: c.on ? '#F0FFF4' : 'white', border: 'none', borderBottom: '1px solid #F0F0F0',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                {c.label}
              </button>
            ))}
            {(key === 'due' || key === 'when') && (picking === key || ranged(key)) && (
              <div style={{ padding: '14px 14px 0' }}>
                <DatePair value={filters[key]} onChange={v => setFilter(key, v)} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={barRef} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
      {button('sort', sortButtonLabel(SORT_LABELS[sortId]), null)}
      {deskFilterButtons(offered).map(key => button(key, DESK_FILTER_NAMES[key], wordsFor(key)))}
    </div>
  );
}

// ── #752, Session 79.22b.5.4b -- the group drawing A draws ──────────────────
//
// A short amber box at the top of Finished: "Waiting for Dallas to decide · 2",
// one card per transfer naming who refused, when, and what. A card opens the
// transfer, whose routing panel is the decision (#567); only the people who
// may decide see its buttons there, as today. Everybody looking at the branch
// sees the group, because it states whose decision it is and offers nothing
// but the card (R1).
export function waitingHeading(branchName, n) {
  return branchName ? `Waiting for ${branchName} to decide · ${n}` : `Waiting for a decision · ${n}`;
}
export const WAITING_FOOT = 'Each one leaves this list when somebody decides.';
// #675 D22 -- a refusal decided on the right keeps its row until the next
// choice, saying so; it leaves the list then, as the line above promises.
export const WAITING_DECIDED = 'Decided';
export const WAITING_CUT = 'More refusals may be waiting than are listed here.';
export const WAITING_FAILED = 'Couldn’t check for refusals waiting for a decision.';

// How many reads of 1,000 a search may make to find the waiting refusals among
// its matches before it stops and keeps the ones it did not reach (Grok).
const WAITING_SEARCH_PAGES = 10;

function WaitingForDecision({ rows, cut, failed, branchName, branchNames, items, coarse, onOpen, chosenId = null }) {
  const when = iso => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null);
  return (
    <section aria-label="Refusals waiting for a decision" style={{
      background: '#FFF8EC', border: '1px solid #F3D9A8', borderRadius: 8, padding: '9px 10px', marginBottom: 12,
    }}>
      {failed ? (
        <div role="status" style={{ fontSize: 13, color: '#8A5A08' }}>{WAITING_FAILED}</div>
      ) : (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#8A5A08', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {/* #675 D22 -- a row decided on the right keeps its place until the
                next choice, and is not counted as still waiting. */}
            {waitingHeading(branchName, rows.filter(t => !t.__pinned).length)}
          </div>
          {rows.map(t => {
            const refused = routableRefusedLines(t.branch_transfer_items, t);
            const refuser = branchNames[String(t.from_branch_id)] || 'The supplying branch';
            const day = when(refusedAtOf(t));
            const shown = refused.slice(0, 2).map(l => `${itemLabel(items, l.item_id)} · ${l.qty_requested}`);
            const more = refused.length - shown.length;
            const chosen = isChosenCard(chosenId, t.id);
            const decided = !!t.__pinned;
            return (
              <button key={t.id} type="button" onClick={() => onOpen(t)}
                aria-current={chosen ? 'true' : undefined}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginTop: 6, padding: '8px 10px',
                  minHeight: controlHeight(coarse, 'min'), background: 'white', border: '1px solid #F3D9A8',
                  borderRadius: 6, cursor: 'pointer', color: '#0F0F0F', fontSize: 13, lineHeight: 1.4,
                  ...(chosen ? CHOSEN_CARD : null),
                }}>
                <div style={{ fontWeight: 600 }}>{transferLabel(t, branchNames)}</div>
                <div style={{ color: '#8A5A08' }}>{day ? `${refuser} refused · ${day}` : `${refuser} refused`}</div>
                <div>{shown.join(' · ')}{more > 0 ? ` · ${more} more` : ''}</div>
                {decided && <div style={{ color: '#14503B', fontWeight: 700 }}>{WAITING_DECIDED}</div>}
              </button>
            );
          })}
          <div style={{ fontSize: 12, color: '#8A5A08', marginTop: 6 }}>{WAITING_FOOT}</div>
          {cut && <div style={{ fontSize: 12, color: '#8A5A08', marginTop: 2 }}>{WAITING_CUT}</div>}
        </>
      )}
    </section>
  );
}

function FinishedPager({ finished, at, onDesk, coarse, onPage, onRetry }) {
  if (finished.state === 'failed') {
    return (
      <div role="alert" style={{ padding: '12px 0', fontSize: 13, color: '#8A1F1F', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        Couldn't load finished transfers.
        <button onClick={onRetry} style={{ minHeight: controlHeight(coarse, 'min'), padding: '0 12px', border: '1px solid #D0D0D0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Try again
        </button>
      </div>
    );
  }
  const rows = finished.rows.length;
  const total = finished.total;
  if (finished.state === 'loading' && rows === 0) {
    return <div style={{ color: '#5C5C5C', fontSize: 13, padding: '8px 0' }}>Loading…</div>;
  }
  if (total === 0) return null;
  const loadingNote = finished.state === 'loading'
    ? <span style={{ color: 'var(--ink-3)' }}> · Loading…</span> : null;

  if (!onDesk) {
    const left = Math.max(total - rows, 0);
    const more = Math.min(25, left);
    return (
      <div style={{ padding: '12px 0', display: 'grid', gap: 8, justifyItems: 'center' }}>
        <div style={{ fontSize: 13, color: '#5C5C5C' }}>Showing {rows} of {total}{loadingNote}</div>
        {more > 0 && (
          <button
            onClick={() => onPage(p => ({ offset: 0, size: Math.min(p.size + 25, 1000) }))}
            disabled={finished.state === 'loading'}
            style={{ width: '100%', minHeight: controlHeight(coarse, 'commit'), border: '1px solid #D0D0D0', borderRadius: 10, background: 'white', fontSize: 15, fontWeight: 700, color: '#0F0F0F', cursor: 'pointer' }}>
            Show {more} more
          </button>
        )}
      </div>
    );
  }

  const first = at.offset + 1;
  const last = Math.min(at.offset + rows, total);
  const onFirst = at.offset === 0;
  const onLast = at.offset + at.size >= total;
  const btn = disabled => ({
    minHeight: controlHeight(coarse, 'min'), padding: '0 12px', border: '1px solid #D0D0D0', borderRadius: 8,
    background: 'white', fontSize: 13, fontWeight: 600, color: disabled ? '#9A9A9A' : '#0F0F0F',
    cursor: disabled ? 'default' : 'pointer',
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '12px 0' }}>
      <div style={{ fontSize: 13, color: '#5C5C5C' }}>Showing {first}–{last} of {total}{loadingNote}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#5C5C5C' }}>
          Rows per page
          <select
            value={at.size}
            onChange={e => onPage({ offset: 0, size: Number(e.target.value) })}
            style={{ minHeight: controlHeight(coarse, 'min'), border: '1px solid #D0D0D0', borderRadius: 8, padding: '0 8px', fontSize: 16, fontWeight: 600, background: 'white', cursor: 'pointer' }}>
            {FINISHED_PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button disabled={onFirst} style={btn(onFirst)}
          onClick={() => onPage(p => ({ ...p, offset: Math.max(p.offset - p.size, 0) }))}>
          Previous
        </button>
        <button disabled={onLast} style={btn(onLast)}
          onClick={() => onPage(p => ({ ...p, offset: p.offset + p.size }))}>
          Next
        </button>
      </div>
    </div>
  );
}

// #675 D3 -- `matched`: "Matched: truck HOU-TRF-00036-2", when a search matched
// something this card does not print. A search must never find a record the
// record cannot confirm (#545).
function MatchedLine({ text }) {
  if (!text) return null;
  return <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: '#0F6E56' }}>{text}</div>;
}

// #675 D21, Session 79.22b.5.5 -- `chosenId`: the transfer open on the desk's
// right-hand side; its card is tinted so the reader always knows which card the
// right side belongs to (callout L). The same prop on every card kind below.
function TransferCard({ transfer, branchNames, items = {}, myBranch, onOpen, stage = null, action = null, own = false, askerName = null, shelfNote = null, shelfFor = null, linesInAction = false, heading = null, matched = null, chosenId = null }) {
  const lines = transfer.branch_transfer_items || [];
  const chosen = isChosenCard(chosenId, transfer.id);
  // #665 D6/D8, Session 79.22b.4.5 -- ON THE TWO JOB TABS THE HEADING IS THE
  // JOB: "Make for Dallas", "Load for Dallas" (callout A). The number and its
  // direction ride above it in small capitals, so the number still never
  // appears alone (371 D9/D20).
  // #697 -- `heading`: the hub's card for a pickup says whose job it is
  // instead ("Dallas collects this here", gate 665 D23).
  const jobHeading = heading ? heading
    : stage === 'to_make' ? makeCardHeading(transfer, branchNames)
    : stage === 'to_load' ? loadCardHeading(transfer, branchNames)
      : null;
  const arriving = inTransitQty(lines);
  const incoming = String(transfer.to_branch_id) === String(myBranch);
  const days = ageInDays(transfer.requested_at);
  const make = makeProgress(lines);
  // #665 -- the whole-transfer line only earns its place above two or more
  // items. On a one-item card it would repeat the item's own line word for
  // word, and gate 665 draws its one-item cards without it (Screens 1 and 2).
  // Counted over the items still in play: a refused item is outside every
  // total (#577), so two items with one refused is a one-item card -- found on
  // screen at 79.22b.4.4, where SAT-TRF-00007 printed its only live line twice.
  const several = lines.filter(l => l.line_status !== 'refused').length > 1;

  // ** #579, Session 79.21b -- NOTHING IS ARRIVING AND NOTHING IS DUE ON A
  // TRANSFER THAT IS OVER. **
  //
  // The Finished list said "2 arriving" in green on a transfer closed short --
  // those two are the one thing on it that is certainly NOT arriving -- and
  // "4d overdue" in red on work that is done. Those are the two signals this
  // list uses to mean LOOK AT THIS, and both were pointed at transfers that
  // need nothing, which sends a manager's attention exactly the wrong way.
  //
  // This card is shared by every tab and neither sub-line ever asked whether
  // the transfer had ended. `arriving` is shipped minus received, which is true
  // of goods on a truck and equally true of goods written off -- the exact
  // distinction #531 drew for the OPEN transfer, in a comment in this same
  // file, which this card never learned.
  const { finished, neverArrived } = endedCardSignals(transfer, arriving);
  const dueDays = finished ? null : leadTimeDays(transfer.needed_by);
  // #800 -- the card's "Promised" line, true on its own (transferCardJobs.js).
  const promised = promisedLine(transfer);

  return (
    <div style={{
      background: '#fff', border: '1px solid #E9E9E9', borderRadius: 10,
      borderLeft: '3px solid #1D9E75', overflow: 'hidden',
      ...(chosen ? CHOSEN_CARD : null),
    }}>
    <button
      onClick={onOpen}
      aria-current={chosen ? 'true' : undefined}
      style={{
        textAlign: 'left', width: '100%', cursor: 'pointer',
        background: 'transparent', border: 0, padding: '11px 13px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        {/* D9 and D20 — the number NEVER renders alone. It names only the branch
            it left, so on a receiving branch's screen it would read as somebody
            else's document. Colby's condition on approving the sender's prefix. */}
        <div style={jobHeading
          ? {
            fontFamily: 'var(--font-head)', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)',
          }
          : { fontWeight: 700, fontSize: 14, color: '#0F0F0F' }}>
          {transferLabel(transfer, branchNames)}
        </div>
        <div style={{ fontSize: 11, color: '#7A7A7A', whiteSpace: 'nowrap' }}>
          {/* #608 / gate 675 D16 -- on Finished, the day it finished: the list
              is in that order, so the date says why a card sits where it does. */}
          {stage === 'finished' && transfer.__endedAt
            ? finishedDay(transfer.__endedAt)
            : days === 0 ? 'today' : `${days}d`}
        </div>
      </div>
      {jobHeading && (
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginTop: 2 }}>
          {jobHeading}
        </div>
      )}
      <div style={{ marginTop: 5, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {stage === 'to_make' && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
            background: own ? '#EAF5F0' : '#F4F4F4', color: own ? '#14503B' : '#3A3A3A',
            border: own ? '1px solid #CFE6DA' : '1px solid #E4E4E4',
          }}>
            {own ? 'For our shelf' : `For ${branchNames[String(transfer.to_branch_id)] || 'another branch'}`}
          </span>
        )}
        {/* 553 F2's stage words for a request made for our own shelf: it never
            travels, so it is not "Received" -- it is on the shelf. */}
        <Chip>{isOwnShelf(transfer) && transfer.status === 'received'
          ? 'On the shelf'
          : (STATUS_TEXT[transfer.status] || transfer.status)}</Chip>
        <Chip>{MODE_LABEL[transfer.fulfilment_mode] || transfer.fulfilment_mode}</Chip>
        {/* #665 D1 -- "2 items" is gone: the items themselves are listed below. */}
        {/* #579 -- the loss, or nothing. Never "arriving" on a finished transfer. */}
        {finished ? (neverArrived > 0 && (
          <span style={{ fontSize: 12, color: '#A32D2D', fontWeight: 700 }}>
            {neverArrived} never arrived
          </span>
        )) : arriving > 0 && (
          <span style={{ fontSize: 12, color: '#1D9E75', fontWeight: 600 }}>
            {incoming ? `${arriving} arriving` : `${arriving} out`}
          </span>
        )}
      </div>

      {/* THE DATES, ON THE CARD. Found by Colby's certification run: the To make
          queue showed a number, a status and an item count — and NO DATE AT ALL,
          on a screen whose only job is deciding what to build first.
          A queue you cannot sequence sends the person who runs the floor to the
          phone to ask what is really needed when, which is the exact call this
          build exists to delete.
          Both dates, because they answer different questions: needed_by is what
          the other branch asked for, confirmed_date is what this branch already
          promised — and a run that is already promised for Friday is not the
          same job as one nobody has committed to. */}
      {(transfer.needed_by || promised) && (
        <div style={{ marginTop: 5, display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11 }}>
          {transfer.needed_by && (
            <span style={{ color: dueDays != null && dueDays < 0 ? '#A32D2D' : '#5C5C5C' }}>
              Needed <strong>{transfer.needed_by}</strong>
              {/* #579 -- `dueDays` is null on a finished transfer, so the
                  countdown and its red disappear with it. */}
              {dueDays != null && (
                dueDays < 0 ? ` · ${Math.abs(dueDays)}d overdue`
                  : dueDays === 0 ? ' · today'
                  : ` · in ${dueDays}d`
              )}
            </span>
          )}
          {promised && (
            // #579 -- the green here means "promised, on track". On a finished
            // transfer there is nothing left to be on track for, so the date
            // stays as a plain fact and the colour goes.
            // #800 -- the range when the items' dates differ, amber when some
            // come after the needed date (approved drawing, callout A).
            <span style={finished
              ? { color: '#5C5C5C' }
              : promised.late
                ? { color: '#8A3A0F', fontWeight: 600 }
                : { color: '#14503B', fontWeight: 600 }}>
              {promised.text}
            </span>
          )}
        </div>
      )}

      {/* WHAT THIS TRANSFER MEANS ON THIS TAB — #553 D2/D3. Gate F2's strings.
          A transfer can honestly carry both lines on two different tabs at the
          same time: 60 still to make here, 40 ready to load there. */}
      {stage === 'to_make' && make.toMake > 0 && (
        <div style={{ marginTop: 7 }}>
          {/* #665 D3 -- the whole transfer in the same words as each item,
              beside the green bar that stays exactly as it was (D2). */}
          {several && (
            <div style={{ fontSize: 12, color: '#14503B', fontWeight: 700 }}>
              {progressText('to_make', make)}
            </div>
          )}
          {/* 553 callout Y -- the row says why it exists: "asked by Ben Ortiz
              with 6 on the shelf". The count is the note's first line, which
              request_own_shelf_make() writes and nothing else does. */}
          {own && (() => {
            const m = /^Asked with (-?[\d.]+) on the shelf/.exec(transfer.notes || '');
            const who = askerName || 'someone';
            return (
              <div style={{ fontSize: 12, color: '#3A3A3A', marginTop: 2 }}>
                {m ? `asked by ${who} with ${Number(m[1])} on the shelf` : `asked by ${who}`}
              </div>
            );
          })()}
          {/* The bar is the same fact as the sentence, for somebody scanning a
              column of rows rather than reading one. `requested` is > 0 on any
              real line — qty_requested carries a CHECK for it — but a width of
              NaN silently renders as zero-width, so it is guarded rather than
              assumed. */}
          <div style={{ marginTop: 4, height: 4, borderRadius: 2, background: '#E9E9E9', overflow: 'hidden' }}>
            <div style={{
              height: '100%', background: '#1D9E75',
              width: `${make.requested > 0 ? Math.round((make.ready / make.requested) * 100) : 0}%`,
            }} />
          </div>
        </div>
      )}
      {stage === 'to_load' && (
        <div style={{ marginTop: 7, fontSize: 12 }}>
          {/* #628 half 2 -- a row listed because the shelf can fill it says
              so, in the approved words, rather than "0 ready". */}
          {shelfNote && make.readyToLoad === 0 ? (
            <span style={{ color: '#14503B', fontWeight: 700 }}>Nothing made · {shelfNote}</span>
          ) : (
            several && <span style={{ color: '#14503B', fontWeight: 700 }}>{progressText('to_load', make)}</span>
          )}
        </div>
      )}

      {/* #665 -- a job card lists its items WITH their boxes, in the action
          below; listing them here too would print every item twice. */}
      {/* #698 finding 5, Session 79.22b.4.7.1 -- ON AWAITING CONFIRMATION THE
          SUPPLYING BRANCH IS BEING ASKED, so an item the asking branch turned
          down is not listed (Colby's ruling A). Every other tab is a record of
          the transfer, and there it stays, reading "not needed". */}
      {!linesInAction && (
        <ItemLines lines={stage === 'confirm' ? linesPutToSupplier(lines, transfer) : lines}
          items={items} stage={stage} status={transfer.status} shelfFor={shelfFor} transfer={transfer} />
      )}
      <MatchedLine text={matched} />
    </button>
    {action}
    </div>
  );
}

// #531, Session 79.12 — A REQUEST CARD IN A TAB FULL OF TRUCK CARDS.
//
// The three load-shaped tabs draw a LOAD: a reference, a place, a promised
// date. These rows have no load at all, which is the whole defect — so drawing
// them as a truck would be a lie about what they are. They lead with the
// transfer and say WHAT IS STILL OWED, which is Colby's own condition on the
// fix. The precedent is To make, which already mixes an order card and a
// reserve card in one list and tells them apart with a chip.
//
// TWO COLOURS, AND THEY MAP TO THE TWO CLOSES RATHER THAN TO SEVERITY.
// Amber is your move: everything Houston said it sent has stopped moving and
// some of it is not here, so somebody closes it short — which reports the
// dollar value and pages both branches' managers. Grey is somebody else's
// move: Houston still owes the rest, still sees it in To load, and nothing has
// been lost. #500's rule is that an amber card every day is a card nobody
// reads, and that is exactly why the waiting case is NOT amber.
//
// THE STATUS CHIP IS DELIBERATELY ABSENT. Both kinds are still `in_transit` in
// the database, so STATUS_TEXT would render "On its way" on a transfer where
// nothing is on its way. The status is right and it is the wrong thing to show
// here, so the card carries a plain sentence with the two numbers in it.
//
// NO VALUE ANYWHERE ON THIS CARD — D65, same as LoadCard. A team member reads
// what is missing, never what it was worth.
function OwedCard({ row, branchNames, items = {}, onOpen, matched = null, chosenId = null }) {
  const { transfer, kind, progress, missing } = row;
  const short = kind === 'short';
  const sender = branchNames[String(transfer.from_branch_id)] || 'the other branch';
  const days = ageInDays(transfer.requested_at);
  const chosen = isChosenCard(chosenId, transfer.id);

  return (
    <button
      onClick={onOpen}
      aria-current={chosen ? 'true' : undefined}
      style={{
        textAlign: 'left', width: '100%', cursor: 'pointer', minHeight: 44,
        background: '#fff', border: '1px solid #E9E9E9', borderRadius: 10,
        borderLeft: `3px solid ${short ? '#EF9F27' : '#9A9A9A'}`, padding: '11px 13px',
        ...(chosen ? CHOSEN_CARD : null),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        {/* D9 and D20 — the number never renders alone, because it names only
            the branch it left and this is the RECEIVING branch's screen. */}
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0F0F0F' }}>
          {transferLabel(transfer, branchNames)}
        </div>
        <div style={{ fontSize: 11, color: '#7A7A7A', whiteSpace: 'nowrap' }}>
          {days === 0 ? 'today' : `${days}d`}
        </div>
      </div>

      <div style={{ marginTop: 5, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* "Short 2" rather than "Discrepancy" — Colby's call at 79.12. It
            names the number somebody would say out loud, and it uses the same
            word as the button they are about to press. */}
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
          background: short ? '#FAEEDA' : '#F1EFE8',
          color: short ? '#412402' : '#2C2C2A',
        }}>
          {short ? `Short ${missing}` : `Waiting on ${sender}`}
        </span>
        <Chip>{MODE_LABEL[transfer.fulfilment_mode] || transfer.fulfilment_mode}</Chip>
      </div>

      {/* #665 D1/D3 -- each item and how many of it are counted in. */}
      <ItemLines lines={transfer.branch_transfer_items} items={items} stage="owed" transfer={transfer} />

      {/* THE TWO NUMBERS, IN A SENTENCE. A card reading "2 missing" leaves the
          reader working out 2 of what; both figures together are what makes it
          answerable without opening anything. */}
      <div style={{ marginTop: 6, fontSize: 13, color: '#3A3A3A' }}>
        {short ? (
          <>
            <strong>{missing} of {progress.shipped} never arrived.</strong>{' '}
            Nothing more is on the way.
          </>
        ) : progress.shipped === 0 ? (
          <>
            <strong>Nothing has been sent yet.</strong>{' '}
            {sender} has {missing} to send.
          </>
        ) : (
          <>
            {/* #665 -- a one-item card already says this on the item's line. */}
            {(transfer.branch_transfer_items || []).filter(l => l.line_status !== 'refused').length > 1 && (
              <><strong>{progress.received} of {progress.requested} counted in.</strong>{' '}</>
            )}
            {sender} still has {missing} to send.
          </>
        )}
      </div>

      <div style={{ marginTop: 5, fontSize: 11, color: '#5C5C5C' }}>
        {/* #800 -- the range when the items' dates differ (approved drawing, A),
            so Dallas never reads the request's date for an item promised later. */}
        {short
          ? 'Open it to close it short, or to chase what is missing.'
          : promisedLine(transfer)?.text || 'No date promised yet.'}
      </div>
      <MatchedLine text={matched} />
    </button>
  );
}

// Colby, Session 79.6: "does the send it button need to be that wide? it kind of
// looks funny." It did, and the reason is worth writing down rather than just
// fixing, because the pattern I copied is correct — somewhere else.
//
// This app DOES use full-width buttons inside modals, and they look right there:
// PurchasingQueuePage's confirm sheets are 380–460px wide, so a full-width button
// is a normal-looking button. This detail is 640px because the item table needs
// the room, and a 640px-wide "Send it" is not a button, it is a bar.
//
// So the rule is about the CONTAINER, not the control: full width on a phone,
// where it is a thumb target and the screen is that wide anyway; natural width
// and right-aligned on desktop, which is where InvoicingPage's own footer puts
// its content and what every system in the gate's research does with a dialog's
// primary action. The stacking order also flips, so the primary action sits on
// the right on desktop and on TOP on a phone — nearest the thumb either way.
// SCREEN 5's card -- #517, gate D66.
//
// WHAT A DRIVER ACTUALLY NEEDS TO KNOW, in the order they need it: which load,
// where it came from, what is on it, and when it was promised. Nothing else.
//
// THE PROMISED DATE IS THE HEADLINE because it is what decides the running
// order, and a card whose most important fact is in small grey text at the
// bottom has buried it. A load nobody promised says so rather than showing an
// empty space that reads like a loading state.
//
// NO VALUE ANYWHERE ON THIS CARD -- D65. A team member drives, and they never
// see what the material is worth. That is not enforced here by choice: the
// panel that COULD show a figure gates it on canSeeLoadValue(), and this card
// simply has no money on it at all, which is the stronger version.
//
// ** `stop` IS WHY THIS CARD DOES NOT LIE AFTER A PARTIAL PICKUP. ** #529: a
// driver who could only fit 10 of 25 leaves 15 at Austin, and the load record
// still says 25 because 25 is what Houston sent. A card built from the load
// would send the next driver to Austin expecting 25 -- so on the collect tab it
// is built from what is STANDING there instead, and it names the yard he has to
// drive to rather than the branch the material came from three days ago.
// #661, Session 79.22b.1 -- `stage` WAS PASSED HERE AND NEVER READ.
// The call site has handed this component its tab since the three load tabs
// were built, with a comment saying the card carries its stage colour "so a
// driver holding a phone sees orange for a truck to fill and blue for one to
// fetch without reading a word". The prop was wired and the signature never
// took it, so every card on all three tabs drew the same green.
//
// THE LEFT EDGE IS THE STAGE. Lateness moves to the due-date, which already
// had its own amber treatment and now reads as a chip -- two facts, two
// places, rather than one stripe trying to carry both.
//
// #630 -- `action` is the row beneath the card: one quantity box per item and
// the button. It sits OUTSIDE the card button for the same reason TransferCard's
// does -- a button inside a button is invalid and the inner press never arrives
// cleanly.
// #697 -- `subline`: a pickup at a hub of the hub's OWN goods says "Austin's own
// shelf · 6 in total" under its heading (gate 665 Screen 10), in place of "Sent
// by" and the total underneath, because nothing was sent - it never left the rack.
// #675 D3 / callout C -- `matchedTruck`: this card IS the truck a search matched,
// so its number, which the card prints, is highlighted as well.
function LoadCard({ load, branchNames, items, isMobile, onOpen, stop = null, stage = null, action = null, pinned = false, subline = null, matched = null, matchedTruck = false, chosenId = null }) {
  const chosen = isChosenCard(chosenId, load.transfer_id);
  const rows = stop
    ? stop.rows.map(r => ({ item_id: r.item_id, qty_loaded: r.available, inventory_items: { name: r.name } }))
    : (load.branch_transfer_load_items || []);
  const total = stop
    ? stop.total
    : rows.reduce((n, r) => n + (Number(r.qty_loaded) || 0), 0);
  const promised = load.__promisedDate;
  // #629 -- a spot on the road is named as the driver typed it. It read "a
  // yard", which sent the person fetching it nowhere in particular.
  const from = stop
    ? `Pick up at ${stop.place || branchNames[String(stop.branchId)] || 'a yard'}`
    // #726 -- a leg that began on the road names the road place, as the
    // history does (legEnds, D10), rather than "another branch".
    : `From ${legEnds(load, branchNames).from || 'another branch'}`;

  // Late is worth colouring; "soon" is not. An amber card every day is a card
  // nobody reads, which is #500's lesson about the bell pointed at a list.
  const overdue = promised && new Date(promised) < new Date(new Date().toDateString());

  const edge = stage?.color || '#1D9E75';

  return (
    <div style={{
      border: '1px solid #E4E4E4', borderLeft: `3px solid ${edge}`,
      background: '#fff', borderRadius: 10, overflow: 'hidden',
      ...(chosen ? CHOSEN_CARD : null),
    }}>
    <button
      onClick={onOpen}
      aria-current={chosen ? 'true' : undefined}
      style={{
        textAlign: 'left', cursor: 'pointer', width: '100%', minHeight: 44,
        border: 0, background: 'none', borderRadius: 0, padding: '12px 14px',
        display: 'block',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          {/* The grey stays written as it was, so #417 D12's ratchet still
              counts it: it is still on every card a search did not match. */}
          <div style={{
            fontFamily: 'var(--font-head)', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
            ...(matchedTruck
              ? { color: '#0F6E56', background: '#E6F4EE', borderRadius: 4, padding: '0 4px', display: 'inline-block' }
              : { color: '#8A8A8A' }),
          }}>
            {load.load_ref}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', marginTop: 2 }}>
            {from}
          </div>
          {(subline || stop) && (
            <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 2 }}>
              {subline || `Sent by ${branchNames[String(load.from_branch_id)] || 'another branch'}`}
            </div>
          )}
        </div>
        {/* #661 -- the edge now carries the STAGE, so lateness carries itself.
            The pair is the one already used for every amber notice on these
            surfaces (#8A5A10 on #FDF3E3), rather than a new colour. */}
        <div style={{
          fontSize: 12, color: overdue ? '#8A5A10' : '#5C5C5C',
          textAlign: 'right', whiteSpace: 'nowrap',
          ...(overdue ? {
            background: '#FDF3E3', borderRadius: 999, padding: '2px 8px', fontWeight: 700,
          } : null),
        }}>
          {promised
            ? <>{overdue ? 'Was due ' : 'Due '}<strong>{promised}</strong></>
            : 'No date promised'}
        </div>
      </div>

      {/* #630 -- WITH AN ACTION, THE LINES MOVE INTO IT. The action row names
          every item beside its own box, so listing them here as well would
          print the same four lines twice and push the button off a phone. */}
      {!action && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* #665 D1 -- every item on the truck; the "and N more" cut-off is
              gone, because the item it hid is the one somebody is looking for. */}
          {rows.map(r => (
            <div key={r.id || r.item_id} style={{ fontSize: 13, color: '#3A3A3A' }}>
              <strong>{r.qty_loaded}</strong>{' '}
              {r.inventory_items?.name || items[String(r.item_id)]?.name || `Item ${r.item_id}`}
            </div>
          ))}
        </div>
      )}

      {/* #694 -- a card kept in place after its pile went says where it stood
          and lets its answer speak; "0 in total" would read as a pile of nothing. */}
      {!pinned && !subline && (
        <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 8 }}>
          {total} in total
          {load.driver_name ? ` · ${load.driver_name}` : ''}
          {load.truck ? ` · ${load.truck}` : ''}
        </div>
      )}
      <MatchedLine text={matched} />
    </button>
    {action}
    </div>
  );
}

// ── #665, gate 665 D6-D13, Session 79.22b.4.5 -- THE TWO JOB CARDS ACT ──────
//
// The row beneath a To make or To load card: a box per item and ONE button,
// drawn by the shared LoadCardAction (src/components/inventory/, moved there
// from this file at 79.22b.4.5 with #630's reasoning intact). What each card
// says is src/lib/transferCardJobs.js, word for word the gate's copy table.
// Module level, never inside the page -- locked rule 2.

// TO MAKE. Who gets the boxes:
//   - somebody at the branch making it: a box per item and the button, which
//     answers with its reason if they may not sign off (Screen 1, third phone);
//   - an admin or executive seeing every branch, or a manager elsewhere:
//     today's "Sign off a run", refusing with "done by the branch making it";
//   - somebody LOOKING at another branch: no action row -- looking is reading
//     (gate 665 F1).
function MakeCardJob({ transfer, items, branchNames, myBranch, auth, busy, lookingElsewhere, pinned, note, onUndo, onOpen, onSignOff, onSubmit }) {
  if (pinned) return <CardNoteOnly note={note} onUndo={onUndo} busy={busy} onOpen={onOpen} />;
  const atThisBranch = String(transfer.from_branch_id) === String(myBranch);
  if (!atThisBranch) {
    return lookingElsewhere ? null
      : <SignOffAction transfer={transfer} myBranch={myBranch} auth={auth} onSignOff={onSignOff} onOpen={onOpen} />;
  }
  const mayByRole = canSignOffProduction(auth);
  const cardLines = makeCardLines(transfer, items);
  return (
    <LoadCardAction
      // After a press the transfer is read again, and the boxes re-fill with
      // what is STILL to make -- Screen 1b's 50 and 100 -- because a new key
      // remounts them on the database's numbers instead of what was typed.
      key={`${transfer.id}:${cardLines.map(l => l.max).join(',')}`}
      busy={busy}
      accent="#14503B"
      allowOver
      lines={cardLines}
      guard={(values, total) => {
        if (!mayByRole) return NEEDS_THE_SWITCH;
        const over = cardLines.find(l => !l.noBox && values[String(l.item_id)] > l.max);
        if (over) return overMakeCeiling(over);
        return total > 0 ? null : SAY_HOW_MANY_MADE;
      }}
      label={(total) => signOffButtonLabel(total, transfer, branchNames)}
      footer={() => signOffFooter(transfer, branchNames)}
      note={note}
      onUndo={onUndo}
      onOpen={onOpen}
      onSubmit={(values) => onSubmit(cardLines, values)}
    />
  );
}

// TO LOAD. Only at the branch the goods stand at, with something made and
// ready -- the sheet's own readyAtOrigin rule (gate 665 B4). The shelf-fill
// row (#628 half 2) keeps no action: how a line is filled is the sheet's call
// (D13), and so are a handover on the road, another driver and a truck number.
function LoadCardJob({ transfer, items, branchNames, myBranch, auth, busy, hubs, drivers, loaderModel, ready, openLoads, pinned, note, onUndo, onOpen, onSubmit }) {
  // The route choice is for THIS load only and starts on the hub (D9). Above
  // every return, because a hook after one breaks the build (79.15b).
  const [via, setVia] = useState(true);
  useEffect(() => { if (note?.kind === 'done') setVia(true); }, [note]);

  // #694 -- the last ready item sent leaves the card pinned with its answer,
  // and that answer's Undo is the one control left on it.
  if (pinned) return <CardNoteOnly note={note} onUndo={onUndo} busy={busy} onOpen={onOpen} />;
  const lines = transfer.branch_transfer_items || [];
  if (String(transfer.from_branch_id) !== String(myBranch) || !hasReadyToLoad(transfer, lines)) return null;

  const cardLines = loadCardLines(transfer, items);
  // ONE route rule and ONE driver rule, shared with the load sheet, so the two
  // cannot disagree (gate 665 F5, locked rule 14).
  const hub = defaultRoute(transfer, hubs);
  const route = routeWords({ transfer, hub, branchNames });
  const goingVia = route.choice && via;
  const pick = driverDefault({ currentUserId: auth?.user?.id, drivers, loaderModel });
  // The database's own next number, from the loads the page already holds
  // (D12); "…" until they are read, and Send waits with its reason (#671).
  const ref = ready ? loadRef(transfer.transfer_number, nextLoadSeq(openLoads, transfer.id)) : null;
  const facts = loadFactsLine({
    route,
    driverText: driverFact({ loaderMode: loaderNamesNobody(loaderModel), pick, currentUserId: auth?.user?.id }),
    ref,
  });

  return (
    <LoadCardAction
      key={`${transfer.id}:${cardLines.map(l => l.max).join(',')}`}
      busy={busy}
      accent="#9A4E09"
      allowOver
      lines={cardLines}
      above={(
        <>
          {route.choice && (
            <RouteChoice via={via} onChange={setVia} viaLabel={route.viaLabel} straightLabel={route.straightLabel} />
          )}
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: route.choice ? 8 : 0, lineHeight: 1.4 }}>
            {facts}
          </div>
        </>
      )}
      guard={(values, total) => {
        if (!ready) return CHECKING_TRUCKS;
        const over = cardLines.find(l => !l.noBox && values[String(l.item_id)] > l.max);
        if (over) return overLoadCeiling(over);
        return total > 0 ? null : TYPE_HOW_MANY_LOAD;
      }}
      label={(total) => sendButtonLabel(total, route, goingVia)}
      footer={(total) => loadCardFooter(transfer, total)}
      note={note}
      onUndo={onUndo}
      onOpen={onOpen}
      onSubmit={(values) => onSubmit({
        cardLines,
        toBranchId: goingVia ? Number(hub.id) : null,
        place: goingVia ? route.hubName : route.destName,
        pick,
      }, values)}
    />
  );
}

// THE TWO-PART ROUTE CHOICE, callout F. The hub is already chosen; straight
// through is one tap, and the button's words follow it. Side by side at every
// width, each half at least 44px tall, the chosen half announced as pressed.
// #1F6FBF on #EEF5FC measures about 4.9, clear of the 4.5 a line of text needs.
function RouteChoice({ via, onChange, viaLabel, straightLabel }) {
  const { coarse } = useTier();
  const half = (on) => ({
    flex: '1 1 0', minWidth: 0, minHeight: Math.max(44, controlHeight(coarse, 'min')),
    padding: '6px 8px', fontSize: 13, fontWeight: on ? 700 : 600, lineHeight: 1.25,
    cursor: 'pointer', borderRadius: 8, border: `1px solid ${on ? '#CFE2F5' : '#E0E0E0'}`,
    background: on ? '#EEF5FC' : '#fff', color: on ? '#1F6FBF' : '#3A3A3A',
  });
  return (
    <div role="group" aria-label="Where this load goes" style={{ display: 'flex', gap: 6 }}>
      <button type="button" aria-pressed={via} onClick={() => onChange(true)} style={half(via)}>
        {viaLabel}
      </button>
      <button type="button" aria-pressed={!via} onClick={() => onChange(false)} style={half(!via)}>
        {straightLabel}
      </button>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
      background: '#F4F4F4', color: '#3A3A3A',
    }}>{children}</span>
  );
}

// A row on the To make queue that NOBODY ASKED FOR — #515 gate screen 1,
// callout B. Our own rack is low on something this branch makes.
//
// THE CARD IS A DIV WRAPPING A BUTTON, NOT A BUTTON, and that is enforcement
// rather than style. An order row is a button because the whole card opens the
// transfer. A reserve row has no document to open: the only action is Record
// production, so the press target is that button alone. Nesting a button inside
// a button is invalid HTML and React will say so in the console, which is how
// #518's chip regressed at 79.7 — a control written as a `<span>` left its
// setter with no caller anywhere and silently removed the only way in.
//
// The chip says SHELF TOP-UP so the two kinds cannot be confused at a glance.
// The order rows carry a branch and a promised date; this one says whose rack
// it is and why it is here.
//
// #746 D28 B, Session 79.22b.6.3 -- it said RESERVE until now. The Welding page
// gave "Reserve" one meaning in Stede: raw frames kept welded and waiting at
// powder coating. This card is FINISHED frames for our own rack, and the
// Welding page already calls that same need Shelf top-up, so two piles under
// one word is gone. Gate 515 drew the chip; its pointer to gate 746 says so.
//
// #761, Session 79.22b.6c.3 -- RECORD PRODUCTION'S ANSWER AND ITS UNDO (T4).
// `note` is this card's answer. A card the run lifted off the list is drawn as
// T4 draws it (`row.pinned`): whose rack, the item, and the answer - no chip, no
// count, no button. A card still low keeps everything and shows the answer
// under it (call A, R1). The card's own frame stays the app's (682 D14).
function ReserveCard({ row, isMobile, onRecord, note = null, onUndo = null, busy = false }) {
  // #632 -- the height comes from what is POINTING at it, not from the width.
  const { coarse } = useTier();
  const { item, qtyOnHand, reorderPoint } = row;
  const frame = {
    background: '#fff', border: '1px solid #E9E9E9', borderRadius: 10,
    borderLeft: '3px solid #EF9F27', padding: '11px 13px',
  };
  if (row.pinned) {
    return (
      <div style={frame} data-part="shelf-run-card">
        <p style={{
          margin: 0, fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 12,
          letterSpacing: '.5px', textTransform: 'uppercase', color: '#6A6A6A',
        }}>
          Our own rack
        </p>
        <h3 style={{
          margin: '2px 0 0', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 16,
          letterSpacing: 0, textTransform: 'none', color: '#0F0F0F',
        }}>
          {item.name}
        </h3>
        <ShelfRunAnswer note={note} onUndo={onUndo} busy={busy} />
      </div>
    );
  }
  return (
    <div style={frame}>
    <div style={{
      display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0F0F0F' }}>{item.name}</div>
        <div style={{ marginTop: 5, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip>Shelf top-up</Chip>
          <span style={{ fontSize: 12, color: '#5C5C5C' }}>
            Our own rack · <strong>{qtyOnHand}</strong> on hand
            {/* #758, 682 D12 -- "at its reorder point of 20" when it sits there. */}
            {reorderWords(qtyOnHand, reorderPoint)}
          </span>
        </div>
      </div>
      {/* Callout C — 44px on the phone, against the app's current 32. #513
          raises the floor app-wide in October; a screen touched before then
          raises its own, as the transfer screens did. */}
      <button
        onClick={onRecord}
        className="btn btn-primary"
        style={{
          minHeight: controlHeight(coarse, 'min'),
          fontSize: isMobile ? 15 : 13,
          padding: '0 14px',
          whiteSpace: 'nowrap',
          flex: isMobile ? '1 0 100%' : '0 0 auto',
        }}
      >
        Record production
      </button>
    </div>
      <ShelfRunAnswer note={note} onUndo={onUndo} busy={busy} />
    </div>
  );
}

// #581 / #567, Session 79.21b -- WHETHER THIS ITEM IS COMING, in one place.
// The desk's table and the phone's cards both draw it, so the two cannot drift
// apart -- and on a phone it is the part that used to fall off the right edge.
// NOT COLOUR ALONE: a mark and a word, because a tint is invisible to anybody
// who cannot separate the two hues.
// ** #579 -- WHAT A CARD SAYS ONCE THE TRANSFER IS OVER. **
//
// Colby's ruling, Sep 19: closed short STATES THE LOSS; every other ending says
// nothing at all; the dates stay as plain facts, because "was it late?" is a
// fair question and only the LIVE signals go.
//
// Pure and exported so one test can walk every finished status.
export function endedCardSignals(transfer, arriving) {
  const finished = FINISHED_STATUSES.includes(transfer.status);
  return {
    finished,
    // `arriving` is shipped minus received. On a closed-short transfer that is
    // exactly what was declared lost, which is why no second sum is needed.
    neverArrived: transfer.status === 'closed_short' ? arriving : 0,
    showArriving: !finished && arriving > 0,
    showCountdown: !finished,
  };
}

//
// ** #579, Session 79.21b -- A FINISHED TRANSFER DOES NOT SAY "SENDING". **
//
// "Sending" is a promise about the future, and on a received, closed-short or
// denied transfer there is no future. The replacement is read from THE LINE'S
// OWN NUMBERS, which sit in the two columns beside it, so a closed-short
// transfer names the loss on the exact item it happened to rather than
// stamping the whole sheet with one word. Colby approved the four sentences.
//
// PURE AND EXPORTED so one test can walk EVERY finished status, which is what
// the issue asks for: the next ending somebody adds cannot quietly bring
// "Sending" back on a transfer that is over.
//
// #741, Session 79.22b.6e -- `loads`, when given, says where the unbooked units
// ARE before any of them is called lost: "38 on the road · 2 never arrived",
// "40 waiting at Austin". Only what no truck and no pickup point still holds
// "never arrived", and only that takes the loss tone. `parts` carries each
// piece's own tone for the column; `text` is the same words joined.
export function lineAnswer(line, transfer, { loads = null, branchNames = {} } = {}) {
  // #572, Session 79.22b.4.7.1 -- WHOSE REFUSAL IT WAS, in the column itself.
  // An item the asking branch turned down read "Can't send" directly above
  // the quote naming the asking branch, so one line said the supplier declined
  // and the next said the asker did. The words Colby approved at the open.
  if (line.line_status === 'refused') {
    return { text: refusedByAsker(line, transfer) ? '✕ Not needed' : '✕ Can’t send', tone: 'refused' };
  }
  // #590 -- once withdrawn, nobody is waiting on an answer and nothing is sending.
  if (transfer.status === 'cancelled') return { text: 'Withdrawn', tone: 'quiet' };
  if (FINISHED_STATUSES.includes(transfer.status)) {
    if (line.line_status !== 'accepted') return { text: 'No answer', tone: 'quiet' };
    const sent = Number(line.qty_shipped || 0);
    const booked = Number(line.qty_received || 0);
    if (sent === 0) return { text: 'Not sent', tone: 'quiet' };
    if (booked < sent) {
      let left = sent - booked;
      const parts = [];
      const take = (n) => { const t = Math.min(Math.max(Number(n) || 0, 0), left); left -= t; return t; };
      if (Array.isArray(loads)) {
        const f = itemInFlight(loads, transfer, line.item_id, branchNames);
        const m = take(f.moving);
        if (m) parts.push({ text: `${m} on the road`, tone: 'good' });
        const w = take(f.waiting);
        if (w) parts.push({ text: `${w} waiting to be counted`, tone: 'good' });
        f.standing.forEach(s => {
          const k = take(s.n);
          if (k) parts.push({ text: `${k} waiting at ${s.name}`, tone: 'good' });
        });
      }
      if (left > 0) parts.push({ text: `${left} never arrived`, tone: 'lost' });
      const text = parts.map(p => p.text).join(' · ');
      const tone = left > 0 ? 'lost' : 'good';
      return Array.isArray(loads) ? { text, tone, parts } : { text, tone };
    }
    return { text: 'Arrived', tone: 'good' };
  }
  if (line.line_status === 'accepted') return { text: '✓ Sending', tone: 'good' };
  return { text: 'Awaiting answer', tone: 'quiet' };
}

const ANSWER_TONE = {
  refused: { fontWeight: 700, color: '#8A2B22' },
  lost: { fontWeight: 700, color: '#A32D2D' },
  good: { fontWeight: 700, color: '#14503B' },
  quiet: { color: '#5C5C5C' },
};

function LineAnswer({ line, transfer, loads = null, branchNames = {} }) {
  const a = lineAnswer(line, transfer, { loads, branchNames });
  // #741 -- each piece in its own tone: what is on its way reads green, only
  // what never arrived reads as the loss.
  if (a.parts && a.parts.length > 1) {
    return (
      <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {a.parts.map((p, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ color: '#5C5C5C' }}> · </span>}
            <span style={ANSWER_TONE[p.tone]}>{p.text}</span>
          </React.Fragment>
        ))}
      </span>
    );
  }
  return (
    <span style={{ fontSize: 12, whiteSpace: 'nowrap', ...ANSWER_TONE[a.tone] }}>
      {a.text}
    </span>
  );
}

// The tint a line carries in both layouts -- #567: the acceptance and the
// refusal need a visual trigger, not only a word in a column.
//
// #579 -- a line that lost units takes the refusal tint rather than the green
// one: "3 never arrived" on a green ground says two opposite things at once.
//
// #741 -- with the transfer's `loads`, the loss tint follows lineAnswer(): a
// line whose missing units are all on a truck or at a pickup point is green.
export function lineTint(line, transfer = null, loads = null) {
  if (line.line_status === 'refused') return '#FDF2F0';
  if (line.line_status !== 'accepted') return undefined;
  if (transfer && FINISHED_STATUSES.includes(transfer.status)
    && Number(line.qty_received || 0) < Number(line.qty_shipped || 0)) {
    if (!Array.isArray(loads)) return '#FDF2F0';
    return lineAnswer(line, transfer, { loads }).tone === 'lost' ? '#FDF2F0' : '#F4FAF7';
  }
  return '#F4FAF7';
}

function TransferDetail({
  transfer, items, receiverStock, promisedByKey = {}, promisesUnknown = false, standardCosts, branchNames, profile, myBranch, busy, isMobile,
  loads, loadsReady = true, auth, hubs = [], drivers = [], stage = null, lookingElsewhere = false,
  loaderModel = 'driver_loads',   // #690
  usersById = {}, production = [], handovers = [], countIns = [], namesLoading = false, isWorkbench = true,
  // #682 / #702, Session 79.22b.6c.2 -- the corrections and open questions for
  // the history, and the page's read after a correction saves.
  corrections = [], extras = [], onCorrected = null, onCorrectPress = null, bellAlert = null,
  // #675 D19 / F5, Session 79.22b.5.5 -- drawn in the desk's right-hand column
  // rather than as a sheet over the page. Everything inside is the same.
  column = false,
  onClose, onApprove, onDeny, onShip, onReceive, onCloseShort,
  onLoad, onArrive, onMarkArrived, onCollect, onCloseUnfilled,
  onGoToStage = null,   // #602 / D31
  // #621 -- opens another transfer on this page: the one a refusal was routed to.
  onOpenTransfer = null, canOpenTransfer = null, onOpenRequest = null,
  // #622, Session 79.22b -- gate 553 D28 puts the sign-off on the To make
  // sheet, and it was only ever built on the To make CARD. A fabricator who
  // arrives from his "ready to make" bell lands on this sheet, so it must
  // carry the action rather than a sentence saying somebody else does it.
  onSignOff = null,
  // #567, Session 79.15i -- answering each line rather than the whole document,
  // and deciding what happens to the ones that were refused.
  onAnswerLines, onRouteRefused, packsFor,
  // #590, gate 371 D37 -- the asker's managers withdraw from here; the asker
  // from My requests. Called only after the database has written the ending.
  onWithdrawn,
}) {
  const lines = transfer.branch_transfer_items || [];
  // #590 -- the Withdraw sheet: its own error line and the typed reason stay
  // inside the sheet (371 F1), so it calls the function itself and only hands
  // back to the page once the ending is written.
  const { phoneLayout: sheetPhone, coarse: sheetCoarse } = useTier();
  const [withdrawing, setWithdrawing] = useState(null);
  // #682 / #702, Session 79.22b.6c.2 -- THE CORRECTION SHEET, opened from a
  // history line's Correct, and ITS ANSWER (callout F): on the transfer, above
  // its items, until the next press, a change of tab or leaving. The sheet is
  // keyed on the transfer, so leaving or opening another clears both.
  const [fixing, setFixing] = useState(null);
  const [fixAnswer, setFixAnswer] = useState(null);
  useEffect(() => { if (busy) setFixAnswer(null); }, [busy]);
  useEffect(() => { setFixAnswer(null); }, [stage]);
  const fixLineFacts = (it) => {
    const line = lines.find(l => String(l.id) === String(it.lineId));
    if (!line) return null;
    return {
      made: madeByLine(production)[String(line.id)] ?? Number(line.qty_ready) ?? 0,
      asked: Number(line.qty_requested) || 0,
      sent: Number(line.qty_shipped) || 0,
    };
  };
  // W8 -- the asker's name, so a manager withdrawing for somebody is told
  // before the press that the person who asked will hear about it.
  const withdrawOffer = transferWithdrawOffer(transfer, {
    profile, branchNames, askerName: usersById?.[String(transfer.requested_by)]?.full_name || null,
  });
  const [denyReason, setDenyReason] = useState('');
  const [shortReason, setShortReason] = useState('');
  const [mode, setMode] = useState(null);   // 'deny' | 'short' | null
  // HOW THIS WAS FILLED — Colby, Session 79.6: "houston may have some of this
  // size frames on hand… is there a way for houston to choose between we'll make
  // and ship to replace what we have in reserve and we'll just ship what we have
  // without making replacements at this time?"
  //
  // A shelf transfer has nothing to decide. A MADE category does, and only the
  // sending branch knows the answer — Dallas cannot tell whether Houston cut
  // them that morning or had them on a rack.
  //
  // Defaults to the document's routing mode, so the common case is one tap and
  // the answer is never blank. Changing it changes whether a production run is
  // recorded at all.
  // #628 half 2 -- A MANAGER AT THE SENDING BRANCH MAY FILL A MADE-TO-ORDER
  // REQUEST FROM THE SHELF, and when nothing is made that is the only way it
  // can go, so it is the default: the approved drawing shows the shelf chosen
  // and Send 5. The database refuses it from anybody else (gate 517 D64).
  const shelfHere = (itemId) => Number(receiverStock?.[`${itemId}:${transfer.from_branch_id}`]?.qty_on_hand) || 0;
  const shelfRouteOpen = String(transfer.from_branch_id) === String(myBranch)
    && canStateHowFilled(auth) && shelfFillable(transfer, lines, shelfHere);
  const [sourced, setSourced] = useState(() => (
    transfer.fulfilment_mode === 'make' && shelfRouteOpen && !hasReadyToLoad(transfer, lines)
      ? 'stock' : transfer.fulfilment_mode));
  const [confirmedDate, setConfirmedDate] = useState(transfer.confirmed_date || '');
  // #517 / D67 -- a promised date per ITEM, held only for the lines that differ
  // from the one above. Empty is the normal case and writes nothing.
  const [lineDates, setLineDates] = useState(() =>
    Object.fromEntries((transfer.branch_transfer_items || [])
      .filter(l => l.promised_date)
      .map(l => [String(l.item_id), l.promised_date])));
  const [perItemDates, setPerItemDates] = useState(
    () => (transfer.branch_transfer_items || []).some(l => l.promised_date));
  const [qty, setQty] = useState(() =>
    Object.fromEntries(lines.map(l => [String(l.item_id), ''])));

  // ── #567, Session 79.15i — ANSWERING EACH LINE ────────────────────────────
  //
  // Colby: "each item being transferred has to be able to be accounted for and
  // tracked at the individual level... if one item's status is dependent on
  // another item that system will always wind up failing."
  //
  // Austin has the MDO and no frames. Their only honest button was Deny, which
  // refused the MDO they were standing next to.
  //
  // THIS IS D67'S DISCLOSURE PATTERN, NOT A NEW ONE. Fifty lines above, the
  // promised-date control does exactly this: one answer for the whole ask, a
  // link to break it out per line, and no link at all on a single-line transfer
  // because the field above already IS the per-item answer. Reusing the shape
  // keeps the common case at two taps — gate D7.
  const [perLine, setPerLine] = useState(false);
  // The form itself -- each item's answer, its reason, the running count and
  // the button -- lives in PerItemAnswer since 79.22b.4.4, shared with the
  // Requests inbox card (#698 half a, gate 665 D25).

  // ── #567 · SCREEN 3 — what happens to the lines that were refused ─────────
  //
  // The refusal creates no document. It comes home to the ASKING branch's
  // approving manager, and they choose — #565's D5, D6 and D8.
  //
  // `refusedOnDoc` reads what the DATABASE says, not what this session's form
  // said: the person routing is not the person who refused, and usually arrives
  // from a notification long afterwards.
  //
  // #698 half (b), Session 79.22b.4.7 -- ONLY WHAT THE SUPPLYING BRANCH REFUSED.
  // An item the asking branch turned down itself routes nowhere (#571's rule,
  // per item), and route_denied_transfer() refuses it the same way - this list
  // and the database are one rule, routableRefusedLines().
  const refusedOnDoc = routableRefusedLines(lines, transfer);
  const acceptedOnDoc = lines.filter(l => l.line_status === 'accepted');
  // #578, Session 79.21b -- DECIDED ONCE ANYBODY HAS DECIDED, including
  // "Nothing more is needed", which raises no successor and until this session
  // left no trace on a partly refused transfer. The database now records every
  // decision on the transfer itself (refusal_decision) and refuses a second one.
  const alreadyDecided = !!transfer.__hasSuccessor || !!transfer.refusal_decision;

  // ── #571 · WHICH REFUSAL WAS THIS, and it decides two different things ────
  //
  // `endorsed_at` is stamped by endorse_branch_transfer() when the ASKING
  // branch's own manager lets a request leave the branch. So:
  //   endorsed  -> the refusal came from the SUPPLYING branch (stage 2)
  //   NOT       -> the asking branch's own manager refused their own team
  //                member (stage 1)
  //
  // NOTHING ELSE ON THE ROW CAN TELL THEM APART. `approved_by` is written at
  // stage 2, so it is null for both, and until Session 79.20b stage-1 approval
  // recorded nobody at all.
  const wasEndorsed = !!transfer.endorsed_at;

  // #572 · WHO ACTUALLY REFUSED IT. Every refusal display used to name
  // `from_branch_id` unconditionally, because until #565's D5/D6 a refusal
  // could only come from the supplying branch. On a stage-1 denial that put the
  // asking manager's own words in the other branch's mouth - the screen read
  // Houston: "check bay 3 before asking Houston."
  // ONE copy of this rule since #573 - the history's Refused line asks the same
  // question, and two copies drift in exactly one half.
  const refusedByBranchId = refusingBranchId(transfer);
  const refusedByName = branchNames[String(refusedByBranchId)]
    || (wasEndorsed ? 'that branch' : 'your branch');
  // #698 half (b), Session 79.22b.4.7 -- each item's quote names the branch that
  // refused THAT item. One transfer can now carry the asking branch's refusals
  // and the supplier's, and the transfer-wide name above is true of only one of
  // them. Falls back to it for an item read without its own record.
  const refuserNameOf = (l) => branchNames[String(lineRefusedByBranchId(l, transfer))] || refusedByName;

  // #753, Session 79.22b.5.4b -- the one rule, shared with Finished's waiting
  // group (#752): refused by the supplier, not decided, and NOT WITHDRAWN.
  // #571's stage-1 guard lives inside it: a stage-1 refusal routes NOTHING,
  // because the manager who refused has decided the material is not needed.
  // The database refuses both too - route_denied_transfer() carries the same
  // guards - because a picker that filters is advisory.
  const canRoute = refusalAwaitsDecision(transfer)
    && canRouteRefusedLines(profile, transfer)
    && !!onRouteRefused;

  const [routeChoice, setRouteChoiceRaw] = useState(null);
  // #578 -- which branch "Try another branch" asks, and the confirmation
  // "Nothing more is needed" must pass before it writes (565 F3).
  const [routeBranch, setRouteBranch] = useState(null);
  const [confirmNone, setConfirmNone] = useState(false);
  const maker = transfer.made_at_branch_id;
  // D15, unchanged: the branch that just refused is never offered again.
  const refuserIsMaker = maker != null && String(maker) === String(transfer.from_branch_id);

  // D9: the quantity is a FRESH DECISION with a sensible default, never an
  // inheritance. Houston makes stakes in pallets of 250 and Dallas asked for 50.
  // #578 -- and the default follows THE ROUTE: its own supplier's pack, rounded
  // up to whole packs, or simply what is still needed on another branch.
  const singleRefused = refusedOnDoc.length === 1 ? refusedOnDoc[0] : null;
  const routePackNow = singleRefused
    ? routePack(packsFor ? packsFor(singleRefused) : [], routeChoice, maker)
    : null;
  const suggestedQty = singleRefused ? suggestedRouteQty(singleRefused.qty_requested, routePackNow) : null;
  const [routeQty, setRouteQty] = useState('');
  const effectiveQty = routeQty !== '' ? routeQty : (singleRefused ? String(suggestedQty) : '');
  // Choosing a different route resets the box to THAT route's default rather
  // than carrying a number typed for another supplier.
  const setRouteChoice = (key) => {
    setRouteChoiceRaw(key);
    setRouteQty('');
    setConfirmNone(false);
    if (key !== 'branch') setRouteBranch(null);
  };

  // #578 -- TRY ANOTHER BRANCH, at every width (565 F4, #551). Only branches
  // with some to spare of every refused item; never the one that refused or
  // the one asking.
  const spareBranches = branchesWithSpare({
    stockByKey: receiverStock,
    itemIds: refusedOnDoc.map(l => l.item_id),
    branchIds: Object.keys(branchNames),
    excludeBranchIds: [transfer.from_branch_id, transfer.to_branch_id],
    // #721 -- a branch's spare after what it has already promised elsewhere.
    promisedByKey,
  });

  const routeBlocked =
    !routeChoice ? 'Choose where this should go.'
      : (routeChoice === 'branch' && !routeBranch) ? 'Choose which branch to ask.'
        : (routeChoice !== 'none' && singleRefused && !(Number(effectiveQty) > 0)) ? 'Choose how many.'
          : null;

  // #602 / D28 -- ENTITLEMENT FIRST, STAGE SECOND. Each `mayX` is exactly the
  // guard this screen has always used; the `canX` below is that guard AND the
  // stage. Keeping the two apart is what lets D31 say "you may do this, but not
  // from here" rather than simply drawing nothing.
  const mayApprove = isApprovable(transfer) && canApproveTransfer(profile, transfer);

  // ── SHIPPING AND RECEIVING ARE NOT ROLE QUESTIONS. THEY ARE LOCATION ONES. ──
  //
  // Found by live test at Session 79.6, not by reading. These two lines first
  // read `seesEveryBranch || at the branch`, which is the shape every other
  // permission in this app takes — and it was WRONG in the one way that matters:
  // it offers a button the database will refuse.
  //
  // Proven on staging as an ADMIN at Dallas, against a transfer Houston was
  // receiving:
  //
  //   new row violates row-level security policy for table "inventory_transactions"
  //
  // inventory_transactions' insert rule is `user_id = auth.uid() AND branch_id =
  // the caller's own branch`, with NO ADMIN EXEMPTION — verified live in this
  // session's migration pre-check. That rule IS gate D4's two-step enforcement,
  // so an admin being refused is the design working rather than a gap in it.
  //
  // Admin and executive keep every READ they had — the select policy does exempt
  // them, which is why they are the only people who can see BOTH ledger rows of
  // one transfer in a single sitting (acceptance line T7). What they do not get
  // is a button that fails, which is #492's class exactly: a control that
  // accepts a press and does nothing useful.
  //
  // The consequence is real and worth stating: NO SINGLE ACCOUNT CAN DRIVE A
  // WHOLE TRANSFER. That is inherent to two-step and correct — somebody at the
  // sending branch sends, somebody at the receiving branch books it in.
  const atSendingBranch = String(transfer.from_branch_id) === String(myBranch);
  const atReceivingBranch = String(transfer.to_branch_id) === String(myBranch);
  // #517 -- WHO MAY SEND A LOAD, and it is wider than "at the sending branch"
  // for one reason: the Austin case. A load dropped at a staging point is
  // collected by somebody standing THERE, and that place is neither end of the
  // transfer. D57 keeps the rule itself unchanged -- shipping is gated on
  // standing where the goods are -- so the test is "is anything of this
  // transfer standing where I am".
  //
  // isLoadable() replaces isShippable(): a transfer already in_transit can
  // still take another truck, which the one-shot rule could not express.
  //
  // #628, Session 79.22b -- TWO QUESTIONS, AND THIS LINE USED TO ASK ONLY THE
  // FIRST. isLoadable() answers "may this transfer take another load" -- is
  // anything still OWED -- and it is right for that. It is wrong for "is there
  // anything to put on a truck NOW", which is what the signpost's "This is
  // ready to load" claims. A make request with 77 owed and none made said it
  // anyway, and pointed at a To load tab that -- correctly -- did not list it.
  // So at the sending branch, loading is offered only when something is made
  // and not yet sent: the SAME test the To load tab applies, so the sheet and
  // the tab cannot disagree again.
  //
  // #628 (F8) -- DELIVERED IS NOT STANDING. standingAt() adds what arrived at
  // a place and subtracts what left it, which is right for a hub and wrong for
  // the destination, because nothing ever leaves the destination: once a load
  // is counted in at Dallas, Dallas read as holding it "standing" for ever and
  // was offered a Send that could only fail. The transfer's own destination is
  // where it ends, so it is never a place goods wait to move on from.
  const standingHere = standingForOnwardLoad(loads, lines, transfer, myBranch);
  const readyAtOrigin = atSendingBranch && hasReadyToLoad(transfer, lines);
  const mayLoad = readyAtOrigin || shelfRouteOpen || (isLoadable(transfer, lines) && standingHere);
  // #655, Session 79.22b -- #628'S SHAPE AT THE RECEIVING END. "May this be
  // received" was asked of the TRANSFER -- it is on the road and you are at the
  // destination -- so Dallas's driver, fetching material waiting at Austin or
  // on the road, was told "This is arriving here. Open To count in" of a load
  // addressed to neither Dallas nor that tab. Receiving is offered only when a
  // load is addressed HERE and not yet counted in: the To count in tab's rule.
  const arrivingHere = arrivingFor(loads, transfer, myBranch);
  const mayReceive = isReceivable(transfer) && atReceivingBranch && arrivingHere.loads.length > 0;
  // #655 -- COLLECTING IS THE DESTINATION'S JOB, and now something the signpost
  // can name. CollectPanel has always offered it to exactly these people (its
  // `isMine`); nothing else on the sheet knew it existed.
  const pickupStops = atReceivingBranch ? hubsWaitingFor(loads || [], transfer) : [];
  const mayCollect = pickupStops.length > 0;
  // #531 part 2 -- COUNTING IT IN AND WRITING IT OFF ARE DIFFERENT ACTS, and
  // until 79.12 one flag did both. Counting stays with the branch standing at
  // the pallet, unchanged above. Declaring the balance lost goes to a branch
  // manager at EITHER end, plus admin and executive -- because the old rule
  // made Houston ask Dallas for permission to write off frames on Houston's
  // own rack, and every system studied lets either side book the loss.
  const mayCloseShort = isReceivable(transfer) && canCloseTransferShort(profile, transfer);
  // #531 -- IS ANYTHING STILL COMING? The same test owedToBranch() uses, and
  // for the same reason: shipped-minus-received is TRUE for goods on a truck
  // and for goods that are gone, so it cannot tell a healthy transfer from a
  // lost one. Nothing rolling and nothing standing at a yard is what makes a
  // gap final.
  const nothingMoving = !(loads || []).some(l => l?.status === 'in_transit')
    && hubsWaitingFor(loads || [], transfer).length === 0;
  // #674 -- THE THIRD STATE neither guard above covered: the truck is in and
  // nobody has counted it. Nothing is moving and the units are not "received",
  // so both conditions held while 38 stakes stood on the dock. Until they are
  // counted, no write-off is offered or announced.
  const waitingToCount = awaitingCountUnits(loads, transfer);
  // #729, Session 79.22b.6e -- where those units wait, by name: the write-off's
  // two sentences are read by a manager at either end, and "here" was false at
  // the sending one.
  const receivingAt = branchNames[String(transfer.to_branch_id)]
    ? `at ${branchNames[String(transfer.to_branch_id)]}` : 'here';
  const stillOnItsWay = stillOnItsWaySentence(loads, transfer, branchNames);

  // ── #602 / GATE 553 D28-D33 — EACH TAB'S SHEET OFFERS THAT TAB'S JOB ───────
  //
  // D32. A sheet opened WITHOUT a tab — a notification, a deep link — derives
  // its stage rather than showing everything, because showing everything there
  // would quietly restore the defect D28 closes, on the path a manager uses
  // most. An unknown stage is treated the same way, deliberately.
  // The same derivation the deep-link handler uses, and fed the same way: the
  // `mayX` values, which are entitlement rather than row state. makeProgress()
  // is imported and pure; `make` is TransferCard's local and is not in scope.
  const effectiveStage = isKnownStage(stage) ? stage : deriveStage({
    isApprovable: mayApprove,
    hasUnmadeLines: atSendingBranch
      && transfer.fulfilment_mode === 'make' && makeProgress(lines).toMake > 0,
    isLoadable: mayLoad,
    // #655 -- the READER's job, not the row's: only the destination collects.
    waitingAtHub: mayCollect,
    isReceivable: mayReceive,
    // #655 -- read off the transfer. "Nothing for me to do" is not "finished",
    // and a live transfer derived to Finished would be the wrong kind of true.
    isFinished: FINISHED_STATUSES.includes(transfer.status),
  });

  // D28. Entitlement AND the stage. Every guard above is unchanged; the stage is
  // ANDed on, so this narrows what is drawn in front of an unchanged database.
  const canApprove = mayApprove && allowsAction(effectiveStage, 'approve');
  const canLoad = mayLoad && allowsAction(effectiveStage, 'load');
  const canShip = canLoad;
  const canReceive = mayReceive && allowsAction(effectiveStage, 'receive');
  const canCloseShort = mayCloseShort && allowsAction(effectiveStage, 'closeShort');

  // D31. What this person MAY do that does not live on this tab, so the sheet
  // can name the tab and offer one tap to it. Only ever built from actions they
  // are already entitled to — never an advertisement for something they cannot
  // do.
  const signpost = signpostFor(effectiveStage, [
    mayApprove && 'approve',
    mayLoad && 'load',
    mayReceive && 'receive',
    mayCloseShort && 'closeShort',
    mayCollect && 'collect',
  ].filter(Boolean));
  // #655 -- gate 553 F2's words need the count and the place.
  const signpostText = signpost && signpostSentence(signpost, {
    n: signpost.action === 'load'
      ? (readyAtOrigin
        ? transferProgress(lines).readyToLoad
        : (lines || []).reduce((sum, l) => sum + standingAt(loads, l.item_id, myBranch), 0))
      : signpost.action === 'receive' ? arrivingHere.qty
        : signpost.action === 'collect' ? pickupStops[0]?.total
          : null,
    yard: signpost.action === 'collect'
      ? (pickupStops[0]?.place || branchNames[String(pickupStops[0]?.branchId)] || null)
      : null,
    // #673 -- where the goods ARE (load) or are GOING (receive), from the
    // transfer rather than from whichever branch the reader is looking at.
    place: signpost.action === 'load'
      ? (branchNames[String(readyAtOrigin ? transfer.from_branch_id : myBranch)] || null)
      : signpost.action === 'receive' ? (branchNames[String(transfer.to_branch_id)] || null)
        : null,
  });

  // #622, Session 79.22b -- THE SIGN-OFF, ON THE SHEET WHERE D28 DREW IT. The
  // same SignOffAction the To make card uses, so its two blocked reasons stay
  // in one place (E5, E6, E8); it answers for itself, which is why the
  // spectator sentence below stands aside whenever it is drawn.
  const makeLeft = transfer.fulfilment_mode === 'make' ? makeProgress(lines).toMake : 0;
  const signOffInSheet = effectiveStage === 'to_make' && !!onSignOff
    && ['approved', 'in_transit'].includes(transfer.status) && makeLeft > 0
    && (canSignOffProduction(auth) || atSendingBranch);

  // #628 -- WHAT IS TRUE INSTEAD, for the branch that will load it. Where the
  // old signpost said "This is ready to load", say what the work actually is.
  // Gate 553 X2's own words for the first case; the second is the made-and-
  // already-gone case, where "nothing has been made" would be false.
  const nothingToLoadYet = effectiveStage === 'to_make' && atSendingBranch
    && transfer.fulfilment_mode === 'make' && isLoadable(transfer, lines) && !readyAtOrigin
    ? (transferProgress(lines).ready > 0
      ? `Nothing is waiting to be loaded — everything made so far has gone. ${makeLeft} still to make.`
      : `Nothing has been made yet. ${makeLeft} still to make.`)
    : null;

  // Somebody who can SEE the document but cannot act on it is told why, rather
  // than shown nothing and left wondering whether the screen is broken.
  //
  // #553 D19 — NAMED BY THE STAGE THAT OPENED IT. The generic sentence below
  // is true from any tab and explains nothing about the job in front of you;
  // the gate's copy table gives the two that do. A reader who came in from To
  // load is asking "why can I not load this", and the honest answer names the
  // RULE rather than the reader's own account.
  // #602 -- the effective stage, so a sheet opened from a notification names
  // the job it derived rather than falling back to the generic sentence.
  // #725, Session 79.22b.6e -- never said to the branch that IS holding the
  // material: Houston's loader, having just sent everything ready, was told the
  // job belonged to somebody else. That reader gets the section's true sentence.
  const stageNote = effectiveStage === 'to_load' && !atSendingBranch && !standingHere
    ? 'Loading is done by the branch holding the material.'
    : effectiveStage === 'to_make'
      ? 'Signing off production is done by the branch making it.'
      : null;
  // #622 -- `!signOffInSheet`: this sentence decided a reader was a spectator
  // by asking four questions, and "may they sign off" was not one of them. A
  // fabricator with the switch on was told the job was somebody else's. When
  // the sign-off is drawn it answers for itself -- enabled, or with its own
  // reason -- so this stands aside. H3 is untouched: San Antonio's manager
  // never reaches the To make stage on a Houston transfer.
  //
  // #655 -- AND THE TWO "YOU ARE NOT SIGNED IN THERE" SENTENCES ARE NOW TRUE BY
  // CONSTRUCTION. Dallas's driver, fetching material from a pickup point, was
  // told "Dallas books this in — you are not signed in there" while signed in
  // at Dallas, because nothing he could do on that stage was one of the four.
  // Each sentence is said only to somebody who is not at that branch, and
  // never while the collect panel is drawing that person a job. H3 keeps its
  // sentence: San Antonio's manager is at neither end.
  const collectDrawn = mayCollect && allowsAction(effectiveStage, 'collect');
  const spectatorNote = !signOffInSheet && !collectDrawn && !canShip && !canReceive && !canApprove && !canCloseShort && (
    stageNote
      || (isShippable(transfer) && !atSendingBranch ? `${branchNames[String(transfer.from_branch_id)] || 'The sending branch'} sends this — you are not signed in there.`
      : isReceivable(transfer) && !atReceivingBranch ? `${branchNames[String(transfer.to_branch_id)] || 'The receiving branch'} books this in — you are not signed in there.`
      : lookingElsewhere ? 'You are looking at another branch. Everything here is read-only.'
      : null));
  // #677 -- valued from the TRUCKS, where the cost has lived since split loads;
  // the bell and the email read the same rule in closeTransferShort().
  const short = shortfall(lines, {
    loads, fromBranchId: transfer.from_branch_id, toBranchId: transfer.to_branch_id,
  });
  const leadDays = leadTimeDays(transfer.needed_by);

  // ── #725 with #730, Session 79.22b.6e -- THE LAST SECTION SAYS WHAT IS TRUE ─
  //
  // Approved as drawn (R2): "The heading is drawn only when the transfer really
  // is at that stage." So a received transfer still ends "Finished", a moving
  // truck still reads "On the road", and every panel that draws a job is drawn
  // only when its stage is true (the load panel when something can be loaded,
  // the collect panel when something waits at a stop, the count-in panel when a
  // truck is coming or a pre-loads transfer is stranded). What can stand without
  // a heading is a manager's Close short or a signpost - and those no longer sit
  // under "On the road" when nothing is on the road. BUILT FIRST WITH "or when
  // something is drawn under it", which kept exactly that, and the screen check
  // as the admin on HOU-TRF-00053 caught it; corrected to the drawing.
  const progressNow = transferProgress(lines);
  const strandedForMe = transfer.status === 'in_transit' && atReceivingBranch && (loads || []).length === 0;
  const drawStageHeading = stageIsTrue(effectiveStage, {
    approvable: isApprovable(transfer),
    unmade: transfer.fulfilment_mode === 'make' && makeLeft > 0 && ['approved', 'in_transit'].includes(transfer.status),
    readyToLoad: hasReadyToLoad(transfer, lines) || canLoad,
    moving: !nothingMoving,
    atPickup: hubsWaitingFor(loads || [], transfer).length > 0,
    arriving: arrivingFor(loads, transfer, transfer.to_branch_id).loads.length > 0 || strandedForMe,
    finished: FINISHED_STATUSES.includes(transfer.status),
    decisionPending: refusalAwaitsDecision(transfer),
  });
  // The person whose job the stage was gets one true sentence in its place;
  // everybody else gets nothing, and the spectator line above stands aside for
  // the true sentence (as it does for the sign-off, #622).
  const emptyStageText = drawStageHeading ? null : emptyStageSentence({
    stage: effectiveStage,
    atSending: atSendingBranch || !!standingHere,
    atReceiving: atReceivingBranch,
    makeLeft,
    madeAny: progressNow.ready > 0,
    sent: progressNow.shipped,
    owed: Math.max(progressNow.requested - progressNow.shipped, 0),
    stillOnItsWay,
    senderName: branchNames[String(transfer.from_branch_id)] || null,
    receiverName: branchNames[String(transfer.to_branch_id)] || null,
  });

  function shipPayload() {
    return lines.map(l => {
      const senderStock = receiverStock[`${l.item_id}:${transfer.from_branch_id}`];
      // Keyed on the ITEM alone since #515 D32 put the cost to make on the
      // item. It used to be `item:branch`, reading the internal vendor's price
      // — which was a pack price and was what we charge rather than what it
      // costs. See load().
      const standard = standardCosts[String(l.item_id)];
      return {
        item_id: l.item_id,
        qty_shipped: qty[String(l.item_id)] === '' ? l.qty_requested : Number(qty[String(l.item_id)]),
        // How this line was filled. For a shelf transfer there is nothing to
        // decide; for a made category the sender says, because only they know.
        sourced,
        // D16 AND D17, AND THIS LINE IS THE FIX FOR A REAL DEFECT.
        //
        // It previously read `l.unit_cost_at_transfer`, which is the cost
        // recorded ON SHIPPING — so before shipping it is always null, and the
        // cost never travelled at all, in either mode. Every unit test passed,
        // because they check the blending arithmetic and the arithmetic was
        // never handed a number. Colby found it by watching a real run: 200
        // stakes arrived valued at nothing while a $4.75 standard cost sat on
        // Houston's record.
        // D45, Session 79.8 — `transfer.fulfilment_mode`, NOT `sourced`.
        //
        // `sourced` is right above this and is the sender's ship-time answer;
        // it decides whether a production row is written. It must not decide
        // what the goods are WORTH. A frame is worth what a frame costs to
        // make whichever way it was filled, which is what a standard cost
        // means and what SAP and Oracle both do.
        //
        // There is a test that reads THIS FILE and fails if `sourced` is ever
        // passed here again — the two names are one character apart in intent
        // and a swap back would be silent.
        unit_cost: costToTravel({
          madeAtBranchId: transfer.made_at_branch_id,
          senderAvgCost: senderStock?.avg_unit_cost,
          standardCost: standard,
        }),
      };
    });
  }

  // #634, Session 79.22b -- receivePayload() WAS HERE, built for the old
  // one-shot receive and never called once counting-in replaced it at 79.15f.
  // It read as live, which is how #634 hid: the average blend now lives in
  // averagesOnArrival() in transferLoads.js, called by countInLoad().

  // #712, Colby's drawing approved at 79.22b.4.3.1 (A-D) -- A MADE COLUMN, on
  // a made-to-order transfer only: a shelf transfer makes nothing, so it gets no
  // column rather than a column of dashes (D). It counts the production runs,
  // never `qty_ready` (A, #713), and a line filled from the maker's shelf says
  // so under the item, so Made and Sent still add up (B). While the runs are
  // still loading the cell reads "…", never a 0 that would say nothing was made.
  const showMade = transfer.fulfilment_mode === 'make';
  const madeCounts = madeByLine(production);
  const fromShelf = shelfSentByItem(loads, transfer.from_branch_id);
  const makerName = branchNames[String(transfer.from_branch_id)] || 'the sending branch';
  const madeText = (l) => (l.line_status === 'refused' ? '—'
    : namesLoading ? '…' : String(madeCounts[String(l.id)] || 0));
  const shelfNote = (l) => {
    const n = fromShelf[String(l.item_id)] || 0;
    return showMade && n > 0 ? `${n} sent from ${makerName}’s shelf` : null;
  };

  const overBooked = lines.some(l => {
    const v = qty[String(l.item_id)];
    if (v === '' || v == null) return false;
    const shipped = Number(l.qty_shipped) || 0;
    const already = Number(l.qty_received) || 0;
    return Number(v) > shipped - already;
  });

  return (
    <DetailFrame column={column} isMobile={isMobile} onClose={onClose}>
      <div style={{ padding: '1.1rem 1.15rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-head)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 16, fontWeight: 700 }}>
              {transferLabel(transfer, branchNames)}
            </div>
            <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 3 }}>
              {STATUS_TEXT[transfer.status]} · {MODE_LABEL[transfer.fulfilment_mode]}
              {transfer.needed_by ? ` · needed by ${transfer.needed_by}` : ''}
            </div>
            {/* The maker's own promise, shown as its own line rather than
                blended into the one above, because it answers a different
                question: that one is what was ASKED for, this is what was
                COMMITTED to. */}
            {transfer.confirmed_date && (
              <div style={{ fontSize: 12, color: '#14503B', marginTop: 3, fontWeight: 600 }}>
                {branchNames[String(transfer.from_branch_id)] || 'The sending branch'} expects it ready {transfer.confirmed_date}
              </div>
            )}
            {/* 371 D35 and F2 -- what was already made stays on the maker's shelf
                as ordinary stock, and the detail says so in numbers. */}
            {transfer.status === 'cancelled' && transfer.fulfilment_mode === 'make' && (() => {
              const made = lines.reduce((n, l) => n + (Number(l.qty_ready) || 0) - (Number(l.qty_shipped) || 0), 0);
              return made > 0 ? (
                <div style={{ fontSize: 12, color: '#3A3A3A', marginTop: 3 }}>
                  {made} made before it was withdrawn {made === 1 ? 'is' : 'are'} on {branchNames[String(transfer.from_branch_id)] || 'the maker'}&rsquo;s shelf.
                </div>
              ) : null;
            })()}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#5C5C5C', lineHeight: 1, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {transfer.work_order_ref && (
          <div style={{ fontSize: 12, color: '#3A3A3A', marginTop: 8 }}>
            Work order <strong>{transfer.work_order_ref}</strong>
          </div>
        )}
        {transfer.notes && (
          <div style={{ fontSize: 12, color: '#3A3A3A', marginTop: 6, whiteSpace: 'pre-wrap' }}>{transfer.notes}</div>
        )}
        {transfer.denial_reason && (
          <div style={{ fontSize: 12, color: '#8A2F2F', marginTop: 8 }}>
            Denied — {transfer.denial_reason}
          </div>
        )}
        {transfer.short_close_reason && (
          <div style={{ fontSize: 12, color: '#8A2F2F', marginTop: 8 }}>
            {/* D18 — the loss in UNITS AND DOLLARS, on the document. A page
                nobody opens unless they already suspect something is why the
                notification goes to a person as well. */}
            Closed short — {transfer.short_close_reason}
            {short.units > 0 && (
              <> · {short.units} unit{short.units === 1 ? '' : 's'} never arrived
                {/* ** THE MONEY IS BEHIND canSeeLoadValue, NOT BEHIND "IS IT
                    PRICED". ** D65: a team member never sees what material is
                    worth, and this read `!short.anyUnpriced` alone -- so the
                    figure was hidden only while the items happened to have no
                    cost recorded. It passed the live test for exactly that
                    reason, which is the 79.9 lesson about a guard that cannot
                    fire. Price the items and every driver at Dallas reads the
                    write-off value of a loss. */}
                {canSeeLoadValue(auth)
                  ? (short.anyUnpriced ? ' (some had no recorded cost)' : `, about ${money(short.value)}`)
                  : ''}</>
            )}
          </div>
        )}

        {/* #682 / #702 -- THE ANSWER AFTER A CORRECTION (P3, callout F): the
            new number and what it means, in the done pair, until the next
            press. There is no clock (gate 665 D21, as amended Sep 27). */}
        {/* #682, 79.22b.6c.2b -- S6, CALLOUT U: opened from Houston's Yes bell, the
            transfer says the extra is now on this shelf, in the done pair with
            its Alert kicker, until the next press (F1). */}
        {bellAlert === 'extra_answered' && extraConfirmedAlert({ extras, branchNames, items }) && (
          <p role="status" style={{
            margin: '12px 0 0', background: '#E1F5EE', color: '#085041', borderRadius: 8,
            padding: '8px 12px', fontSize: 13, lineHeight: 1.35,
          }}>
            <span style={{
              display: 'block', margin: '0 0 4px', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 12,
              letterSpacing: '.5px', textTransform: 'uppercase', color: 'inherit',
            }}>Alert</span>
            {extraConfirmedAlert({ extras, branchNames, items })}
          </p>
        )}

        {fixAnswer && (
          <p role="status" style={{
            margin: '12px 0 0', background: '#E1F5EE', color: '#085041', borderRadius: 8,
            padding: '8px 12px', fontSize: 13, lineHeight: 1.35,
          }}>
            <b style={{ fontWeight: 600 }}>{fixAnswer[0]}</b>
            {fixAnswer[1] && <><br />{fixAnswer[1]}</>}
          </p>
        )}

        {/* ** #581 -- ON A PHONE EACH ITEM IS A CARD. ** This table is 600px
            wide inside 373px, so it scrolled sideways and what fell off the
            right edge was the ANSWER: whether the item is actually coming.
            Nothing said there was more to the right. Gate 567's F4 draws phone
            lines as cards and 553's F4 puts a phone on one column; the break is
            the WORKBENCH width, because a tablet is carried too (#532). */}
        {!isWorkbench ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
            {lines.map(l => {
              const item = items[String(l.item_id)];
              return (
                <div key={l.id} style={{
                  border: '1px solid #EFEFEF', borderRadius: 10, padding: '10px 12px',
                  background: lineTint(l, transfer, loadsReady ? loads : null),
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0F0F0F' }}>
                    {itemLabel(items, l.item_id)}
                  </div>
                  {item?.sku && <div style={{ fontSize: 11, color: '#6A6A6A', marginTop: 1 }}>{item.sku}</div>}
                  {shelfNote(l) && <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>{shelfNote(l)}</div>}
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6,
                    fontSize: 13, color: '#3A3A3A',
                  }}>
                    <span>Asked <strong>{l.qty_requested}</strong></span>
                    {showMade && <span>Made <strong>{madeText(l)}</strong></span>}
                    <span>Sent <strong>{l.qty_shipped ?? '—'}</strong></span>
                    <span>Booked <strong>{l.qty_received ?? '—'}</strong></span>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <LineAnswer line={l} transfer={transfer} loads={loadsReady ? loads : null} branchNames={branchNames} />
                  </div>
                  {/* THE REASON TRAVELS WITH THE LINE -- the only message the
                      asking branch ever gets about that item. */}
                  {l.line_status === 'refused' && l.refusal_reason && (
                    <div style={{ fontSize: 12, color: '#8A2B22', marginTop: 5 }}>
                      {refuserNameOf(l)}: “{l.refusal_reason}”
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
        <table style={{ width: '100%', marginTop: 14, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#5C5C5C', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <th style={{ padding: '4px 0' }}>Item</th>
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>Asked</th>
              {showMade && <th style={{ padding: '4px 6px', textAlign: 'right' }}>Made</th>}
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>Sent</th>
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>Booked</th>
              {/* #567 — the column that answers "is this one actually coming".
                  Before line-level refusal every line shared the header's fate,
                  so there was nothing per line to say. */}
              <th style={{ padding: '4px 0 4px 6px', textAlign: 'right' }}>Answer</th>
              {/* #517 -- the quantity boxes moved into LoadPanel and
                  ArrivalPanel. This table is now what it always should have
                  been: what was asked for and where it has got to. A box here
                  AND a box there is two places to type the same number. */}
            </tr>
          </thead>
          <tbody>
            {lines.map(l => {
              const item = items[String(l.item_id)];
              return (
                <tr key={l.id} style={{
                  borderTop: '1px solid #F0F0F0',
                  // #567 — A REFUSED LINE IS TINTED, not merely labelled. Colby:
                  // the acceptance and the denial need visual triggers that
                  // "leave no confusion with the user reading the modals,
                  // screens, or tables". A word in a column is read only by
                  // somebody already looking at that column.
                  background: lineTint(l, transfer, loadsReady ? loads : null),
                }}>
                  <td style={{ padding: '7px 0' }}>
                    <div style={{ fontWeight: 600, color: '#0F0F0F' }}>{itemLabel(items, l.item_id)}</div>
                    {item?.sku && <div style={{ fontSize: 11, color: '#7A7A7A' }}>{item.sku}</div>}
                    {shelfNote(l) && <div style={{ fontSize: 11.5, color: '#5C5C5C', marginTop: 2 }}>{shelfNote(l)}</div>}
                    {/* THE REASON TRAVELS WITH THE LINE. It is the only message
                        the asking branch ever gets about that item, and putting
                        it anywhere else means reading two places to learn one
                        thing. */}
                    {l.line_status === 'refused' && l.refusal_reason && (
                      <div style={{ fontSize: 11.5, color: '#8A2B22', marginTop: 3 }}>
                        {refuserNameOf(l)}: “{l.refusal_reason}”
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '7px 6px', textAlign: 'right' }}>{l.qty_requested}</td>
                  {showMade && <td style={{ padding: '7px 6px', textAlign: 'right' }}>{madeText(l)}</td>}
                  <td style={{ padding: '7px 6px', textAlign: 'right' }}>{l.qty_shipped ?? '—'}</td>
                  <td style={{ padding: '7px 6px', textAlign: 'right' }}>{l.qty_received ?? '—'}</td>
                  {/* NOT COLOUR ALONE — a mark and a word as well, because a
                      tint is invisible to anybody who cannot separate the two
                      hues, and this is the column that says whether the material
                      is coming. */}
                  <td style={{ padding: '7px 0 7px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <LineAnswer line={l} transfer={transfer} loads={loadsReady ? loads : null} branchNames={branchNames} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}

        {/* #709 -- read from the TRUCKS: what is on one still moving, and what
            stands at a pickup point. Shipped minus received could not tell a
            moving unit from a lost one, so a truck counted in short read
            "2 still in transit" with nothing on the road. */}
        {isReceivable(transfer) && stillOnItsWay && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#1D9E75', fontWeight: 600 }}>
            {stillOnItsWay}
          </div>
        )}

        {/* WHERE MY STUFF IS, for whoever is only looking. Every load on this
            transfer, what left on it and what was counted in -- so the question
            does not need a second screen. */}
        {/* D20 / F4 — the four columns STACK on a phone AND a tablet, so the
            break is the WORKBENCH width rather than the phone one. #532 is why:
            tablets are CARRIED, and every iPad but the Mini was being handed
            the desktop layout because 768 was the app's only boundary. */}
        <LoadHistory loads={loads} branchNames={branchNames} items={items} transfer={transfer}
          usersById={usersById} production={production} handovers={handovers} countIns={countIns}
          corrections={corrections} extras={extras}
          namesLoading={namesLoading} isMobile={!isWorkbench}
          auth={auth}
          // Pressing Correct is a press: the last answer goes (callout F).
          onCorrect={onCorrected ? (fix) => { setFixAnswer(null); onCorrectPress?.(); setFixing(fix); } : null} />
        {fixing && (
          <CorrectionSheet
            kind={fixing.kind}
            context={fixing.context}
            items={fixing.items}
            lineFacts={fixLineFacts}
            branchNames={branchNames}
            // #682 / #702, 79.22b.6c.3 -- whose approvers the bells reach (B5).
            transfer={transfer}
            onClose={() => setFixing(null)}
            // A correction that lost to another one made a moment earlier:
            // the page reads again, so the history is current when it closes.
            onRaced={() => { onCorrected?.(); }}
            onSaved={async (answer) => {
              await onCorrected?.();
              setFixing(null);
              setFixAnswer(answer);
            }}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
          {/* #725 -- the true sentence below answers for itself. */}
          {spectatorNote && !emptyStageText && (
            <div style={{ fontSize: 12, color: '#5C5C5C', background: '#F7F7F7', borderRadius: 8, padding: '9px 11px' }}>
              {spectatorNote}
            </div>
          )}
          {/* WHAT APPROVING COMMITS YOU TO, in days, before you press it. The
              gate's approval screen reads "Approving commits Houston to
              manufacturing 200. Needed by Sep 20 — 12 days." The NUMBER is the
              point: it is what lets the person committing the company judge
              whether it is achievable, rather than finding out when the date is
              missed.

              #522, SECOND HALF, FIXED AT SESSION 79.15c. The verb is now
              SUPPLYING rather than MAKING, and that is not a wording
              preference. Since #515 the maker holds a reserve rack, so
              approving a make-mode transfer commits them to getting the
              material here — by building it OR by shipping what is already
              on their shelf. The ship sheet asks them which, two steps
              later, so claiming manufacture here pre-empts an answer the
              app goes on to request.

              #522's entry named TWO sentences and said they had to be
              reworded together or the screens would disagree. 79.10 fixed
              only the request sheet, and for four days they did disagree.
              The third copy is the approval EMAIL in notify.js — changed
              in the same commit, because the third copy is never in the
              file you are editing. */}
          {canApprove && transfer.needed_by && (
            <div style={{
              fontSize: 12, borderRadius: 8, padding: '9px 11px',
              background: leadDays != null && leadDays < 7 && transfer.fulfilment_mode === 'make' ? '#FFF6E5' : '#F7F7F7',
              color: '#3A3A3A',
            }}>
              {transfer.fulfilment_mode === 'make'
                ? <>Approving commits {branchNames[String(transfer.from_branch_id)] || 'your branch'} to supplying this — making it, or sending it from their stock.</>
                : <>Approving commits {branchNames[String(transfer.from_branch_id)] || 'your branch'} to sending this.</>}
              {' '}Needed by <strong>{transfer.needed_by}</strong>
              {leadDays != null && <> — <strong>{leadDays === 0 ? 'today' : leadDays < 0 ? `${Math.abs(leadDays)} days ago` : `${leadDays} days`}</strong></>}.
            </div>
          )}

          {/* Gate: "They will confirm a date when they accept." The requester is
              PROMISED this on the request sheet, so not building it made that
              sentence untrue. Kept separate from needed_by — one field holding
              both would mean accepting an order silently rewrites what was
              asked for, and nobody could answer "are we hitting people's
              dates?" ever again. */}
          {canApprove && mode !== 'deny' && transfer.fulfilment_mode === 'make' && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 5 }}>
                When will it be ready? <span style={{ fontWeight: 400, color: '#7A7A7A' }}>optional — they see this</span>
              </label>
              <input
                type="date"
                value={confirmedDate}
                onChange={e => setConfirmedDate(e.target.value)}
                style={{ fontSize: 16, padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, width: isMobile ? '100%' : 200, boxSizing: 'border-box' }}
              />

              {/* D67 — ONE DATE IS OFFERED FOR ALL OF THEM, AND CHANGING ONE IS
                  A TAP. Colby: different things have different lead times and
                  some of it is already on the shelf.

                  Only offered on a multi-line ask, because on one line the
                  field above already IS the per-item date and a second control
                  saying the same thing is a question with one answer. */}
              {lines.length > 1 && !perItemDates && (
                <button
                  onClick={() => setPerItemDates(true)}
                  style={{ display: 'block', marginTop: 8, background: 'none', border: 'none', padding: 0, fontSize: 12, color: '#14503B', cursor: 'pointer', textDecoration: 'underline', minHeight: controlHeight(sheetCoarse, 'min') }}>
                  Some of these will be ready sooner than others
                </button>
              )}

              {lines.length > 1 && perItemDates && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                    When each is ready
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {lines.map(l => (
                        <tr key={l.item_id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                          <td style={{ padding: '7px 0', fontSize: 13, color: '#0F0F0F' }}>
                            {itemLabel(items, l.item_id)}
                          </td>
                          <td style={{ padding: '7px 0 7px 10px', textAlign: 'right', width: 170 }}>
                            <input
                              type="date"
                              value={lineDates[String(l.item_id)] ?? ''}
                              // The placeholder IS the whole-ask date: a blank
                              // line means "same as everything else", which is
                              // why nothing is written for it.
                              onChange={e => setLineDates(d => ({ ...d, [String(l.item_id)]: e.target.value }))}
                              style={{ fontSize: 16, padding: '6px 8px', border: '1px solid #DDD', borderRadius: 8, width: 160, boxSizing: 'border-box' }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 6, lineHeight: 1.4 }}>
                    Leave one blank and it takes the date above. The requester sees whichever applies to their line.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── #567 · SCREEN 3 — the decision the refusal leaves behind ──── */}
          {canRoute && (
            <div style={{ border: '1px solid #E0C48A', background: '#FDF6E7', borderRadius: 10, padding: '12px 13px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F' }}>
                {acceptedOnDoc.length > 0
                  ? `${refusedByName} couldn't send everything`
                  : `Refused by ${refusedByName}`}
                {' · needs your decision'}
              </div>

              {/* THE ACCEPTED LINES ARE SHOWN AND EXPLICITLY FINISHED — callout E.
                  Omitting them leaves the manager wondering what happened to the
                  MDO; four words answer it and stop a second enquiry. */}
              {acceptedOnDoc.map(l => (
                <div key={l.item_id} style={{ border: '1px solid #CFE6DC', background: '#F1F8F5',
                                              borderRadius: 8, padding: '8px 10px', marginTop: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#14503B' }}>
                    {l.qty_requested} × {itemLabel(items, l.item_id)} — {branchNames[String(transfer.from_branch_id)] || 'the sending branch'} is sending these
                  </div>
                  <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>Nothing for you to do.</div>
                </div>
              ))}

              {refusedOnDoc.map(l => (
                <div key={l.item_id} style={{ border: '1px solid #E4E4E4', background: '#FFF',
                                              borderRadius: 8, padding: '9px 10px', marginTop: 8 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                    {l.qty_requested} × {itemLabel(items, l.item_id)}
                  </div>
                  {l.refusal_reason && (
                    <div style={{ fontSize: 12.5, color: '#5C5C5C', marginTop: 3 }}>
                      {refusedByName}: “{l.refusal_reason}”
                    </div>
                  )}
                </div>
              ))}

              {/* D9 — and the SENTENCE is the feature. A number that appears
                  without explanation reads as a bug: the manager asked for 50.
                  #578: the sentence names the supplier the number came from,
                  and the number is that route's own pack, rounded up. */}
              {singleRefused && routeChoice !== 'none' && (
                <div style={{ marginTop: 11 }}>
                  <label style={{ fontSize: 11.5, fontWeight: 700, color: '#5C5C5C', textTransform: 'uppercase',
                                  letterSpacing: '0.8px', display: 'block', marginBottom: 5 }}>
                    How many do you still need?
                  </label>
                  <input
                    type="number" min="1" inputMode="numeric"
                    value={effectiveQty}
                    onChange={e => setRouteQty(e.target.value)}
                    // #708 -- the one shared confirm-box style; it was already
                    // edged green, and now matches every other count to confirm.
                    className={QTY_BOX_CLASS}
                    style={qtyBox({ width: 120, align: 'left' })}
                  />
                  <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 4 }}>
                    {/* A MISSING PACK QUANTITY IS SILENT, never "pallets of 0".
                        X2 names it. */}
                    {routePackNow
                      ? <>{routeChoice === 'buy'
                          ? `${routePackNow.vendorName || 'The vendor'} sells these in ${packPhrase(routePackNow)}.`
                          : `${branchNames[String(maker)] || 'The maker'} makes these in ${packPhrase(routePackNow)}.`}
                          {' '}You asked for {singleRefused.qty_requested}.</>
                      : <>You asked for {singleRefused.qty_requested}.</>}
                  </div>
                </div>
              )}

              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#5C5C5C', textTransform: 'uppercase',
                            letterSpacing: '0.8px', margin: '13px 0 5px' }}>
                Where should this go?
              </div>

              {[
                { key: 'make', label: `Ask ${branchNames[String(maker)] || 'the maker'} to make these`,
                  sub: `${branchNames[String(maker)] || 'The maker'} fills from its rack or manufactures — its call.`,
                  // D15: the maker refusing means production is down, so it is
                  // not offered. #578: WHY is said once, in the line under the
                  // routes, rather than here and there both.
                  hide: maker == null || refuserIsMaker },
                // #578 -- TRY ANOTHER BRANCH, which gates 565 (D8) and 567
                // approved and the panel never offered. Drawn even when nobody
                // can spare any, with the reason, never silently absent (F1).
                // #721's reader of #608's cut-off: with the promises unknown,
                // "has some to spare" would overstate, and "nobody has" might
                // be false. It says it could not check, and is not pressable.
                { key: 'branch', label: 'Try another branch',
                  sub: promisesUnknown
                    ? "Couldn't check what other branches have already promised. Try again shortly."
                    : spareBranches.length
                      ? `${spareBranches.length} branch${spareBranches.length === 1 ? ' has' : 'es have'} some to spare.`
                      : 'No other branch has any to spare.',
                  unavailable: promisesUnknown || spareBranches.length === 0 },
                { key: 'buy', label: 'Send to Purchasing', sub: 'Buy it in.' },
                { key: 'none', label: 'Nothing more is needed',
                  sub: acceptedOnDoc.length > 0
                    ? 'Records that the refused items are not needed. What was agreed still comes.'
                    : 'Closes this with nothing ordered.' },
              ].filter(r => !r.hide).map(r => (r.unavailable ? (
                // A route with nothing behind it answers the tap with its reason
                // rather than sitting dead (#492): it is not pressable, and the
                // reason is on the face of it.
                <div key={r.key} aria-disabled="true"
                  style={{ border: '1px dashed #D6D6D6', background: '#FAFAFA', borderRadius: 8,
                           padding: '9px 11px', marginTop: 7, minHeight: isMobile ? 48 : 40, boxSizing: 'border-box' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#5C5C5C' }}>{r.label}</div>
                  <div style={{ fontSize: 12.5, color: '#5C5C5C', marginTop: 2 }}>{r.sub}</div>
                </div>
              ) : (
                <button key={r.key} onClick={() => setRouteChoice(r.key)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                           border: routeChoice === r.key ? '2px solid #157A59' : '1px solid #E4E4E4',
                           background: routeChoice === r.key ? '#EAF5F0' : '#FFF',
                           borderRadius: 8, padding: '9px 11px', marginTop: 7, minHeight: isMobile ? 48 : 40 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0F0F0F' }}>{r.label}</div>
                  <div style={{ fontSize: 12.5, color: '#5C5C5C', marginTop: 2 }}>{r.sub}</div>
                </button>
              )))}

              {/* #578 -- WHICH BRANCH, each with what it can spare, biggest
                  first. Only branches with some of every refused item. */}
              {routeChoice === 'branch' && spareBranches.length > 0 && (
                <div role="radiogroup" aria-label="Which branch to ask" style={{ marginTop: 8 }}>
                  {spareBranches.map(b => (
                    <button key={b.branchId} role="radio" aria-checked={routeBranch === b.branchId}
                      onClick={() => setRouteBranch(b.branchId)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                               width: '100%', textAlign: 'left', cursor: 'pointer', marginTop: 6,
                               border: routeBranch === b.branchId ? '2px solid #157A59' : '1px solid #E4E4E4',
                               background: routeBranch === b.branchId ? '#EAF5F0' : '#FFF',
                               borderRadius: 8, padding: '8px 11px', minHeight: isMobile ? 48 : 40 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0F0F0F' }}>
                        {branchNames[String(b.branchId)] || `Branch ${b.branchId}`}
                      </span>
                      <span style={{ fontSize: 12.5, color: '#14503B', fontWeight: 600, textAlign: 'right' }}>
                        {singleRefused ? `${b.spare} to spare` : 'has all of them to spare'}
                        {/* #721 (approved drawing) -- what this branch has
                            already promised elsewhere, under its spare. One
                            item only: several would need a line each (E). */}
                        {singleRefused && b.promised > 0 && (
                          <span style={{ display: 'block', color: '#5C5C5C', fontWeight: 400 }}>
                            {b.promised} already promised
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* The branch that just refused, absent with a reason — D15 and
                  callout G. SAID ONCE (#578): the make route used to say it too. */}
              <div style={{ fontSize: 12.5, color: '#5C5C5C', margin: '8px 0 2px' }}>
                <strong>{refusedByName} isn't offered</strong> — they just refused.
              </div>

              {/* 565 F3 -- "Nothing more is needed" CONFIRMS FIRST, because it
                  writes an ending nothing else records and it cannot be undone. */}
              {confirmNone ? (
                <div role="alertdialog" aria-label="Confirm nothing more is needed"
                  style={{ border: '1px solid #E0C48A', background: '#FFF', borderRadius: 8,
                           padding: '10px 12px', marginTop: 10 }}>
                  <div style={{ fontSize: 13.5, color: '#0F0F0F', lineHeight: 1.45 }}>
                    {acceptedOnDoc.length > 0
                      ? `This records that nothing more is needed for the refused ${refusedOnDoc.length === 1 ? 'item' : 'items'} on ${transfer.transfer_number || 'this transfer'}. What was agreed still comes. It can't be undone.`
                      : `This closes ${transfer.transfer_number || 'this transfer'} with nothing ordered. It can't be undone.`}
                  </div>
                  <Actions isMobile={isMobile}>
                    <ActionButton
                      onClick={() => onRouteRefused({
                        route: 'none', qty: null, itemIds: refusedOnDoc.map(l => l.item_id),
                      })}
                      busy={busy}
                      busyLabel="Recording…"
                      className="btn btn-primary"
                      style={actionWidth(isMobile, sheetCoarse)}
                    >
                      Yes, nothing more is needed
                    </ActionButton>
                    <button className="btn btn-secondary" onClick={() => setConfirmNone(false)}
                      style={actionWidth(isMobile, sheetCoarse)} disabled={busy}>
                      Go back
                    </button>
                  </Actions>
                </div>
              ) : (
                <Actions isMobile={isMobile}>
                  <ActionButton
                    onClick={() => (routeChoice === 'none'
                      ? setConfirmNone(true)
                      : onRouteRefused({
                        route: routeChoice,
                        qty: singleRefused ? Number(effectiveQty) : null,
                        itemIds: refusedOnDoc.map(l => l.item_id),
                        toBranchId: routeChoice === 'branch' ? routeBranch : null,
                      }))}
                    blockedReason={routeBlocked}
                    busy={busy}
                    busyLabel="Sending…"
                    className="btn btn-primary"
                    style={actionWidth(isMobile, sheetCoarse)}
                  >
                    {/* FOUND ON THE LIVE SCREEN, 79.15i: a label that names a
                        destination nobody picked tells the manager what they
                        are about to do, wrongly. Neutral until they choose. */}
                    {!routeChoice
                      ? 'Send'
                      : routeChoice === 'none'
                        ? 'Nothing more is needed'
                        : routeChoice === 'buy'
                          ? `Send ${singleRefused ? effectiveQty + ' ' : ''}to Purchasing`
                          : routeChoice === 'branch'
                            ? `Ask ${routeBranch ? (branchNames[String(routeBranch)] || 'that branch') : 'a branch'}${singleRefused ? ` for ${effectiveQty}` : ''}`
                            : `Send ${singleRefused ? effectiveQty + ' ' : ''}to ${branchNames[String(maker)] || 'the maker'}`}
                  </ActionButton>
                </Actions>
              )}
            </div>
          )}

          {/* #578 -- ONCE DECIDED, SAY WHAT WAS DECIDED. The panel leaves the
              moment anybody decides, and an absence is not an answer: the
              second manager opening the same notification sees who decided
              and which way, rather than wondering where the buttons went. */}
          {refusedOnDoc.length > 0 && alreadyDecided && (
            <div style={{ border: '1px solid #E4E4E4', background: '#FAFAFA', borderRadius: 8,
                          padding: '9px 11px', fontSize: 13, color: '#0F0F0F', lineHeight: 1.45 }}>
              {(() => {
                const d = transfer.refusal_decision;
                const who = usersById?.[String(transfer.refusal_decided_by)]?.full_name
                  || usersById?.[String(transfer.refusal_decided_by)]?.name || null;
                const when = transfer.refusal_decided_at
                  ? new Date(transfer.refusal_decided_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : null;
                const what = d === 'none' ? 'Nothing more is needed for the refused items'
                  : d === 'buy' ? 'The refused items were sent to Purchasing'
                    : d === 'make' ? `${branchNames[String(maker)] || 'The maker'} was asked to make the refused items`
                      : d === 'branch' ? 'Another branch was asked for the refused items'
                        : 'What happens to the refused items has been decided';
                const tail = [who, when].filter(Boolean).join(', ');
                // #621 -- NAME WHAT IT WENT TO, AND OPEN IT (gate 565 F2, "See
                // {number}", and its error row: "a link to the successor"). A
                // transfer this page holds opens here; a purchase request opens
                // in the Requests inbox. Either way one tap, on a full-width
                // control like the signpost's so a finger can hit it. Anything
                // that cannot be opened from here is named, so it can be found.
                const s = transfer.__successor;
                const openSuccessor = !s?.number ? null
                  : s.kind === 'transfer' && onOpenTransfer && canOpenTransfer?.(s.id) ? () => onOpenTransfer(s.id)
                    : s.kind === 'request' && onOpenRequest ? () => onOpenRequest(s.id)
                      : null;
                const openable = !!openSuccessor;
                return (
                  <>
                    <strong>Decided.</strong> {what}{tail ? ` — ${tail}` : ''}.
                    {s?.number && !openable && <> See <strong>{s.number}</strong>.</>}
                    {openable && (
                      <button type="button" onClick={openSuccessor}
                        style={{ display: 'flex', alignItems: 'center', width: '100%', minHeight: 44,
                                 background: 'none', border: 'none', padding: '0 2px', fontSize: 13,
                                 color: '#1F6FBF', textDecoration: 'underline', fontWeight: 600,
                                 textAlign: 'left', cursor: 'pointer' }}>
                        See {s.number}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {canApprove && mode !== 'deny' && !perLine && (
            <>
              <Actions isMobile={isMobile}>
                {/* Names the branch for the same reason the inbox card does.
                    "Approve" alone is not a lie, but it does not say WHICH of the
                    two approvals this is -- and after 79.10 there are two. */}
                <ActionButton onClick={() => onApprove(confirmedDate || null, perItemDates ? lineDates : null)} busy={busy} busyLabel="Confirming…" className="btn btn-primary" style={actionWidth(isMobile, sheetCoarse)}>
                  Confirm {branchNames[String(transfer.from_branch_id)] || 'the sender'} can supply
                </ActionButton>
                <button
                  onClick={() => setMode('deny')}
                  className="btn btn-danger"
                  style={actionWidth(isMobile, sheetCoarse)}
                >
                  Deny…
                </button>
              </Actions>

              {/* #567 D7 — THE LINK, AND IT IS ABSENT ON A SINGLE-LINE TRANSFER
                  RATHER THAN DISABLED. Deny already IS the per-line answer when
                  there is one line, and 79.8's Record production button is the
                  precedent for why that matters: a control whose precondition no
                  real row satisfies is an empty state wearing a button. */}
              {/* #698 (b), 79.22b.4.7.1 -- counted over the items the supplier is
                  ASKED about: two asked plus one Dallas turned down is still two,
                  and one asked is Deny's to answer (567 D7). */}
              {linesPutToSupplier(lines, transfer).length > 1 && onAnswerLines && (
                <button
                  onClick={() => setPerLine(true)}
                  style={{ display: 'block', marginTop: 8, background: 'none', border: 'none', padding: 0,
                           fontSize: 12.5, color: '#14503B', cursor: 'pointer', textDecoration: 'underline',
                           minHeight: controlHeight(sheetCoarse, 'min') }}>
                  I can't supply all of these
                </button>
              )}
            </>
          )}

          {/* ── #567 · SCREEN 2 — the per-line answer ─────────────────────── */}
          {canApprove && mode !== 'deny' && perLine && (
            <PerItemAnswer
              // #698 (b) -- only what the supplier is asked; the database
              // refuses an answer to anything else ("Already decided").
              lines={linesPutToSupplier(lines, transfer)}
              items={items}
              supplier={branchNames[String(transfer.from_branch_id)] || null}
              asker={branchNames[String(transfer.to_branch_id)] || null}
              busy={busy}
              isMobile={isMobile}
              coarse={sheetCoarse}
              onSubmit={onAnswerLines}
              onCancel={() => setPerLine(false)}
            />
          )}

          {canApprove && mode === 'deny' && (
            <>
              <label style={{ fontSize: 12, color: '#3A3A3A', fontWeight: 600 }}>Why are you denying this?</label>
              <textarea
                value={denyReason}
                onChange={e => setDenyReason(e.target.value)}
                rows={3}
                style={{ fontSize: 16, padding: 8, border: '1px solid #DDD', borderRadius: 8, width: '100%', boxSizing: 'border-box' }}
              />
              {/* #492's lesson, and the standard #503 set: a control never
                  accepts a press and then does nothing. The reason renders
                  BENEATH the button, never in a tooltip — mobile is the priority
                  surface and a phone shows a tooltip never. */}
              <ActionButton
                onClick={() => onDeny(denyReason)}
                blockedReason={denyReason.trim() ? null : 'Type a reason first — the person who asked only ever sees this message.'}
                busy={busy}
                busyLabel="Denying…"
                className="btn btn-danger"
                style={actionWidth(isMobile, sheetCoarse)}
              >
                Deny this transfer
              </ActionButton>
              <button onClick={() => setMode(null)} style={{ background: 'none', border: 'none', fontSize: 13, color: '#5C5C5C', cursor: 'pointer', minHeight: controlHeight(sheetCoarse, 'min') }}>Cancel</button>
            </>
          )}

          {/* Only a made category asks. A shelf transfer has one honest answer
              and asking would be a question with no second option. */}
          {/* D64 — THE DRIVER IS NEVER ASKED HOW A LINE WAS FILLED.
              Found on screen at 79.10's acceptance run: a team member at
              Houston opened the load sheet and was asked to choose between
              "Houston made these" and "From Houston's shelf". That answer
              WRITES A PRODUCTION ROW, and #515 exists because a run was once
              recorded that nobody performed. It defaults from what was
              approved; only somebody who stages the goods changes it. */}
          {canShip && transfer.fulfilment_mode === 'make' && canStateHowFilled(auth) && (
            <div style={{ background: '#F7F7F7', borderRadius: 8, padding: '11px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#3A3A3A', marginBottom: 7 }}>
                How are you filling this?
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  // #79.10, Colby's pronoun catch applied here too. "We made
                  // these" and "From our shelf" are a guess about who is
                  // reading, and an admin or executive sees every branch's
                  // documents. The sending branch's NAME is true for everyone.
                  { key: 'make',
                    label: `${branchNames[String(transfer.from_branch_id)] || 'The sender'} made these`,
                    // #628 half 2 -- THE APPROVED WORDS. "Records a production
                    // run" stopped being true at 79.15e, when loading stopped
                    // recording one: what is made is what was signed off.
                    hint: `Only what is signed off (${makeProgress(lines).readyToLoad})` },
                  { key: 'stock',
                    label: `From ${branchNames[String(transfer.from_branch_id)] || 'their'} shelf`,
                    hint: 'No production recorded' },
                ].map(o => {
                  const on = sourced === o.key;
                  return (
                    <button key={o.key} onClick={() => setSourced(o.key)}
                      style={{
                        flex: '1 1 200px', textAlign: 'left', cursor: 'pointer',
                        border: on ? '1.5px solid #1D9E75' : '1px solid #E0E0E0',
                        background: on ? '#F1FAF6' : '#fff',
                        borderRadius: 8, padding: '9px 11px', minHeight: 44,
                      }}>
                      <div style={{ fontSize: 13, fontWeight: on ? 700 : 600, color: '#0F0F0F' }}>{o.label}</div>
                      <div style={{ fontSize: 11, color: '#5C5C5C', marginTop: 2, lineHeight: 1.35 }}>{o.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* GATE D47, THE OTHER HALF — WARN AT SHIP TIME, NOT ONLY AT RECORD
              TIME. Until this, the only warning about an unpriced thing was on
              the sheet that records a production run. Somebody who recorded a
              run in August could send the goods in September with nothing said
              — and the branch receiving them would book them in at nothing.

              It reads the SAME payload that is about to be sent rather than
              recomputing the rule, so the warning cannot disagree with what
              actually travels. That is the #514 lesson: a second computation of
              the caller is a second implementation, and it can be kinder than
              the real one.

              NOT A BLOCK, per D41 — and the reason is stated for each cause,
              because they need different people. A made thing nobody priced is
              a manager's to fix; something bought with no branch average
              settles when an invoice is reconciled. */}
          {/* #517 / D65 WIDENED AT COLBY'S RULING, 79.10 — BEHIND THE SAME GATE
              AS THE MONEY. Found on the driver's screen during the acceptance
              run: a team member loading a truck was told the item "will leave
              here worth nothing".

              No figure, so D65 held literally — and it still told somebody who
              cannot act on it that costing is missing. Colby: "team members
              dont need to see the warning." The warning exists so a MANAGER
              sets the cost before the goods leave; to the person loading the
              trailer it is noise about a job that is not theirs. */}
          {canShip && canSeeLoadValue(auth) && (() => {
            const warnings = shipPayload()
              .map(l => shipmentValueWarning({
                fulfilmentMode: transfer.fulfilment_mode,
                unitCost: l.unit_cost,
                itemName: items[String(l.item_id)]?.name,
              }))
              .filter(Boolean);
            if (warnings.length === 0) return null;
            return (
              <div style={{
                background: '#FFF8EC', border: '1px solid #F3D9A8', borderLeft: '3px solid #F3D9A8',
                borderRadius: 8, padding: '11px 13px', color: '#8A5A08', fontSize: 13, lineHeight: 1.5,
              }}>
                <strong>
                  {warnings.length === 1
                    ? `${warnings[0].itemName} will leave here worth nothing.`
                    : `${warnings.length} of these will leave here worth nothing.`}
                </strong>{' '}
                {warnings[0].made
                  ? 'Nothing says what it costs to make, so the receiving branch books it in at nothing. A manager can set that on the item.'
                  : 'This branch has no average cost for it yet — that settles once an invoice is reconciled against a receipt.'}
                {' '}Sending is still right; the count matters more than the figure.
              </div>
            );
          })()}

          {/* SCREEN 1 -- Houston sends what fits. #517, gate D48/D51/D61.
              The quantity box already accepted a smaller number before today;
              what the panel adds is the sentence saying what that MEANS, the
              load naming itself before you press send, and the second ending
              sitting beside Send rather than buried. */}
          {/* #602 / D28 and D31, gate 553's addendum.

              THE CHIP names the job in WORDS. The tab already carries a colour,
              and colour alone is invisible to anybody who cannot separate two
              hues -- the same reason #581's arriving/never-arrived mark is a
              mark and a word rather than a tint.

              THE SIGNPOST is the half that keeps D28 inside the two-tap floor.
              Somebody at the sending branch who opens a loadable transfer from
              On the way must not simply find nothing: an unexplained absence is
              the same puzzle as a dead press (#492). One line, one tap, and the
              sheet stays open -- the tab state lives in the parent already. It
              is drawn ONLY for something this person is actually entitled to do,
              never as an advertisement for something they cannot. */}
          {/* #711, Session 79.22b.4.3 -- A SECTION HEADING, NOT A TAG. Colby:
              the grey pill "looks out of place" and should read as the heading
              of the section it starts. Option A of the three he was shown: the
              History heading's own style -- small capitals, the faint ink -- over
              a green rule, so the sheet reads as sections. D28 holds: the job is
              still named in WORDS, the same words, on every stage. */}
          {/* #725 with #730, 79.22b.6e -- drawn only when something is under it
              or the transfer really is at this stage (drawStageHeading above). */}
          {drawStageHeading && (
          <div role="heading" aria-level={3} style={{
            fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase',
            letterSpacing: '0.8px', borderBottom: '2px solid #1D9E75', paddingBottom: 4, marginBottom: 8,
          }}>
            {/* #726 -- a pickup met only on the road says so. */}
            {stageChipFor(effectiveStage, {
              onRoad: pickupStops.length > 0 && pickupStops.every(s => s.branchId == null && !!s.place),
            }) || 'This transfer'}
          </div>
          )}

          {/* #725 -- in its place, the one true sentence for the person whose
              job the stage was. */}
          {emptyStageText && (
            <div style={{ fontSize: 13, color: '#3A3A3A', lineHeight: 1.45 }}>
              {emptyStageText}
            </div>
          )}

          {signpost && onGoToStage && (
            <button
              onClick={() => onGoToStage(signpost.stage)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                minHeight: 44, background: 'none', border: 'none', padding: '0 2px',
                fontSize: 12, color: '#5C5C5C', textAlign: 'left', cursor: 'pointer',
              }}
            >
              <span>
                {/* #655 -- gate 553 F2's words, with the count and the place. */}
                {signpostText}{' '}
                <span style={{ color: '#1F6FBF', textDecoration: 'underline', fontWeight: 600 }}>
                  Open {signpost.label}
                </span>
              </span>
            </button>
          )}

          {/* #628 -- the true sentence where the false signpost used to be. */}
          {nothingToLoadYet && (
            <div style={{ fontSize: 13, color: '#3A3A3A', lineHeight: 1.45 }}>
              {nothingToLoadYet}
            </div>
          )}

          {/* #622 -- the To make stage's own action, on its own sheet (D28). */}
          {signOffInSheet && (
            <SignOffAction transfer={transfer} myBranch={myBranch} auth={auth}
              onSignOff={() => onSignOff(transfer)} />
          )}

          {/* #628 half 2, APPROVED BY COLBY AS DRAWN -- "Nothing more is
              coming" on To make as well as To load. Before, it lived only on
              the load panel, which a request with nothing made never reaches,
              so the supplier's one way to say "we cannot fill this" was
              unreachable exactly when it was needed (acceptance line H5).
              The sending branch only, as on To load (#616) - and through the
              same one form of the rule, which transferCallerRules.test.js
              reads: the control draws nothing when it is not allowed. */}
          {effectiveStage === 'to_make' && ['approved', 'in_transit'].includes(transfer.status) && (
            <NothingMoreComingOnMake transfer={transfer} lines={lines} branchNames={branchNames}
              busy={busy} isMobile={isMobile}
              onCloseUnfilled={canSayNothingMoreComing(profile, transfer) ? onCloseUnfilled : null} />
          )}

          {canLoad && (
            <LoadPanel
              transfer={transfer}
              lines={lines}
              loads={loads}
              loadsReady={loadsReady}
              items={items}
              branchNames={branchNames}
              myBranch={myBranch}
              auth={auth}
              fulfilmentSourced={sourced}
              fromShelf={transfer.fulfilment_mode === 'make' && sourced === 'stock'}
              shelfFor={shelfHere}
              hubs={hubs}
              drivers={drivers}
              loaderModel={loaderModel}
              isMobile={isMobile}
              busy={busy}
              onLoad={onLoad}
              // #616 -- only the SENDING branch says nothing more is coming; a
              // hub where the goods merely stand cannot know it, and the
              // database now refuses it there.
              onCloseUnfilled={canSayNothingMoreComing(profile, transfer) ? onCloseUnfilled : null}
              // The cost that travels is computed HERE rather than in the panel,
              // because it is the same rule shipPayload() has always used and a
              // second copy is a second implementation -- #514's lesson, where a
              // test helper turned out to be kinder than the real caller.
              unitCostFor={(l) => costToTravel({
                madeAtBranchId: transfer.made_at_branch_id,
                senderAvgCost: receiverStock[`${l.item_id}:${transfer.from_branch_id}`]?.avg_unit_cost,
                standardCost: standardCosts[String(l.item_id)],
              })}
            />
          )}

          {/* SCREENS 2 AND 3 -- a load turns up and is counted. #517, D55/D63.
              DALLAS RECEIVES A LOAD, NOT A TRANSFER: with two trucks on the
              road a screen offering one box per item cannot tell which load the
              person is standing in front of. */}
          {/* #529 -- THE HANDOVER. Listed by the To collect tab and, until
              now, backed by nothing. */}
          {/* #602 / D28 -- the handover is the To collect tab's job. The panel
              still self-gates on there being something standing at a yard; the
              stage decides whether it is drawn at all. */}
          {mode !== 'short' && allowsAction(effectiveStage, 'collect') && (
            <CollectPanel
              transfer={transfer}
              loads={loads}
              items={items}
              branchNames={branchNames}
              myBranch={myBranch}
              isMobile={isMobile}
              busy={busy}
              onCollect={onCollect}
              // #553 D11 -- who is taking it, so the swap carries both names.
              auth={auth}
              drivers={drivers}
            />
          )}

          {/* #602 / D28 -- counting a load in is the To count in tab's job. */}
          {mode !== 'short' && allowsAction(effectiveStage, 'receive') && (
            <ArrivalPanel
              transfer={transfer}
              loads={loads}
              items={items}
              branchNames={branchNames}
              myBranch={myBranch}
              isMobile={isMobile}
              busy={busy}
              // #682 -- P5 held on the transfer on a phone (callout J).
              onArrive={async (args) => {
                const r = await onArrive?.(args);
                if (r && typeof r === 'object' && r.asked && !column) setFixAnswer([r.asked, null]);
                return r;
              }}
              onMarkArrived={onMarkArrived}
            />
          )}

          {/* The GENUINE short close, unchanged and deliberately still here.
              D56: goods that LEFT and never arrived is a different event from a
              balance nobody is going to send, and calling both by one name puts
              a loss report in front of a manager for a truck that was never
              loaded. This is the loss one. */}
          {/* #492 -- THE CONTROL IS ABSENT, SO THE REASON IS ON THE SCREEN.
              Narrowing the write-off to managers takes the button away from a
              team member who had it, and an unexplained absence is the same
              puzzle as a dead press.

              ** THE FIRST VERSION OF THIS WAS WRONG TWICE AND THE LIVE TEST
              FOUND BOTH. ** It read "count in what arrived" on HOU-TRF-00004,
              whose every load had ALREADY been counted in -- there was nothing
              to count and the sentence sent somebody looking for a control
              that correctly was not there. And its condition was bare
              `short.units > 0`, which is true of goods on a truck as well as
              goods that are gone, so it would have announced a write-off while
              a driver was still on the road. `nothingMoving` is the same
              correction owedToBranch() needed, one layer in. */}
          {canReceive && !canCloseShort && short.units > 0 && nothingMoving && waitingToCount === 0 && (
            <div style={{ fontSize: 12, color: '#5C5C5C' }}>
              {short.units} never arrived and nothing more is on the way. A manager at{' '}
              {branchNames[String(transfer.to_branch_id)] || 'the receiving branch'} or{' '}
              {branchNames[String(transfer.from_branch_id)] || 'the sending branch'}{' '}
              writes the balance off.
            </div>
          )}
          {/* #674 -- said to everybody who can count or write off: the goods are
              HERE, and counting comes before any loss is booked. */}
          {(canReceive || canCloseShort) && short.units > 0 && nothingMoving && waitingToCount > 0 && (
            <div style={{ fontSize: 12, color: '#5C5C5C' }}>
              {/* #729 -- a manager at the SENDING branch may read this too, so the
                  place is named. */}
              {waitingToCount} {waitingToCount === 1 ? 'is' : 'are'} {receivingAt} waiting to be counted. Count them in first
              {' '}— anything still missing after that can be written off.
            </div>
          )}

          {canCloseShort && mode !== 'short' && (
            <Actions isMobile={isMobile}>
              {/* #674 -- a write-off while a load waits to be counted would book
                  as lost what is standing on the dock. The press is answered
                  (#492) rather than the button vanishing. */}
              <ActionButton
                onClick={() => setMode('short')}
                blockedReason={waitingToCount > 0 ? `Count in what is ${receivingAt} first — ${waitingToCount} ${waitingToCount === 1 ? 'is' : 'are'} waiting to be counted.` : null}
                className="btn btn-danger"
                style={actionWidth(isMobile, sheetCoarse)}
              >
                Close short…
              </ActionButton>
            </Actions>
          )}

          {canCloseShort && mode === 'short' && (
            <>
              <div style={{ fontSize: 12, color: '#5C5C5C' }}>
                {/* No stock moves on a short close — the sender is already down
                    the full sent quantity, so a write-off here would take it off
                    them twice. D11 and D18. */}
                {/* #705's twenty-seventh -- the verb follows the number too. */}
                This records that {short.units || 'the missing'} unit{short.units === 1 ? '' : 's'} {short.units === 1 ? 'is' : 'are'} never arriving
                {/* Same gate, same reason -- see the note on the document above. */}
                {canSeeLoadValue(auth) && short.units > 0 && !short.anyUnpriced
                  ? `, worth about ${money(short.value)}` : ''}.
                No stock moves — the sending branch is already down what they sent.
              </div>
              <textarea
                value={shortReason}
                onChange={e => setShortReason(e.target.value)}
                rows={3}
                placeholder="What happened?"
                style={{ fontSize: 16, padding: 8, border: '1px solid #DDD', borderRadius: 8, width: '100%', boxSizing: 'border-box' }}
              />
              <ActionButton
                onClick={() => onCloseShort(shortReason)}
                blockedReason={shortReason.trim() ? null : 'Say what happened — both branches’ managers will read this.'}
                busy={busy}
                busyLabel="Closing…"
                className="btn btn-danger"
                style={actionWidth(isMobile, sheetCoarse)}
              >
                Close short
              </ActionButton>
              <button onClick={() => setMode(null)} style={{ background: 'none', border: 'none', fontSize: 13, color: '#5C5C5C', cursor: 'pointer', minHeight: controlHeight(sheetCoarse, 'min') }}>Cancel</button>
            </>
          )}

          {/* #590, gate 371 D37 -- "Withdraw this request" or "We don't need the
              rest", for the asker's managers; absent for the supplying branch
              and once everything is sent (F1). Red text on white, 7.07. */}
          {withdrawOffer && (
            <Actions isMobile={isMobile}>
              <button onClick={() => setWithdrawing(withdrawOffer)} className="btn"
                style={{ ...actionWidth(isMobile, sheetCoarse), background: '#FFFFFF', color: '#A32D2D', border: '1px solid #E3B8B8' }}>
                {withdrawOffer.control}
              </button>
            </Actions>
          )}
        </div>
      </div>
      {withdrawing && (
        <WithdrawSheet
          visible
          phoneLayout={sheetPhone}
          coarse={sheetCoarse}
          title={withdrawing.title}
          subtitle={withdrawing.subtitle}
          consequence={withdrawing.consequence}
          reasonLabel={withdrawing.reasonLabel}
          blockedReason={withdrawing.blockedReason}
          keepLabel={withdrawing.keepLabel}
          submitLabel={withdrawing.submitLabel}
          onClose={() => setWithdrawing(null)}
          onSubmit={reason => withdrawTransfer(transfer, {
            scope: withdrawing.scope, reason, withdrawnBy: profile?.id || null,
            withdrawnByName: profile?.full_name || null, branchNames,
          })}
          onDone={result => { const o = withdrawing; setWithdrawing(null); onWithdrawn?.(result, o); }}
        />
      )}
    </DetailFrame>
  );
}

// #675 D19 / F5, Session 79.22b.5.5 -- THE TWO FRAMES OF THE ONE SHEET. Below the
// desk: today's sheet on a phone and centred modal elsewhere, through Overlay
// (locked rule 3). On a desk: a plain section filling the right-hand column,
// which scrolls on its own (DeskSplit). Module level, never inside a component
// (locked rule 2).
function DetailFrame({ column, isMobile, onClose, children }) {
  if (column) {
    return <section aria-label="The chosen transfer">{children}</section>;
  }
  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={onClose} maxWidth={640}>
      {children}
    </Overlay>
  );
}

// #675 / GATE 675 D22, Session 79.22b.5.5b -- THE ANSWER AT THE TOP OF THE RIGHT
// SIDE (callout K): "Approved. The sending branch has been told." -- the
// action's own answer, unchanged -- then "It's now on To make at Houston." when
// the press moved the transfer off the tab, then Next on the tab when another
// card is still waiting. The house message bar (InlineMessage), so it reads as
// every other answer in Stede does; it stays until dismissed, the next choice, a
// change of tab or another press. Module level (locked rule 2); exported for
// its test.
export function DeskAnswer({ note, nowOn = null, nextLabel = null, onNext, onDismiss, coarse }) {
  if (!note) return null;
  const text = note.text || null;
  if (!text && !nowOn) return null;
  const type = note.kind === 'error' ? 'error' : note.kind === 'info' ? 'info' : 'success';
  return (
    <div data-desk-answer style={{ padding: '12px 14px 0' }}>
      <InlineMessage
        type={type}
        onDismiss={onDismiss}
        style={{ marginBottom: 0 }}
        message={(
          <span role="status">
            {text && <strong>{text}</strong>}
            {text && nowOn ? ' ' : null}
            {nowOn}
            {nextLabel && (
              <span style={{ display: 'block', marginTop: 8 }}>
                <button
                  type="button"
                  onClick={onNext}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: controlHeight(coarse, 'min'),
                    padding: '0 12px', border: 'none', borderRadius: 8, background: '#14503B', color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  {nextLabel}
                  <span aria-hidden="true">→</span>
                </button>
              </span>
            )}
          </span>
        )}
      />
    </div>
  );
}

// #675 D19-D21, Session 79.22b.5.5 -- THE DESK: THE LIST ON THE LEFT, THE CHOSEN
// TRANSFER ON THE RIGHT.
//
// From Stede's desk width (1024px) the list is a 400px column with the search
// and the filter buttons at its head, and the chosen transfer fills the rest.
// Each scrolls on its own, so the transfer's buttons stay put while the list
// scrolls (callout J; the Purchasing Queue's shell). Nothing opens on the right
// by itself (D20). The list column lays its cards out as a tablet does, by the
// column's width rather than the window's (F4): LayoutTierContext.
//
// The right side goes back to its top when another transfer is chosen, so a
// transfer never opens scrolled to where the last one was read.
// Below the desk it is the list alone, exactly as before.
//
// #675 / gate 675 D22, Session 79.22b.5.5b -- `answer` is the answer to a press on
// the right, drawn above the transfer; for each new one (`answerKey`) the right
// side returns to its top, where the answer is, and the page and the list stay
// still. And the chosen card is kept in sight in the list -- Next chooses a card
// that may sit below the fold -- by moving the list column's own scroll, never
// the page's (#700: scrollIntoView moves every scrollable ancestor).
function DeskSplit({ onDesk, head, detail, chosenId, answer = null, answerKey = null, children }) {
  const detailRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    if (detailRef.current) detailRef.current.scrollTop = 0;
    const list = listRef.current;
    if (!list || chosenId == null || typeof requestAnimationFrame !== 'function') return undefined;
    const frame = requestAnimationFrame(() => {
      const card = list.querySelector('[aria-current="true"]');
      if (!card) return;
      const c = card.getBoundingClientRect();
      const s = list.getBoundingClientRect();
      if (c.top < s.top) list.scrollTop -= (s.top - c.top) + 8;
      else if (c.bottom > s.bottom) list.scrollTop += (c.bottom - s.bottom) + 8;
    });
    return () => cancelAnimationFrame(frame);
  }, [chosenId]);
  useEffect(() => {
    const el = detailRef.current;
    if (answerKey == null || !el) return;
    if (typeof el.scrollTo === 'function') el.scrollTo({ top: 0, behavior: 'smooth' });
    else el.scrollTop = 0;
  }, [answerKey]);
  if (!onDesk) return <div>{children}</div>;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `${DESK_LIST_WIDTH}px minmax(0, 1fr)`, gap: DESK_GAP,
      flex: 1, minHeight: 0,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flexShrink: 0 }}>{head}</div>
        <div ref={listRef} data-desk-list style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '2px 4px 16px 2px' }}>
          <LayoutTierContext.Provider value="tablet">
            {children}
          </LayoutTierContext.Provider>
        </div>
      </div>
      <div ref={detailRef} data-desk-detail style={{
        minHeight: 0, overflowY: 'auto', background: '#fff',
        border: '1px solid #E9E9E9', borderRadius: 10,
      }}>
        {detail && answer}
        {detail || (
          <div role="status" style={{
            height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '2rem', textAlign: 'center', color: '#5C5C5C', fontSize: 14,
          }}>
            {DESK_PLACEHOLDER}
          </div>
        )}
      </div>
    </div>
  );
}
